import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { getActiveSiteId, getActiveTenantId } from "../services/activeTenant";
import SourceXTour from "../components/sourcex/SourceXTour";
import { TOUR_STEPS, findTourItem } from "../components/sourcex/sourcexTourSteps";
import { hasAnsweredTour, markTourAnswered } from "../services/sourcexTourStore";
import { getSourcexTemplatesPath, getStudiesPath } from "../services/firestorePaths";
import {
  ArrowLeft,
  Plus,
  Sparkles,
  CheckCircle2,
  FileText,
  Pencil,
  Trash2,
  Rows3,
  Copy,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ChevronsUp,
  ChevronsDown,
  ListTree,
  LibraryBig,
  Printer,
  GitBranch,
  Eye,
  Loader2,
  Undo2,
  GripVertical,
  Merge,
  History,
  ScrollText,
  ClipboardList,
  ListChecks,
  X,
  Compass,
} from "lucide-react";
import QuestionField, {
  inputCls as sharedInputCls,
  selectCls as sharedSelectCls,
} from "../components/sourcex/QuestionField";
import SourcePreviewField from "../components/sourcex/SourcePreviewField";
import FieldTypePalette, {
  FIELD_TYPE_LABELS,
} from "../components/sourcex/FieldTypePalette";
import QuestionBuilder from "../components/sourcex/QuestionBuilder";
import ConditionBuilder, { choicesForQuestion } from "../components/sourcex/ConditionBuilder";
import VersionHistoryModal from "../components/sourcex/VersionHistoryModal";
import VersionRedlineModal from "../components/sourcex/VersionRedlineModal";
import TemplateAuditModal from "../components/sourcex/TemplateAuditModal";
import CopyQuestionModal from "../components/sourcex/CopyQuestionModal";
import { useDialog } from "../components/sourcex/useDialog";
import {
  buildBranchDepth,
  buildOutlineNumbers,
} from "../components/sourcex/branchLayout";
import ExportSourceDocModal from "../components/sourcex/ExportSourceDocModal";
import StepProgressOverlay from "../components/sourcex/StepProgressOverlay";
import SourceDocPreviewModal from "../components/sourcex/SourceDocPreviewModal";
import {
  SOURCE_QUESTION_TEMPLATES,
  SOURCE_QUESTION_TEMPLATES_MENU,
} from "../services/sourceQuestionTemplates";
import {
  TEMPLATE_DRAG_TYPE,
  isTemplateDrag,
  readTemplateId,
} from "../services/templateDrag";
import {
  SOURCE_LOG_TEMPLATES_MENU,
  findLogTemplate,
  buildLogSection,
} from "../services/sourceLogTemplates";
import {
  ELIGIBILITY_SECTION_NAME,
  EMPTY_ELIGIBILITY,
  buildEligibilitySection,
} from "../services/eligibilityChecklist";
import {
  extractEligibilityCriteria,
  eligibilityReadWarning,
} from "../services/eligibilityService";
import { ELIGIBILITY_EXTRACTION_ENABLED } from "../config/featureFlags";
import { findProtocolUrl } from "../services/protocolExtractionService";
import {
  buildStandardAdvice,
  describeStandardAdvice,
  isStandardAdviceDismissed,
  standardAdviceKey,
} from "../services/standardBlockAdvice";
import {
  logTemplateAuditEvent,
  listTemplateAuditEvents,
  buildQuestionDiff,
  TEMPLATE_AUDIT_ACTIONS,
} from "../services/templateAuditService";
import { resolveCurrentActor, describeActor } from "../services/approverIdentity";
import {
  defaultExportConfig,
  exportStudyIdFor,
  footerDateFor,
  formatDocDate,
} from "../services/sourceDocExport";
import SourceDocsVersionNotice from "../components/sourcex/SourceDocsVersionNotice";
import { PROTOCOL_COMPARE_ENABLED, SOURCEX_TOUR_ENABLED } from "../config/featureFlags";
import AiHelperNotice from "../components/AiHelperNotice";
import {
  downloadSourceDocx,
  sourceDocxFilename,
} from "../services/sourceDocDocx";
import {
  normalizeTemplate,
  stripAnswers,
  validateTemplate,
  collectQuestionProblems,
  makeQuestion,
  applyQuestionEdit,
  makeSection,
  makeEvent,
  OP_LABELS,
  VALUELESS_OPS,
  remapConditionValues,
  findFieldsUsingOption,
  parseCondition,
  buildCondition,
  describeCondition,
  buildDependentsMap,
  conditionLeaves,
  makeTemplateVersion,
  versionEventId,
  versionEvent,
  sectionsForEvent,
  validateEvent,
  backfillEventApproval,
  eventIsApproved,
  eventIsPaperFinal,
  approvalProgress,
  templateStatusFromSections,
  splitTableIntoQuestions,
  repointBranchRefs,
  headingBlockAt,
  cloneBlockForEvent,
  cloneQuestionForEvent,
  questionCopyTargets,
} from "../services/templateSchema";
import {
  pushTemplateVersion,
  migrateEmbeddedVersions,
  listTemplateVersions,
} from "../services/templateVersionService";
import {
  previousApprovedVersion,
  approvalNumberOf,
  latestApprovedVersionFor,
  sameVisitContent,
  versionLabel,
} from "../services/templateRedline";

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
  </div>
);

// Shared with PatientSource via the field renderer, so the template preview
// and the real patient form look identical.
const inputCls = sharedInputCls;
const selectCls = sharedSelectCls;


// DOM id for a question/heading block, so the outline can scroll to it.
const anchorId = (id) => `sx-q-${id}`;

/**
 * Merge ONE visit from `fromSections` into `intoSections`, leaving every other
 * visit in the target exactly as it was.
 *
 * This is what promoting a single approved visit into the canonical template
 * needs. Replacing the canonical doc wholesale was right while an approval
 * covered every visit at once; now it would wipe out visits approved on an
 * earlier pass. A visit not already in the target is appended to its own
 * section — creating that section if the target has never seen it.
 */
const mergeEventIntoSections = (intoSections = [], fromSections = [], eventId = "") => {
  let source = null;
  let sourceSectionName = "";
  for (const s of fromSections) {
    const ev = (s?.events || []).find((e) => e?.id === eventId);
    if (ev) {
      source = ev;
      sourceSectionName = s.name;
      break;
    }
  }
  if (!source) return intoSections;

  let placed = false;
  const merged = intoSections.map((s) => {
    const has = (s?.events || []).some((e) => e?.id === eventId);
    if (has) {
      placed = true;
      return {
        ...s,
        events: (s.events || []).map((e) => (e?.id === eventId ? source : e)),
      };
    }
    // Not there yet, but this is the section it belongs to — append it.
    if (!placed && s?.name === sourceSectionName) {
      placed = true;
      return { ...s, events: [...(s.events || []), source] };
    }
    return s;
  });
  if (placed) return merged;
  return [...merged, { name: sourceSectionName, events: [source] }];
};

/** Today, in the DD-Mon-YYYY the export header uses. */
const todayStr = () => {
  const d = new Date();
  const mon = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][
    d.getMonth()
  ];
  return `${String(d.getDate()).padStart(2, "0")}-${mon}-${d.getFullYear()}`;
};

/** A visit's current name, for labelling a snapshot taken of it. */
const eventNameById = (template, eventId) => {
  for (const s of template?.sections || []) {
    for (const ev of s?.events || []) {
      if (ev?.id === eventId) return ev.eventName || "";
    }
  }
  return "";
};

/**
 * Put a visit-scoped snapshot back into a full template, replacing only that
 * one visit and leaving every other visit — and the section order — exactly as
 * it was. This is what makes restoring one visit safe to do.
 */
const replaceEventInSections = (sections = [], eventId = "", snapshotSections = []) => {
  // The visit as it was in the snapshot. A snapshot that doesn't contain the
  // visit it names restores nothing rather than emptying it.
  let restored = null;
  for (const s of snapshotSections) {
    const ev = (s?.events || []).find((e) => e?.id === eventId);
    if (ev) {
      restored = ev;
      break;
    }
  }
  if (!restored) return sections;
  return sections.map((s) => ({
    ...s,
    events: (s.events || []).map((ev) => (ev?.id === eventId ? restored : ev)),
  }));
};

// Split a flat question list into heading blocks — the same grouping
// moveHeadingBlock uses (a Header owns every field until the next Header).
// Fields before the first heading become an untitled leading group.
const buildOutline = (questions = []) => {
  const groups = [];
  // Each group carries its heading's POSITION as well as the heading itself.
  // The document keys collapse state by position because an AI-built template
  // can repeat a question id, and the outline has to agree with it or the two
  // fall out of sync on exactly the duplicated sections.
  let current = { heading: null, headingIndex: null, items: [] };
  questions.forEach((q, qi) => {
    if (q.type === "Header") {
      if (current.heading || current.items.length) groups.push(current);
      current = { heading: q, headingIndex: qi, items: [] };
    } else {
      current.items.push(q);
    }
  });
  if (current.heading || current.items.length) groups.push(current);
  return groups;
};

// Fields that can control a branch: anything that holds ONE scalar answer.
// Headings and signature blocks hold none, so branching on them could never
// be satisfied. A Table (repeatable, or a composite question's several
// pieces bundled into one) holds many sub-answers instead of one — there's
// no single "the answer" to compare a rule against — so picking one as the
// controlling question used to seed the rule builder with something it
// couldn't actually build a working condition from, which is what made
// "sub questions" look broken whenever a table sat in the way.
const canControlBranch = (q) =>
  q && q.type !== "Header" && q.type !== "Signature" && q.type !== "Table";

/**
 * "RegX already has a standard block for this section."
 *
 * One line, on the row that heads the section — the heading, or the first
 * question when the section has none. It names the menu AND the block, because
 * "use the standard" without saying where is an instruction nobody can follow.
 *
 * Deliberately advisory: no button, nothing blocked. Swapping a hand-built
 * section for the standard one deletes work somebody did, and that is the
 * coordinator's call to make — not a click to trip over. See
 * services/standardBlockAdvice.js for which sections are flagged, and for why a
 * section that already IS the standard block is never flagged.
 *
 * @param {import("../services/standardBlockAdvice").StandardAdvice} advice
 */
function StandardBlockNote({ advice, onDismiss }) {
  if (!advice) return null;
  const named = (labels) =>
    labels.map((label, i) => (
      <span key={label}>
        {i > 0 && " or "}
        <strong className="font-semibold text-amber-200">{label}</strong>
      </span>
    ));
  return (
    <div
      title={describeStandardAdvice(advice)}
      className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-amber-100/90"
    >
      <LibraryBig className="w-3.5 h-3.5 shrink-0 text-amber-300" />
      {/* `useNow` is never empty: a topic with nothing usable yet raises no
          notice at all, so this never asks anyone to go and click something
          that is still being written. See buildStandardAdvice. */}
      <span className="font-semibold text-amber-200">Standard block exists.</span>
      <span>Use {named(advice.useNow)} so every study matches.</span>
      {/* The OTHER menu, when this topic is standardized in both. Hearing only
          about the half that happens to be finished is how a coordinator never
          learns the standing log exists. Mentioned, not offered — it is not
          why the notice is here. */}
      {!!advice.coming.length && (
        <span className="text-amber-100/70">
          Also coming: {advice.coming.join(" and ")}.
        </span>
      )}
      {onDismiss && (
        // The escape hatch. A study CAN have a good reason to keep its own
        // wording — a sponsor who mandates it — and an advisory that cannot be
        // silenced stops being advice and becomes noise that trains people to
        // ignore the colour. Reversible (see the muted book button this leaves
        // on the heading) and recorded in the audit trail, because "we
        // deliberately kept our own" is exactly the decision a monitor asks
        // about later.
        <button
          type="button"
          onClick={onDismiss}
          title="Hide this — this section deliberately keeps its own wording. Recorded in the audit trail, and reversible from the heading."
          className="ml-auto shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-amber-400/30 text-[10px] font-semibold uppercase tracking-wide text-amber-200/90 hover:bg-amber-400/20 hover:text-white transition"
        >
          <X className="w-3 h-3" /> Keep ours
        </button>
      )}
    </div>
  );
}

/**
 * Stable collapse keys for a visit's headings, by position.
 *
 * Neither half works alone. Position alone shifts under every insert, reorder
 * and delete, so a collapsed section silently became a different one. The id
 * alone isn't unique either — an AI-built template can repeat one (the SoA
 * extraction can emit the same assessment twice for a visit), and two headings
 * sharing a key made a click look like it did nothing and a delete remove both.
 * The id plus WHICH occurrence of that id this is survives reordering and is
 * unique by construction.
 *
 * @returns {Map<number, string>} question index -> collapse key, headings only
 */
function sectionKeysOf(questions) {
  const seen = new Map();
  const keys = new Map();
  (questions || []).forEach((q, i) => {
    const nth = seen.get(q.id) || 0;
    seen.set(q.id, nth + 1);
    if (q.type === "Header") keys.set(i, `${q.id}#${nth}`);
  });
  return keys;
}

/**
 * The branching notes shown on a field in the document (read-only).
 *
 * Two directions, because a coordinator reviewing a template needs both:
 *  • this field is conditional — under exactly which answers does it appear,
 *    and does one match suffice or must they all hold at once;
 *  • this field controls others — which fields does answering it reveal.
 * Without the second, a controlling question looks ordinary and its options can
 * be renamed out from under a branch without anyone noticing.
 */
function BranchNotes({ question, labelById, dependents, numberById, onJump }) {
  const own = question?.visibleWhen
    ? describeCondition(question.visibleWhen, labelById)
    : null;
  // The rules behind those descriptions, in the same order — describeCondition
  // maps rules 1:1 to parts — so each line knows WHICH question it is about and
  // can offer to take you there.
  const ownRules = question?.visibleWhen
    ? parseCondition(question.visibleWhen).rules
    : [];
  const reveals = dependents || [];
  if (!own && reveals.length === 0) return null;

  // Only offer the jump for a question that is actually on this page. A rule
  // can name a question in another visit, which has no card here to scroll to.
  const jumpable = (id) => !!(onJump && id && labelById?.has(id));
  const jumpCls =
    "text-left underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer";

  return (
    <div className="mt-1.5 space-y-1">
      {own && (
        <div className="rounded-lg border border-fuchsia-400/25 bg-fuchsia-400/[0.07] px-2 py-1.5">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-fuchsia-200">
            <GitBranch className="w-3 h-3" />
            Only shows when
            {own.parts.length > 1 && (
              <span className="px-1.5 py-0.5 rounded bg-fuchsia-400/20 text-[10px] uppercase tracking-wider">
                {own.mode === "all" ? "all" : "any one"}
              </span>
            )}
          </div>
          {own.complex ? (
            <p className="mt-0.5 text-[11px] text-fuchsia-100/80">
              Advanced rule — click the pencil to see it.
            </p>
          ) : (
            <ul className="mt-0.5 space-y-0.5">
              {own.parts.map((p, i) => {
                const targetId = ownRules[i]?.questionId;
                return (
                  // Rule order is the identity here — the list is positional.
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i} className="text-[11px] text-fuchsia-100/90">
                    {i > 0 && (
                      <span className="text-fuchsia-300/70 mr-1">
                        {own.mode === "all" ? "and" : "or"}
                      </span>
                    )}
                    {jumpable(targetId) ? (
                      <button
                        type="button"
                        onClick={() => onJump(targetId)}
                        title="Go to this question"
                        className={`${jumpCls} text-fuchsia-100/90 hover:text-white`}
                      >
                        {p}
                      </button>
                    ) : (
                      p
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {reveals.length > 0 && (
        <div className="rounded-lg border border-sky-400/25 bg-sky-400/[0.07] px-2 py-1.5">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-sky-200">
            <Eye className="w-3 h-3" /> Shows {reveals.length} other field
            {reveals.length === 1 ? "" : "s"}
          </div>
          <ul className="mt-0.5 space-y-0.5">
            {reveals.map((d, i) => {
              // Numbered the same way the rule above is, so both directions of
              // a dependency name the question the same way.
              const n = numberById?.get(d.id);
              const label = n ? `${n} ${d.question || ""}`.trim() : d.question;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <li key={`${d.id}-${i}`} className="text-[11px] text-sky-100/90">
                  <span className="text-sky-300/70 mr-1">
                    {(OP_LABELS[d.rule.op] || d.rule.op)}
                    {VALUELESS_OPS.includes(d.rule.op)
                      ? ""
                      : ` “${
                          Array.isArray(d.rule.value)
                            ? d.rule.value.join(" / ")
                            : d.rule.value
                        }”`}{" "}
                    →
                  </span>
                  {jumpable(d.id) ? (
                    <button
                      type="button"
                      onClick={() => onJump(d.id)}
                      title="Go to this question"
                      className={`${jumpCls} text-sky-100/90 hover:text-white`}
                    >
                      {label}
                    </button>
                  ) : (
                    label
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// Document outline (Google-Docs style): a sticky, dense index of the current
// event. Collapsed headings keep the whole form on one screen; expanding one
// reveals its fields. Every control here delegates to the SAME handlers the
// main document uses — this panel adds navigation, never its own behavior.
function TemplateOutline({
  questions,
  expanded,
  onToggleHeading,
  onJump,
  onMoveHeadingBlock,
  onMoveQuestion,
  onReorder,
  problemById,
  branchDepth,
  numberById,
  onDropTemplate,
  templateDragId,
  // Sections RegX has a standard block for that aren't using it. Keyed by row
  // id — see services/standardBlockAdvice.js. The outline only ever flags the
  // HEADING row: the warning itself is written out in the document, and this
  // index exists so a section that needs it can be found without scrolling.
  standardAdviceById,
}) {
  const groups = useMemo(() => buildOutline(questions), [questions]);
  const headingCount = groups.filter((g) => g.heading).length;

  // Native HTML5 drag-and-drop reorder. `dragId` is what's being dragged;
  // `overId` is the row it's hovering, for a drop-line highlight. A plain click
  // still toggles/jumps — a drag only starts on an actual pointer drag.
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  // A template block is in flight. This comes from the parent as STATE set at
  // drag start, not from re-reading dataTransfer on every event: a dragover
  // whose `types` read comes back empty must not be mistaken for "no drag in
  // progress", which is what stopped auto-scroll dead mid-gesture.
  const templateOver = !!templateDragId;

  // ---- Edge auto-scroll ---------------------------------------------------
  // The outline scrolls independently, and HTML5 drag suppresses the wheel, so
  // reaching a row below the fold used to mean parking the pointer on the
  // browser's own narrow auto-scroll strip. Instead: anywhere within a
  // generous band of the top/bottom edge scrolls, faster the closer you get.
  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const speedRef = useRef(0);

  const stopAutoScroll = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    speedRef.current = 0;
  };

  // Last pointer Y of the drag. The speed is recomputed from this EVERY frame
  // rather than latched once, because the geometry moves underneath us: as the
  // page scrolls, the sticky panel repositions and which scroller should be
  // moving changes mid-gesture.
  const lastYRef = useRef(0);

  /**
   * What should move this frame, and how fast.
   * @returns {{speed: number, usePage: boolean}} speed in px/frame (negative =
   *   up); `usePage` means scroll the WINDOW instead of the list.
   */
  const computeAutoScroll = () => {
    const el = scrollRef.current;
    if (!el) return { speed: 0, usePage: false };
    const clientY = lastYRef.current;
    const rect = el.getBoundingClientRect();

    // The scroll box is often TALLER than the space left on screen — with a
    // long outline it runs off the bottom of the window entirely. Its own
    // bottom edge is then off-screen, and a band measured from it is somewhere
    // the pointer can never go: the drag just runs out of screen and nothing
    // scrolls. So the bands are measured from the visible part of the box.
    const viewportBottom =
      window.innerHeight || document.documentElement.clientHeight || rect.bottom;
    const top = Math.max(rect.top, 0);
    const bottom = Math.min(rect.bottom, viewportBottom);
    const visible = Math.max(0, bottom - top);
    if (visible <= 0) return { speed: 0, usePage: false };

    // Keep the two bands from meeting in the middle on a short box.
    const ZONE = Math.max(36, Math.min(72, visible / 3));
    const MAX = 22; // px per frame at the edge or beyond it

    let speed = 0;
    // `t` is clamped at 1, so AT or PAST the edge counts as full speed rather
    // than falling off — dragging to the very bottom of the screen is exactly
    // how people ask to scroll down, and that has to keep working.
    if (clientY < top + ZONE) {
      const t = Math.max(0, Math.min(1, (top + ZONE - clientY) / ZONE));
      speed = -Math.ceil(MAX * t);
    } else if (clientY > bottom - ZONE) {
      const t = Math.max(0, Math.min(1, (clientY - (bottom - ZONE)) / ZONE));
      speed = Math.ceil(MAX * t);
    }
    if (!speed) return { speed: 0, usePage: false };

    // The list still has room — move it.
    const down = speed > 0;
    const atListEnd = down
      ? el.scrollTop + el.clientHeight >= el.scrollHeight - 1
      : el.scrollTop <= 0;
    if (!atListEnd) return { speed, usePage: false };

    // The list is fully scrolled but its tail can still be rendered BELOW the
    // window, so the last rows are on the page yet off the screen and no
    // amount of list-scrolling reveals them. Hand off to the page, which moves
    // the panel itself into view. Only stop once the page is out of room too.
    const docEl = document.documentElement;
    const pageY = window.scrollY ?? docEl.scrollTop ?? 0;
    const pageMax = Math.max(
      0,
      docEl.scrollHeight - (window.innerHeight || docEl.clientHeight || 0)
    );
    const atPageEnd = down ? pageY >= pageMax - 1 : pageY <= 0;
    if (atPageEnd) return { speed: 0, usePage: false };
    return { speed, usePage: true };
  };

  const stepAutoScroll = () => {
    const { speed, usePage } = computeAutoScroll();
    if (!speed) {
      speedRef.current = 0;
      rafRef.current = null;
      return;
    }
    speedRef.current = speed;
    if (usePage) window.scrollBy(0, speed);
    else if (scrollRef.current) scrollRef.current.scrollTop += speed;
    rafRef.current = requestAnimationFrame(stepAutoScroll);
  };

  const updateAutoScroll = (clientY) => {
    lastYRef.current = clientY;
    const { speed } = computeAutoScroll();
    speedRef.current = speed;
    if (speed && !rafRef.current) {
      rafRef.current = requestAnimationFrame(stepAutoScroll);
    } else if (!speed) {
      stopAutoScroll();
    }
  };

  // Auto-scroll is driven from the WINDOW, not from the panel's own handlers.
  // A drag that leaves the panel stops firing events on it, so edge-scrolling
  // that depends on staying inside the panel dies exactly when the user
  // gestures past the edge — which is the natural way to ask for more scroll,
  // and why scrolling down felt broken while scrolling up worked.
  const dragIdRef = useRef(null);
  const templateDragRef = useRef(null);
  useEffect(() => {
    dragIdRef.current = dragId;
  }, [dragId]);
  useEffect(() => {
    templateDragRef.current = templateDragId;
  }, [templateDragId]);

  useEffect(() => {
    const onWindowDragOver = (ev) => {
      const el = scrollRef.current;
      if (!el) return;
      // Both kinds of drag are tracked as sticky state, so neither depends on
      // a per-event dataTransfer read that some browsers won't answer.
      const dragging =
        dragIdRef.current || templateDragRef.current || isTemplateDrag(ev);
      if (!dragging) {
        stopAutoScroll();
        return;
      }
      // Only while the pointer is roughly over the outline column, so dragging
      // across the document doesn't scroll the outline behind it.
      const rect = el.getBoundingClientRect();
      const SLACK = 96;
      if (ev.clientX < rect.left - SLACK || ev.clientX > rect.right + SLACK) {
        stopAutoScroll();
        return;
      }
      updateAutoScroll(ev.clientY);
    };
    const onWindowDragEnd = () => {
      stopAutoScroll();
      setDragId(null);
      setOverId(null);
    };
    window.addEventListener("dragover", onWindowDragOver);
    window.addEventListener("dragend", onWindowDragEnd);
    window.addEventListener("drop", onWindowDragEnd);
    return () => {
      window.removeEventListener("dragover", onWindowDragOver);
      window.removeEventListener("dragend", onWindowDragEnd);
      window.removeEventListener("drop", onWindowDragEnd);
      stopAutoScroll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDragStart = (ev, id) => {
    ev.stopPropagation();
    setDragId(id);
    ev.dataTransfer.effectAllowed = "move";
    try {
      ev.dataTransfer.setData("text/plain", id);
    } catch {
      /* some browsers restrict setData outside a user gesture */
    }
  };
  const onDragOverRow = (ev, id) => {
    const fromTemplate = templateOver || isTemplateDrag(ev);
    if (!fromTemplate && (!dragId || dragId === id)) return;
    ev.preventDefault();
    ev.dataTransfer.dropEffect = fromTemplate ? "copy" : "move";
    if (overId !== id) setOverId(id);
  };
  const onDropRow = (ev, id) => {
    ev.preventDefault();
    ev.stopPropagation();
    stopAutoScroll();
    // A block dragged in from "Use from template" lands here; anything else is
    // an ordinary reorder of a row that already exists.
    if (templateOver || isTemplateDrag(ev)) {
      const tplId = readTemplateId(ev) || templateDragId;
      setDragId(null);
      setOverId(null);
      if (tplId) onDropTemplate?.(tplId, id);
      return;
    }
    const src = dragId || ev.dataTransfer.getData("text/plain");
    setDragId(null);
    setOverId(null);
    if (src && src !== id) onReorder?.(src, id);
  };
  const onDragEnd = () => {
    stopAutoScroll();
    setDragId(null);
    setOverId(null);
  };

  // Drag activity anywhere in the scroll area — including the gaps between
  // rows — keeps auto-scroll alive, so the pointer never has to stay pinned to
  // a row to keep moving.
  const onPanelDragOver = (ev) => {
    const fromTemplate = templateOver || isTemplateDrag(ev);
    if (!dragId && !fromTemplate) return;
    ev.preventDefault();
    ev.dataTransfer.dropEffect = fromTemplate ? "copy" : "move";
  };

  // Dropped on the panel but not on a row — append at the end, which is what
  // the empty space below the last row looks like it means. Without this, a
  // long drag toward the bottom that overshoots the last row just vanished.
  const onPanelDrop = (ev) => {
    const fromTemplate = templateOver || isTemplateDrag(ev);
    if (!fromTemplate && !dragId) return;
    ev.preventDefault();
    ev.stopPropagation();
    stopAutoScroll();
    setOverId(null);
    if (fromTemplate) {
      const tplId = readTemplateId(ev) || templateDragId;
      if (tplId) onDropTemplate?.(tplId, null);
      return;
    }
    const src = dragId;
    setDragId(null);
    if (src) onReorder?.(src, null); // null target == move to the end
  };
  const dropLine = (id) =>
    overId === id ? "outline outline-2 outline-purple-400/70 outline-offset-[-2px]" : "";
  // Does any field in a group (or its heading) have a problem? Used to flag the
  // collapsed heading row red so the user can find it without expanding.
  const groupHasProblem = (g) =>
    (g.heading && problemById?.has(g.heading.id)) ||
    g.items.some((q) => problemById?.has(q.id));

  // Tiny reorder pair — deliberately smaller than the document's own controls
  // so the outline stays compact.
  const moveBtns = (onUp, onDown, what) => (
    <span className="row-actions flex items-center shrink-0">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onUp();
        }}
        title={`Move ${what} up`}
        className="w-5 h-5 flex items-center justify-center rounded text-slate-500 hover:text-white hover:bg-white/10 transition"
      >
        <ChevronUp className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDown();
        }}
        title={`Move ${what} down`}
        className="w-5 h-5 flex items-center justify-center rounded text-slate-500 hover:text-white hover:bg-white/10 transition"
      >
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
    </span>
  );

  return (
    <div
      onDragOver={onPanelDragOver}
      onDrop={onPanelDrop}
      // Deliberately no onDragLeave: leaving the panel is how the user asks to
      // scroll past its edge, so the drop affordance must survive it. The
      // window-level dragend/drop listener is what clears this state.
      className={`rounded-2xl border backdrop-blur-xl p-2.5 transition ${
        templateOver
          ? "border-purple-300/60 bg-purple-500/[0.10]"
          : "border-white/10 bg-white/[0.03]"
      }`}
    >
      <div className="flex items-center gap-1.5 px-1 pb-2 mb-1 border-b border-white/10">
        <ListTree className="w-4 h-4 text-purple-300 shrink-0" />
        <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white">
          Outline
        </span>
        <span className="ml-auto text-[11px] text-slate-500">
          {headingCount || groups.length}
        </span>
      </div>

      {templateOver && (
        <p className="mb-1 px-1.5 py-1 rounded-lg bg-purple-500/15 border border-purple-300/30 text-[11px] text-purple-100">
          Drop on a section to place the block above it — or below for the end.
        </p>
      )}

      {groups.length === 0 ? (
        <p
          className={`px-1 py-2 text-xs ${
            templateOver ? "text-purple-100" : "text-slate-500"
          }`}
        >
          {templateOver ? "Drop here to add it." : "Nothing to outline yet."}
        </p>
      ) : (
        <div
          ref={scrollRef}
          className="max-h-[calc(100vh-11rem)] overflow-y-auto scrollbar-thin pr-0.5">
          {groups.map((g, gi) => {
            const key = g.heading ? g.heading.id : `ungrouped-${gi}`;
            const isOpen = expanded.has(key);
            const headingProblem = groupHasProblem(g);
            // A standard block exists for this section and isn't in use. Ranks
            // below a problem — a broken section is never recoloured into a
            // suggestion — and only on the row the document writes the warning
            // on, so a section flags once here too.
            // `!!advice` first and not just the id comparison: a group with no
            // heading has no advice AND no heading id, and `undefined ===
            // undefined` would have flagged every ungrouped block.
            const advice = g.heading ? standardAdviceById?.get(g.heading.id) : null;
            const advises =
              !!advice && !headingProblem && advice.headingId === g.heading.id;
            return (
              <div key={key} className="mb-0.5">
                <div
                  draggable={!!g.heading}
                  onDragStart={
                    g.heading ? (ev) => onDragStart(ev, g.heading.id) : undefined
                  }
                  onDragEnd={onDragEnd}
                  onDragOver={
                    g.heading ? (ev) => onDragOverRow(ev, g.heading.id) : undefined
                  }
                  onDrop={g.heading ? (ev) => onDropRow(ev, g.heading.id) : undefined}
                  className={`group flex items-center gap-0.5 rounded px-1.5 py-1 cursor-pointer transition ${
                    g.heading && dragId === g.heading.id ? "opacity-50" : ""
                  } ${g.heading ? dropLine(g.heading.id) : ""} ${
                    headingProblem
                      ? "bg-rose-500/10 hover:bg-rose-500/[0.16]"
                      : advises
                      ? "bg-amber-400/10 hover:bg-amber-400/[0.18]"
                      : g.heading
                      ? "hover:bg-white/[0.06]"
                      : "hover:bg-white/[0.04] opacity-80"
                  }`}
                  onClick={() => {
                    // The row itself always navigates: expand it if it's
                    // collapsed (the document must be open for the target to
                    // exist to scroll to), then jump either way. Previously
                    // this only jumped on the transition into "open", so
                    // clicking an already-expanded heading a second time just
                    // collapsed it — the user had to collapse and re-expand to
                    // get the scroll. Collapsing now lives on the arrow alone.
                    if (!isOpen) onToggleHeading(key, g.headingIndex, true);
                    if (g.heading) onJump(g.heading.id);
                  }}
                  title={
                    advises
                      ? describeStandardAdvice(advice)
                      : g.heading
                      ? "Click to jump to this section — use the arrow to collapse"
                      : "Fields before the first heading"
                  }
                >
                  <ChevronRight
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onToggleHeading(key, g.headingIndex, !isOpen);
                    }}
                    title={isOpen ? "Collapse this section" : "Expand this section"}
                    className={`w-3.5 h-3.5 shrink-0 transition-transform hover:text-white ${
                      headingProblem
                        ? "text-rose-300"
                        : advises
                        ? "text-amber-300"
                        : "text-slate-500"
                    } ${isOpen ? "rotate-90" : ""}`}
                  />
                  {/* Same number as the document's own heading band, so the
                      index and the form can be read against each other.
                      OUTSIDE the truncating span: a number is a handful of
                      characters and is the thing you scan for, so it must not
                      be what gets cut off when a title is long. Quiet grey and
                      normal weight — present when looked for, not competing
                      with the titles in a dense list. */}
                  {g.heading && numberById?.get(g.heading.id) && (
                    <span className="shrink-0 tabular-nums font-normal text-[10px] text-slate-500">
                      {numberById.get(g.heading.id)}
                    </span>
                  )}
                  <span
                    className={`flex-1 min-w-0 truncate text-xs leading-tight ${
                      headingProblem
                        ? "font-semibold text-rose-200"
                        : advises
                        ? "font-semibold text-amber-100"
                        : g.heading
                        ? "font-semibold text-slate-100"
                        : "italic text-slate-400"
                    }`}
                  >
                    {g.heading ? g.heading.question || "(untitled heading)" : "Ungrouped fields"}
                  </span>
                  {headingProblem && (
                    <AlertTriangle className="w-3 h-3 shrink-0 text-rose-300" />
                  )}
                  {/* The same book icon the "Use from template" button wears,
                      so the mark in the index and the menu it points at are
                      recognisably the same thing. The row's tooltip carries the
                      full sentence; the document spells it out in words. */}
                  {advises && (
                    <LibraryBig className="w-3 h-3 shrink-0 text-amber-300" />
                  )}
                  {g.heading &&
                    moveBtns(
                      () => onMoveHeadingBlock(g.heading.id, -1),
                      () => onMoveHeadingBlock(g.heading.id, 1),
                      "this section"
                    )}
                </div>

                {isOpen &&
                  g.items.map((q) => {
                    const fieldProblem = problemById?.has(q.id);
                    // Same nesting as the document, so the index and the form
                    // read the same shape.
                    const depth = branchDepth?.get(q.id) || 0;
                    return (
                      <div
                        key={q.id}
                        draggable
                        onDragStart={(ev) => onDragStart(ev, q.id)}
                        onDragEnd={onDragEnd}
                        onDragOver={(ev) => onDragOverRow(ev, q.id)}
                        onDrop={(ev) => onDropRow(ev, q.id)}
                        onClick={() => onJump(q.id)}
                        title={
                          depth > 0
                            ? `${q.question || "(untitled)"} — only appears after a certain answer`
                            : `${q.question || "(untitled)"} — drag to reorder`
                        }
                        style={{ marginLeft: `${0.75 + depth * 0.6}rem` }}
                        className={`group flex items-center gap-1 pl-2 border-l rounded px-1.5 py-[3px] cursor-pointer transition ${
                          dragId === q.id ? "opacity-50" : ""
                        } ${dropLine(q.id)} ${
                          fieldProblem
                            ? "border-rose-400/70 bg-rose-500/[0.08] hover:bg-rose-500/[0.16]"
                            : depth > 0
                            ? "border-fuchsia-400/45 hover:bg-white/[0.05]"
                            : "border-white/10 hover:bg-white/[0.05]"
                        }`}
                      >
                        {depth > 0 && (
                          <span className="shrink-0 text-[11px] text-fuchsia-300/70">↳</span>
                        )}
                        {/* Outside the truncating span, same grey as a
                            heading's — one quiet column of numbers down the
                            index, whatever the nesting level. */}
                        {numberById?.get(q.id) && (
                          <span className="shrink-0 tabular-nums text-[10px] text-slate-500">
                            {numberById.get(q.id)}
                          </span>
                        )}
                        <span
                          className={`flex-1 min-w-0 truncate text-[11px] leading-tight ${
                            fieldProblem ? "text-rose-200" : "text-slate-400"
                          }`}
                        >
                          {q.question || "(untitled)"}
                        </span>
                        {fieldProblem && (
                          <AlertTriangle className="w-3 h-3 shrink-0 text-rose-300" />
                        )}
                        {moveBtns(
                          () => onMoveQuestion(q.id, -1),
                          () => onMoveQuestion(q.id, 1),
                          "this field"
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })}

          {/* An explicit tail target, so "put it at the end" is somewhere you
              can actually aim rather than the sliver below the last row. Shown
              for a template block AND for an ordinary row being reordered. */}
          {(templateOver || dragId) && (
            <div className="mt-1 px-2 py-3 rounded-lg border border-dashed border-purple-300/50 bg-purple-500/10 text-[11px] text-purple-100 text-center">
              Drop here to move to the end
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SectionEditor() {
  const { studyId } = useParams();
  const [template, setTemplate] = useState(null);
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  // Which question's "show this only when…" popover is open, and its DRAFT
  // rule state — the indent arrows and the conditional badge both target
  // this. The draft is deliberately separate from the persisted
  // `q.visibleWhen`: buildCondition() drops a rule that doesn't have a value
  // yet (by design, so an unfinished condition never gets saved), so if the
  // popover re-derived its rows from the persisted value on every keystroke,
  // picking a controlling question but not yet its answer would immediately
  // round-trip through buildCondition -> nothing saved -> back to "no rule"
  // -> the picker resets to its start screen before the user can finish.
  // Keeping an independent draft is what lets an in-progress selection stay
  // on screen while still saving the moment it becomes valid.
  const [branchPopoverFor, setBranchPopoverFor] = useState(null);
  const [branchDraft, setBranchDraft] = useState(null); // {mode, rules, complex}
  // Which heading's "merge into…" picker is open.
  const [mergeMenuFor, setMergeMenuFor] = useState(null);
  const [problems, setProblems] = useState([]);
  const [exporting, setExporting] = useState(false);
  // The whole-document preview (see SourceDocPreviewModal) — what replaced the
  // per-question grey preview boxes.
  const [previewing, setPreviewing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Versions live in their own Firestore subcollection now (not a field on
  // the template), so opening the modal fetches them on demand.
  const [templateVersions, setTemplateVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exportingVersionId, setExportingVersionId] = useState(null);
  const [redlineVersion, setRedlineVersion] = useState(null);
  // What that redline is measured against: the previous approved version of
  // the SAME visit, picked out of the history already in hand. Derived on
  // read rather than stamped onto the version at approval time, so it works
  // on every version already recorded and adds no write to the approval path.
  const redlineBaseline = previousApprovedVersion(templateVersions, redlineVersion);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Who is signed in, resolved once and held in a ref so every audit write and
  // every version snapshot can attribute itself without another lookup — and
  // without a re-render dependency, since these are called from inside save
  // handlers rather than from render.
  const actorRef = useRef({ email: "", uid: null, name: "", initials: "" });
  // The outline numbers, reachable from the save handlers (which run outside
  // render) so an audit line can say "1.2 Weight" rather than just "Weight".
  const numberByIdRef = useRef(new Map());
  useEffect(() => {
    let cancelled = false;
    resolveCurrentActor().then((a) => {
      if (!cancelled) actorRef.current = a;
    });
    return () => {
      cancelled = true;
    };
  }, []);
  // Version id currently being restored — disables every Restore button
  // while it's in flight rather than just the one clicked, so a second click
  // can't start a second restore mid-write.
  const [restoringVersionId, setRestoringVersionId] = useState(null);
  const [saveError, setSaveError] = useState(false);
  // Reordering a visit with the up/down arrows changes array order with
  // nothing else on screen changing (the selected visit stays selected, and
  // its document content looks identical) — so the click otherwise LOOKS like
  // it did nothing. This is the confirmation that something happened.
  const [visitMoveNotice, setVisitMoveNotice] = useState(null);
  const visitMoveNoticeTimer = useRef(null);
  // Same problem, one level up: the section picker keeps showing the section
  // you just moved, so reordering sections looks like it did nothing either.
  const [sectionMoveNotice, setSectionMoveNotice] = useState(null);
  const sectionMoveNoticeTimer = useRef(null);
  useEffect(
    () => () => {
      if (visitMoveNoticeTimer.current) clearTimeout(visitMoveNoticeTimer.current);
      if (sectionMoveNoticeTimer.current) clearTimeout(sectionMoveNoticeTimer.current);
    },
    []
  );
  // Approval writes to Firestore and takes a second or two; the buttons show a
  // spinner and lock while it runs.
  const [approving, setApproving] = useState(false);
  // Heading index whose block is being copied to other visits, or null.
  const [copyBlockFor, setCopyBlockFor] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);
  // Id of the one question being copied ("Copy question"), or null.
  const [copyQuestionFor, setCopyQuestionFor] = useState(null);
  const {
    alert: showAlert,
    confirm: askConfirm,
    prompt: askPrompt,
    dialog,
  } = useDialog();
  // Which outline headings are expanded. Collapsed by default so a long form
  // fits on one screen — the whole point of the outline.
  const [outlineOpen, setOutlineOpen] = useState(() => new Set());
  // "Use from template" dropdown.
  const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
  const templateMenuRef = useRef(null);
  // "Add Section" dropdown, in the page header — one menu for every kind of
  // section: a blank one, the eligibility checklist, and the standing logs.
  // (It was three separate buttons; same actions, one control.)
  const [logMenuOpen, setLogMenuOpen] = useState(false);
  const logMenuRef = useRef(null);
  // The standing-logs sub-list inside that menu. Expanded inline rather than
  // as a second popover — a nested popover would need its own outside-click
  // and edge handling to say "pick one of these".
  const [logSubmenuOpen, setLogSubmenuOpen] = useState(false);
  // "Add Inclusion/Exclusion". Reading a protocol is a slow server call — up to
  // a minute on a long document — so this drives the full StepProgressOverlay
  // rather than only greying out a small button in a toolbar: a coordinator who
  // clicks and then sees nothing change assumes it didn't work and clicks again.
  // `step` is what the overlay is currently saying; "" means it is closed.
  const [eligibilityStep, setEligibilityStep] = useState("");
  const eligibilityBusy = eligibilityStep !== "";
  // Visit picker — a custom dropdown (not a plain <select>) so its rows can
  // be dragged to reorder visits, not just switched between.
  const [visitMenuOpen, setVisitMenuOpen] = useState(false);
  const visitMenuRef = useRef(null);
  const [draggingVisit, setDraggingVisit] = useState(null);
  const [visitDragOver, setVisitDragOver] = useState(null);
  // Which reusable block is currently being dragged, held for the whole drag.
  // The outline needs a reliable "a template is in flight" signal that doesn't
  // depend on re-reading dataTransfer during dragover.
  const [draggingTemplateId, setDraggingTemplateId] = useState(null);
  // Ids of just-added blocks — outlined green until the user clicks one, so a
  // freshly inserted section is easy to spot even though it lands at the top.
  const [justAddedIds, setJustAddedIds] = useState(() => new Set());
  // The question a jump just landed on, ringed for a moment. See flashQuestion.
  const [flashId, setFlashId] = useState(null);
  const flashTimer = useRef(null);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    []
  );
  const navigate = useNavigate();

  const clearJustAdded = (id) =>
    setJustAddedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  // Close the template menu (and the visit and log pickers) on an outside click.
  useEffect(() => {
    const handler = (e) => {
      if (templateMenuRef.current && !templateMenuRef.current.contains(e.target))
        setTemplateMenuOpen(false);
      if (visitMenuRef.current && !visitMenuRef.current.contains(e.target))
        setVisitMenuOpen(false);
      if (logMenuRef.current && !logMenuRef.current.contains(e.target)) {
        setLogMenuOpen(false);
        setLogSubmenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close a heading's "merge into…" picker on an outside click. A per-row
  // popover (one per heading) rather than a single fixed one, so this checks
  // by CSS class instead of a single ref.
  useEffect(() => {
    if (!mergeMenuFor) return;
    const handler = (e) => {
      if (!e.target.closest?.(".merge-menu-root")) setMergeMenuFor(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mergeMenuFor]);

  // Toggling a section in the outline toggles it in the document too, so the
  // index and the form never disagree about what's open.
  const toggleOutlineGroup = (key, headingIndex, willOpen) => {
    setOutlineOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    // The "ungrouped fields" pseudo-group owns no heading. Index 0 IS a real
    // section, so this must test for null, not falsiness.
    if (headingIndex === null || headingIndex === undefined) return;
    const sectionKey = sectionKeysOf(selectedEventObj?.questions).get(headingIndex);
    if (!sectionKey) return;
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (willOpen) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };

  // Headings collapsed in the MAIN document (separate from the outline's own
  // expand state — one is a table of contents, the other is the document).
  // Holds collapse KEYS, not positions — see sectionKeysOf.
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());

  // Takes the heading's POSITION and resolves it to that heading's stable
  // collapse key, so an edit elsewhere in the form can't shift which section a
  // stored "collapsed" refers to.
  const toggleSection = (headingIndex) => {
    const q = (selectedEventObj?.questions || [])[headingIndex];
    if (q) clearJustAdded(q.id); // clicking it acknowledges the "new" highlight
    const sectionKey = sectionKeysOf(selectedEventObj?.questions).get(headingIndex);
    if (!sectionKey) return;
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };

  // Scroll the document to a heading/field. Pure navigation — nothing is
  // selected, edited or saved by jumping. If the target sits inside a collapsed
  // heading, open that heading first so the click can't land on nothing.
  const jumpToQuestion = (id) => {
    clearJustAdded(id); // navigating to it counts as acknowledging it
    const t = templateRef.current || template;
    const ev = t?.sections
      ?.find((s) => s.name === selectedSection)
      ?.events?.find((e) => e.eventName === selectedEvent);

    // Which heading must be open for this target to be visible: the target
    // itself when it's a heading, otherwise the heading that owns it.
    const keys = sectionKeysOf(ev?.questions);
    let owner = null;
    let toOpen = null;
    (ev?.questions || []).some((q, i) => {
      if (q.type === "Header") {
        if (q.id === id) {
          toOpen = keys.get(i);
          return true;
        }
        owner = keys.get(i);
      } else if (q.id === id) {
        toOpen = owner;
        return true;
      }
      return false;
    });
    if (toOpen) {
      setCollapsedSections((prev) => {
        if (!prev.has(toOpen)) return prev;
        const next = new Set(prev);
        next.delete(toOpen);
        return next;
      });
    }

    // Next frame, so a just-expanded field exists in the DOM to scroll to.
    requestAnimationFrame(() => {
      const el = document.getElementById(anchorId(id));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    flashQuestion(id);
  };

  // A brief ring on whatever was just jumped to. Landing in the middle of a
  // long form after a smooth scroll leaves you looking at a screen of cards
  // with no idea which one you asked for — so the target says so itself, for
  // long enough to be seen and short enough not to become another badge.
  const flashQuestion = (id) => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
    setFlashId(id);
    flashTimer.current = setTimeout(() => setFlashId(null), 1800);
  };

  // Switching event resets the outline's expanded state — the keys belong to
  // the event you were looking at.
  // Every section starts collapsed, so opening an event shows just its headings
  // — the whole form at a glance instead of a wall of fields to scroll past.
  useEffect(() => {
    setOutlineOpen(new Set());
    setJustAddedIds(new Set());
    setBranchPopoverFor(null);
    setBranchDraft(null);
    setMergeMenuFor(null);
    const t = templateRef.current || template;
    const ev = t?.sections
      ?.find((s) => s.name === selectedSection)
      ?.events?.find((e) => e.eventName === selectedEvent);
    // Every section collapsed. This used to collect heading IDS while the
    // collapse set is keyed by section key, so nothing actually matched and the
    // whole form opened expanded.
    setCollapsedSections(new Set(sectionKeysOf(ev?.questions).values()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, selectedSection]);

  // 🔹 Load or initialize template
  useEffect(() => {
    const loadTemplate = async () => {
      const ref = doc(db, getSourcexTemplatesPath(), studyId);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        // Upgraded to the v2 shape in memory on every load. Nothing is written
        // back until the coordinator edits something, so opening a template
        // read-only never mutates it.
        const data = snap.data();
        // A template approved BEFORE approval was per visit carries only a
        // template-level `status: "approved"`. Its status is now derived from
        // its visits, so without stamping that approval onto each of them the
        // first save would find none approved and silently demote a live
        // template — the one patients are enrolled against — to a draft.
        // No-op for anything already carrying per-visit status.
        const normalized = normalizeTemplate(
          backfillEventApproval({ id: snap.id, ...data })
        );
        // Never keep the old embedded versionHistory array in memory — it has
        // no role anymore (versions live in their own subcollection) and
        // re-spreading it into a future write is exactly what can push the
        // document over Firestore's 1 MiB limit.
        delete normalized.versionHistory;
        setTemplate(normalized);
        setProblems(validateTemplate(normalized));
        // Self-heal in the background: a template opened before this fix may
        // still carry that array on the SERVER doc too. Moving it out (and
        // clearing the field) here means simply opening the editor fixes a
        // template that was previously blocked on approval, without the
        // coordinator having to do anything.
        migrateEmbeddedVersions(studyId, data.versionHistory).catch((err) => {
          // eslint-disable-next-line no-console
          console.error("[SectionEditor] version-history migration failed:", err);
        });
      } else {
        const newTemplate = normalizeTemplate({ studyId, sections: [] });
        await setDoc(ref, newTemplate);
        setTemplate(newTemplate);
      }
    };
    loadTemplate();
  }, [studyId]);

  // The version history is fetched up front, not only when Version History is
  // opened: the Approve button needs this visit's latest approved version to
  // know whether anything has actually changed since it, and that has to be
  // known before anyone reaches for the button. Opening the modal still
  // refetches, so a version written moments ago is never missed.
  useEffect(() => {
    let cancelled = false;
    listTemplateVersions(studyId)
      .then((v) => {
        if (!cancelled) setTemplateVersions(v);
      })
      .catch((err) => {
        // Not fatal: without the history the Approve button simply stays
        // enabled, which is how it behaved before this existed.
        // eslint-disable-next-line no-console
        console.error("[SectionEditor] failed to preload version history:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [studyId]);

  // Always-current template, so a mutation reads the latest state even when a
  // previous one's write is still in flight — the render-lag fix below makes
  // clicks fast enough that a stale closure would otherwise drop an edit.
  const templateRef = useRef(null);
  useEffect(() => {
    templateRef.current = template;
  }, [template]);

  // Default the section selector to the first section ("Study Visits" — the only
  // one) once the template loads, so the user isn't stuck on "Select Section".
  // Guarded so it runs once and never fights a later manual change.
  const didDefaultSection = useRef(false);
  useEffect(() => {
    if (didDefaultSection.current) return;
    const secs = template?.sections || [];
    if (secs.length > 0) {
      didDefaultSection.current = true;
      setSelectedSection((prev) => prev || secs[0].name);
    }
  }, [template]);

  // A log section — and the eligibility checklist — holds exactly one event, so
  // there is nothing to choose: select it as soon as that section is showing.
  // Without this the editor sits on "Select Visit" over a section with a single
  // obvious answer. Not guarded by a ran-once ref like the section default
  // above: this has to re-run every time the user switches BACK to such a
  // section, which clears selectedEvent.
  useEffect(() => {
    const secs = templateRef.current?.sections || template?.sections || [];
    const sec = secs.find((s) => s.name === selectedSection);
    if (sec?.kind !== "log" && sec?.kind !== "criteria") return;
    const only = sec.events?.[0]?.eventName;
    if (only && selectedEvent !== only) setSelectedEvent(only);
  }, [template, selectedSection, selectedEvent]);

  // 🔹 Guided tour (built documents only, flag SOURCEX_TOUR_ENABLED, 2026-10-29).
  // Asked once per person + company + site ("Show me" / "No thanks", both
  // remembered, sourcexTourStore.js); the header's Tour button replays it. It only
  // explains: getting the page ready for a step (prepareTourStep) just picks a visit
  // and opens a heading, which is view state; nothing is saved.
  const tourAvailable = SOURCEX_TOUR_ENABLED && String(studyId || "").endsWith("__assembled");
  const [tourOpen, setTourOpen] = useState(false);
  const [tourAsking, setTourAsking] = useState(false);
  const tourWho = () => ({ uid: auth.currentUser?.uid, tenantId: getActiveTenantId(), siteId: getActiveSiteId() });
  const startTour = () => setTourOpen(true);
  const tourAskedRef = useRef(false);
  useEffect(() => {
    if (!tourAvailable || !template || tourAskedRef.current) return;
    tourAskedRef.current = true;
    if (hasAnsweredTour(tourWho())) return;
    setTourAsking(true);
    (async () => {
      const yes = await askConfirm(
        `A quick walk through what this page can do, in ${TOUR_STEPS.length} short steps. You can stop at any time, and take it again later with the Tour button at the top.`,
        { title: "Would you like a quick tour?", confirmLabel: "Show me", cancelLabel: "No thanks" }
      );
      markTourAnswered(tourWho(), yes ? "taken" : "declined");
      setTourAsking(false);
      if (yes) setTourOpen(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourAvailable, template]);

  // View state only (sourcexTourSteps.js `needs`):
  //   "visit"    select a visit if none is (the first one with questions here)
  //   "expanded" + open its first heading
  //   "question" / "table" / "editor"  + open the heading holding that item,
  //              found in the WHOLE visit (findTourItem); "editor" also opens that
  //              question's editor.
  // The editor the tour opened is closed again (setEditingIndex(null): for an
  // existing question that is exactly Cancel, nothing saved) by the next step that
  // doesn't need it, and when the tour ends. An editor the person already had open
  // is used as is and left open. Kept in a ref so the tour gets one stable function.
  const tourEditorIdRef = useRef(null);
  const editingIndexRef = useRef(null);
  editingIndexRef.current = editingIndex;
  const closeTourEditor = () => {
    if (tourEditorIdRef.current && editingIndexRef.current === tourEditorIdRef.current) setEditingIndex(null);
    tourEditorIdRef.current = null;
  };
  const openHeading = (questions, headingIndex) => {
    const key = sectionKeysOf(questions).get(headingIndex);
    if (!key) return;
    setCollapsedSections((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };
  const prepareTourRef = useRef(null);
  prepareTourRef.current = async (needs) => {
    if (needs !== "editor") closeTourEditor();
    if (!needs) return;
    const t = templateRef.current;
    const secName = selectedSection || t?.sections?.[0]?.name || "";
    const sec = (t?.sections || []).find((s) => s.name === secName);
    if (!sec) return;
    let evName = selectedEvent;
    if (!evName || !(sec.events || []).some((e) => e.eventName === evName)) {
      const pick = (sec.events || []).find((e) => (e.questions || []).length) || sec.events?.[0];
      if (!pick) return;
      evName = pick.eventName;
      if (secName !== selectedSection) setSelectedSection(secName);
      setSelectedEvent(evName);
      // Let the visit draw (it opens with every heading closed) before opening one.
      await new Promise((r) => setTimeout(r, 120));
    }
    const qs = (sec.events || []).find((e) => e.eventName === evName)?.questions || [];
    if (needs === "expanded") {
      const [firstHeading] = sectionKeysOf(qs).keys();
      if (firstHeading !== undefined) openHeading(qs, firstHeading);
      return;
    }
    if (needs !== "question" && needs !== "table" && needs !== "editor") return;
    // The person's own open editor: show that one.
    if (needs === "editor" && editingIndexRef.current && !tourEditorIdRef.current) return;
    const item = findTourItem(qs, needs);
    if (!item) return; // the visit has none: the step explains where it would be
    if (item.headingIndex >= 0) openHeading(qs, item.headingIndex);
    if (needs === "editor" && !editingIndexRef.current) {
      tourEditorIdRef.current = qs[item.index].id;
      setEditingIndex(qs[item.index].id);
    }
  };
  const prepareTourStep = useCallback((needs) => prepareTourRef.current(needs), []);
  const endTour = useCallback(() => {
    closeTourEditor();
    setTourOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Id of a field that was JUST inserted and whose editor is open for the first
  // time. If the user cancels instead of saving, this blank field is removed
  // rather than left behind as an empty "structural problem". Cleared once the
  // field is saved, or once an existing field is opened for editing.
  const pendingNewId = useRef(null);

  // Coalesced background persistence. Rapid edits (add/move a run of fields)
  // used to each block on their own Firestore round-trip before the UI updated
  // — that was the lag. Now the local state updates instantly and the write is
  // debounced, so a burst of edits costs ONE write of the final state.
  const writeTimer = useRef(null);
  const pendingWrite = useRef(null);

  const flushWrite = async () => {
    if (writeTimer.current) {
      clearTimeout(writeTimer.current);
      writeTimer.current = null;
    }
    const clean = pendingWrite.current;
    pendingWrite.current = null;
    if (!clean) return;
    try {
      await updateDoc(doc(db, getSourcexTemplatesPath(), studyId), clean);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[SectionEditor] save failed:", err);
      setSaveError(true);
    }
  };

  // Bounded undo history. Every call to saveTemplate is a discrete user edit
  // (add/move/delete/etc. all funnel through it), so the state it's about to
  // replace is exactly the one "undo" should restore. Capped so a long editing
  // session doesn't grow this without limit.
  const UNDO_LIMIT = 25;
  const undoStackRef = useRef([]);
  const isUndoingRef = useRef(false);
  const [canUndo, setCanUndo] = useState(false);

  // Optimistic: update local state (and the ref) synchronously so the UI is
  // instant, then schedule the write. stripAnswers is the PHI guard — a
  // `sourcex_templates` doc is shared by every patient and has no per-patient
  // audit trail, so a clinical value must never reach it.
  const saveTemplate = (updated) => {
    // `status` is DERIVED from the visits on every single write, never set by
    // a caller — approved exactly when all of them are. Deriving it here, in
    // the one funnel every edit goes through, is what stops it drifting out of
    // step with the visits it describes (delete the last unapproved visit and
    // the template becomes approved; add a visit and it stops being).
    //
    // `approvedVisitCount` is the same fact flattened to a top-level number,
    // because that is the only shape a Firestore rule can read — rules cannot
    // walk into sections[].events[] to find out. See templateWriteOk.
    const derived = {
      ...updated,
      status: templateStatusFromSections(updated?.sections),
      approvedVisitCount: approvalProgress(updated?.sections).approved,
    };
    const clean = stripAnswers(derived);
    if (!isUndoingRef.current && templateRef.current) {
      undoStackRef.current.push(templateRef.current);
      if (undoStackRef.current.length > UNDO_LIMIT) undoStackRef.current.shift();
      setCanUndo(true);
    }
    templateRef.current = clean;
    setTemplate(clean);
    setProblems(validateTemplate(clean));
    setSaveError(false);
    pendingWrite.current = clean;
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      flushWrite();
    }, 500);
    return clean;
  };

  // Pop the last snapshot and make it current again. Goes through
  // saveTemplate (so the reverted state still gets persisted and validated),
  // but flagged so the undo itself isn't pushed back onto the stack.
  const undoEdit = () => {
    const stack = undoStackRef.current;
    if (!stack.length) return;
    const prev = stack.pop();
    isUndoingRef.current = true;
    try {
      saveTemplate(prev);
    } finally {
      isUndoingRef.current = false;
    }
    setCanUndo(stack.length > 0);
  };

  // Flush any pending write when leaving the editor, so a debounced change is
  // never lost on Back/navigate.
  useEffect(() => {
    return () => {
      flushWrite();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔹 Approve THE SELECTED VISIT.
  //
  // Approval is per visit, not per template. A source document IS one visit's
  // worth of form: a coordinator finishes Screening weeks before Visit 3 even
  // exists, and signing off the whole template to release Screening meant
  // either waiting for everything or approving work nobody had reviewed.
  //
  // The template's own status is DERIVED from its visits — approved only once
  // every visit is (templateStatusFromSections). Nothing sets it directly, so
  // patient enrolment's "this is still a draft" warning and the Firestore rule
  // that locks an approved template both keep meaning what they always did.
  const markApproved = async () => {
    if (approving) return; // a second click while it's working would re-run it
    const ev = selectedEventObj;
    if (!ev) {
      await showAlert("Pick the visit you want to approve first.", {
        title: "No visit selected",
      });
      return;
    }

    // The same check the button's disabled state uses, re-run here. The button
    // is the guard people see; this is the one that holds if a click lands
    // just as the history finishes loading, or from a keyboard activation on a
    // stale render. Nothing to approve is not an error — say so and stop.
    const approvedNow = versionEvent(
      latestApprovedVersionFor(templateVersions, ev.id)
    );
    if (eventIsApproved(ev) && sameVisitContent(approvedNow, ev)) {
      await showAlert(
        `“${ev.eventName}” is exactly the version that was already approved, so there is nothing to sign off. Make a change first — re-approving an untouched visit would record a new version and a new sign-off date for work that never happened.`,
        { title: "Nothing to approve" }
      );
      return;
    }

    // Approval is the GCP sign-off: it makes this the form every patient's
    // source document is built from. Block it on structural problems rather
    // than warning, since a duplicate question id or a branch pointing at a
    // deleted question corrupts data silently once patients are enrolled.
    // Scoped to THIS visit — a half-built Visit 3 must not hold up Screening —
    // but a question id shared with another visit still blocks, because that
    // one corrupts both. See validateEvent.
    const latest = templateRef.current || template;
    const found = validateEvent(latest, ev.id);
    if (found.length) {
      setProblems(validateTemplate(latest));
      await showAlert(found.slice(0, 8).join("\n"), {
        title: `Can't approve ${ev.eventName} — ${found.length} problem${
          found.length === 1 ? "" : "s"
        } in this visit must be fixed first`,
        tone: "danger",
      });
      return;
    }

    // The built (assembled) template lives at `{studyId}__assembled`, but the
    // SourceX patient workflow enrolls every patient against the CANONICAL doc
    // `sourcex_templates/{baseStudyId}` (see AddPatientModal). So approving a
    // visit here must PROMOTE that visit into the canonical doc — otherwise
    // the work of the Protocol→eCRF step never reaches a patient.
    const isAssembled = studyId.endsWith("__assembled");
    const baseStudyId = studyId.replace(/__assembled$/, "");

    // Who is signing off. Resolved fresh at the moment of approval rather than
    // read from the ref, so the record can never attribute an approval to a
    // stale lookup taken when the page opened. See approverIdentity.js — the
    // name is best-effort and the email is the identity that always holds.
    const actor = await resolveCurrentActor();
    actorRef.current = actor;

    const progress = approvalProgress(latest.sections);
    const remaining =
      progress.total - progress.approved - (eventIsApproved(ev) ? 0 : 1);
    const ok = await askConfirm(
      `${
        isAssembled
          ? `“${ev.eventName}” becomes the ACTIVE source document for study ${baseStudyId} — every patient enrolled from now on gets this version of it.`
          : `“${ev.eventName}” becomes an approved source document for this study.`
      }\n\nNo other visit is approved or changed by this.${
        remaining > 0
          ? `\n\n${remaining} other visit${
              remaining === 1 ? "" : "s"
            } will still be a draft.`
          : "\n\nThis is the last one — the whole template becomes approved."
      }\n\nMake sure you've verified this visit's fields against the protocol.\n\nApproving as: ${describeActor(
        actor
      )}`,
      { title: `Approve ${ev.eventName}?`, confirmLabel: "Approve this visit" }
    );
    if (!ok) return;

    // Everything past this point writes to Firestore and takes a moment. The
    // buttons show a spinner meanwhile, so the gap between clicking Approve and
    // the confirmation can't be mistaken for nothing happening — or for it
    // already being done.
    setApproving(true);
    try {
      // Make sure any debounced edits are on disk before we snapshot.
      await flushWrite();
      const approvedAt = new Date().toISOString();
      const approvedBy = {
        email: actor.email || "",
        name: actor.name || "",
        initials: actor.initials || "",
      };

      // Stamp the approval on THIS visit, then recompute the template's status
      // from all of them.
      const nextSections = (latest.sections || []).map((s) => ({
        ...s,
        events: (s.events || []).map((e) =>
          e?.id === ev.id ? { ...e, status: "approved", approvedAt, approvedBy } : e
        ),
      }));
      const nextTemplate = stripAnswers({
        ...latest,
        sections: nextSections,
        status: templateStatusFromSections(nextSections),
      });
      const approvedEventSections = sectionsForEvent(nextTemplate.sections, ev.id);

      // One durable record of "this is what was approved, and when".
      const approvalVersion = makeTemplateVersion({
        kind: "approved",
        label: isAssembled ? `Approved (promoted from ${studyId})` : "Approved",
        sections: approvedEventSections,
        eventId: ev.id,
        eventName: ev.eventName || "",
        actor,
      });

      const logApproval = () =>
        logTemplateAuditEvent({
          templateId: studyId,
          action: TEMPLATE_AUDIT_ACTIONS.TEMPLATE_APPROVED,
          title: `Approved visit “${ev.eventName}”`,
          eventId: ev.id,
          eventName: ev.eventName || "",
          actor,
          metadata: {
            approvedAt,
            approvedByName: actor.name || "",
            approvedByInitials: actor.initials || "",
            promotedTo: isAssembled ? baseStudyId : "",
            templateStatus: nextTemplate.status,
          },
        });

      if (isAssembled) {
        try {
          const canonicalRef = doc(db, getSourcexTemplatesPath(), baseStudyId);
          const canonicalSnap = await getDoc(canonicalRef);
          const canonical = canonicalSnap.exists() ? canonicalSnap.data() : null;
          // Version history belongs to the canonical doc's OWN timeline, not
          // whatever the assembled working copy happened to carry. A template
          // opened before version history moved to its own subcollection may
          // still have the old embedded array — clear that first so the write
          // below can't reintroduce it.
          await migrateEmbeddedVersions(baseStudyId, canonical?.versionHistory);
          await pushTemplateVersion(baseStudyId, approvalVersion);

          // MERGE this one visit in, rather than replacing the canonical doc
          // wholesale. Wholesale was right while an approval covered every
          // visit at once; now it would wipe out every visit approved on an
          // earlier pass.
          const mergedSections = canonical?.sections?.length
            ? mergeEventIntoSections(canonical.sections, nextTemplate.sections, ev.id)
            : nextTemplate.sections;
          const mergedTemplate = stripAnswers({
            ...(canonical || nextTemplate),
            sections: mergedSections,
            status: templateStatusFromSections(mergedSections),
            studyId: baseStudyId,
            promotedFrom: studyId,
            promotedAt: approvedAt,
          });
          await setDoc(canonicalRef, mergedTemplate);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[SectionEditor] promote failed:", err);
          await showAlert("Sorry — could not promote this visit. Please try again.", {
            title: "Approval failed",
            tone: "danger",
          });
          return;
        }
        // The assembled working copy gets the same approval recorded in its
        // own history too, so reopening IT later still shows the approval.
        await pushTemplateVersion(studyId, approvalVersion);
        // Into the in-memory history immediately, so the Approve button greys
        // out the moment this lands rather than at the next page load.
        setTemplateVersions((prev) => [approvalVersion, ...prev]);
        saveTemplate(nextTemplate);
        logApproval();
        await flushWrite();
        await showAlert(
          `Study ${baseStudyId} will now enroll new patients against this version of “${ev.eventName}”.`,
          { title: `✅ ${ev.eventName} approved` }
        );
        return;
      }

      await pushTemplateVersion(studyId, approvalVersion);
      setTemplateVersions((prev) => [approvalVersion, ...prev]);
      saveTemplate(nextTemplate);
      logApproval();
      await flushWrite();
      await showAlert(
        nextTemplate.status === "approved"
          ? "That was the last one — every visit in this template is now approved."
          : `“${ev.eventName}” is approved. The other visits are still drafts.`,
        { title: `✅ ${ev.eventName} approved` }
      );
    } finally {
      setApproving(false);
    }
  };

  // 🔹 Open Version History — fetched from the versions subcollection each
  // time, so a version pushed a moment ago (e.g. by an approval that just
  // ran) always shows up rather than reading a possibly-stale local copy.
  const openHistory = async () => {
    // Version history is a VISIT's history now — without one selected there is
    // nothing to show, which is why the button that opens this is disabled.
    if (!selectedEventObj) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      setTemplateVersions(await listTemplateVersions(studyId));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[SectionEditor] failed to load version history:", err);
      setTemplateVersions([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // 🔹 Download an older version straight to Word, without touching the live
  // template. "What did this look like?" is asked far more often than "put it
  // back", and answering it by restoring — then restoring again to undo — is a
  // destructive way to read a document.
  const exportVersion = async (version) => {
    const ev = versionEvent(version);
    if (!ev) {
      // A whole-template snapshot has no single visit to export; fall back to
      // the visit currently open, if that snapshot contains it.
      const fallback = versionEvent({ ...version, eventId: selectedEventObj?.id });
      if (!fallback) {
        await showAlert(
          "This snapshot doesn't contain the visit you're viewing, so there's nothing to export from it. Restore it instead, or pick a visit it covers.",
          { title: "Nothing to export", tone: "danger" }
        );
        return;
      }
      return exportEventToDocx(version, fallback);
    }
    return exportEventToDocx(version, ev);
  };

  const exportEventToDocx = async (version, ev) => {
    setExportingVersionId(version.id);
    try {
      // The same header/footer identifiers a live export uses, so an archived
      // version prints as the document it was, not an unlabelled one.
      let study = {};
      try {
        const snap = await getDoc(
          doc(db, getStudiesPath(), exportStudyIdFor(template, studyId))
        );
        if (snap.exists()) study = snap.data();
      } catch (_) {
        /* defaults are fine — this is a read-only export */
      }
      const config = defaultExportConfig(
        study,
        template?.exportConfig || {},
        todayStr()
      );
      // The footer date is when THIS SNAPSHOT was approved, taken from the
      // snapshot's own visit — an archived copy has to carry the date it was
      // signed off, not the date somebody reprinted it. A built/backup
      // snapshot was never approved, so it prints as a draft dated when the
      // snapshot itself was taken.
      config.date = footerDateFor(ev, version.createdAt);
      // Stamped with WHICH version this is, so an exported archive copy can
      // never be mistaken for the current document.
      const archivedLabel = `${config.version || ""} · version of ${new Date(
        version.createdAt
      ).toLocaleString()}`.trim();
      await downloadSourceDocx({
        event: ev,
        config: { ...config, version: archivedLabel },
        filename: sourceDocxFilename(ev, config).replace(
          /\.docx$/,
          `_${String(version.createdAt).slice(0, 10)}.docx`
        ),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[SectionEditor] version export failed:", err);
      await showAlert("Sorry — that version could not be exported.", {
        title: "Export failed",
        tone: "danger",
      });
    } finally {
      setExportingVersionId(null);
    }
  };

  // 🔹 The audit trail — read fresh each time so an edit made a moment ago is
  // already in it.
  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      setAuditEvents(await listTemplateAuditEvents(studyId));
    } finally {
      setAuditLoading(false);
    }
  };

  // 🔹 Replace the current content with an earlier version (see Version
  // History). The current content is snapshotted first — a "backup" version
  // — so restoring is itself never a one-way trip either. Lands as a draft
  // regardless of what the restored version's own status was: restored
  // content needs a human to re-approve it before it can go live again,
  // same as any other edit to an approved template.
  const restoreVersion = async (version) => {
    const when = new Date(version.createdAt).toLocaleString();
    const vEventId = versionEventId(version);
    // A visit-scoped version puts back ONE visit. A whole-template snapshot
    // (the older ones) still replaces everything, and says so — restoring one
    // from a list headed by a single visit's name is exactly the surprise
    // worth spelling out before it happens.
    const ok = await askConfirm(
      vEventId
        ? `This replaces “${
            version.eventName || "this visit"
          }” with the version from ${when}. No other visit is touched, and the current content is saved as a version first.`
        : `This is a whole-template snapshot: it replaces EVERY visit with the version from ${when}, not just the one you're looking at. The current content is saved as a version first.`,
      {
        title: vEventId ? "Restore this visit?" : "Restore the whole template?",
        confirmLabel: "Restore",
        tone: vEventId ? undefined : "danger",
      }
    );
    if (!ok) return;

    setRestoringVersionId(version.id);
    try {
      await flushWrite();
      const latest = templateRef.current || template;

      // Back up only what is about to be overwritten, at the same scope.
      await pushTemplateVersion(
        studyId,
        makeTemplateVersion({
          kind: "backup",
          label: "Before restoring an older version",
          sections: vEventId
            ? sectionsForEvent(latest.sections, vEventId)
            : latest.sections,
          eventId: vEventId,
          eventName: vEventId ? eventNameById(latest, vEventId) : "",
          actor: actorRef.current,
        })
      );

      const nextSections = vEventId
        ? replaceEventInSections(latest.sections, vEventId, version.sections)
        : version.sections;

      const restored = normalizeTemplate({
        ...latest,
        sections: nextSections,
        status: "draft",
      });
      saveTemplate(restored);
      await flushWrite();
      setHistoryOpen(false);
      logTemplateAuditEvent({
        templateId: studyId,
        action: TEMPLATE_AUDIT_ACTIONS.VERSION_RESTORED,
        title: vEventId
          ? `Restored “${version.eventName || "visit"}” from the ${when} version`
          : `Restored the whole template from the ${when} version`,
        eventId: vEventId,
        eventName: version.eventName || "",
        actor: actorRef.current,
        metadata: { versionId: version.id, versionCreatedAt: version.createdAt },
      });
      await showAlert(
        "Restored. Review it, then approve again when you're ready.",
        { title: "✅ Restored" }
      );
    } finally {
      setRestoringVersionId(null);
    }
  };

  // 🔹 Add new Section
  const addSection = async () => {
    const name = await askPrompt("Enter new section name:", "", {
      title: "Add section",
      confirmLabel: "Add",
    });
    if (!name) return;
    const newSection = makeSection({ name, events: [] });
    const updated = { ...template, sections: [...(template.sections || []), newSection] };
    await saveTemplate(updated);
    setSelectedSection(name);
  };

  // 🔹 Add a standing LOG (con meds, medical history, exacerbations).
  //
  // A log gets its OWN section rather than being appended to a visit: it runs
  // for the whole study, not for one visit, and it prints its own landscape
  // page with a blank "Page __ of __". Everything else about it is an ordinary
  // section holding one ordinary event, so approval, versioning, reordering,
  // deletion, export and the patient's fill-in all work on it unchanged.
  const addLog = async (logId) => {
    const log = findLogTemplate(logId);
    if (!log) return;
    // The single gate every insertion passes through — the menu already
    // disables an unavailable log, but no future call site should be able to
    // get one in behind that. Same guard insertTemplateAt has.
    if (log.unavailable) return;

    const t = templateRef.current || template;
    const sections = t.sections || [];

    // `selectedSection` and `selectedEvent` are keyed by NAME, not id (see
    // updateSelectedEvent), so two sections sharing a name would collide and
    // an edit would land on whichever matched first. Select the one that's
    // already there instead of adding a second.
    const existing = sections.find((s) => s.name === log.label);
    if (existing) {
      setSelectedSection(existing.name);
      setSelectedEvent(existing.events?.[0]?.eventName || null);
      await showAlert(`“${log.label}” is already in this template.`);
      return;
    }

    const section = buildLogSection(logId, { makeSection });
    if (!section) return;
    const event = section.events[0];

    await saveTemplate({ ...t, sections: [...sections, section] });
    setSelectedSection(section.name);
    setSelectedEvent(event.eventName);

    // Named explicitly rather than through audit(), which fills in whichever
    // visit is SELECTED — that isn't this one yet.
    logTemplateAuditEvent({
      templateId: studyId,
      action: TEMPLATE_AUDIT_ACTIONS.LOG_ADDED,
      title: `Added log “${log.label}”`,
      eventId: event.id,
      eventName: event.eventName,
      actor: actorRef.current,
    });
  };

  // 🔹 Add the INCLUSION/EXCLUSION CRITERIA checklist.
  //
  // Gets its own section, exactly as a log does and for the same reason: it
  // belongs to the whole study rather than to one visit, and it prints its own
  // page. Everything else about it is an ordinary section holding one ordinary
  // event, so approval, versioning, reordering, deletion, export and the
  // patient's fill-in all work on it unchanged.
  //
  // The criteria are read from the protocol this template was generated from
  // (`template.protocolUrl`, written by GenerateTemplateModal). A study with no
  // protocol on file, or a protocol nothing can be read out of, gets the BLANK
  // checklist and a note saying so — a coordinator typing the criteria into the
  // grid is exactly what they do today, and it is a far better outcome than an
  // error and no section.
  const addEligibility = async () => {
    const t = templateRef.current || template;
    const sections = t.sections || [];

    // Selection is keyed by NAME, not id (see addLog), so a second section of
    // the same name would collide. Select the one that's already there.
    const existing = sections.find((s) => s.name === ELIGIBILITY_SECTION_NAME);
    if (existing) {
      setSelectedSection(existing.name);
      setSelectedEvent(existing.events?.[0]?.eventName || null);
      await showAlert(`“${ELIGIBILITY_SECTION_NAME}” is already in this template.`);
      return;
    }

    let criteria = EMPTY_ELIGIBILITY;
    let source = "blank";
    let note = "";
    // The overlay opens BEFORE the first await, so there is never a moment
    // between the click and the screen changing.
    setEligibilityStep("Finding the study protocol");
    try {
      // A study can hold its protocol in either of two places, depending on
      // which road it came in by — `protocolUrl` on this template (the AI
      // template generator) or its own document under
      // `studies/{id}/protocols` (the Protocols page). Checking only the first
      // told coordinators with an APPROVED protocol that they had none.
      //
      // exportStudyIdFor because a BUILT template is opened at
      // `{studyId}__assembled`, and `studies/{studyId}__assembled` does not
      // exist — the same trap the export header fell into. See that function.
      const protocolUrl =
        t.protocolUrl || (await findProtocolUrl(exportStudyIdFor(t, studyId)));

      if (protocolUrl) {
        setEligibilityStep("Finding the inclusion and exclusion criteria");
        criteria = await extractEligibilityCriteria(protocolUrl);
        source = "protocol";
        // A read that came back SHORT looks exactly like one that came back
        // complete, so where the server knows it might be partial, say so now
        // rather than let it be discovered by a monitor.
        note = eligibilityReadWarning(criteria.meta);
      } else {
        note =
          "No protocol PDF could be found for this study, so the checklist was added blank — type the criteria into the grid. (Upload one on the study's Protocols page, then delete this section and add it again to read the criteria from it.)";
      }
    } catch (e) {
      criteria = EMPTY_ELIGIBILITY;
      source = "blank";
      note = `Couldn't read the criteria from the protocol (${
        e.message || "unknown error"
      }). The checklist was added blank — type the criteria into the grid.`;
    }

    const section = buildEligibilitySection(criteria, { makeSection });
    const event = section.events[0];

    // templateRef again rather than `t`: the extraction takes a while, and
    // anything the coordinator saved in the meantime must not be thrown away.
    const latest = templateRef.current || t;
    saveTemplate({
      ...latest,
      sections: [...(latest.sections || []), section],
    });
    setSelectedSection(section.name);
    setSelectedEvent(event.eventName);
    // Only now, with the section on screen behind it. Closing the overlay
    // before the state is set would show the old page for a frame, which reads
    // as "nothing happened".
    setEligibilityStep("");

    // Named explicitly rather than through audit(), which fills in whichever
    // visit is SELECTED — that isn't this one yet.
    logTemplateAuditEvent({
      templateId: studyId,
      action: TEMPLATE_AUDIT_ACTIONS.ELIGIBILITY_ADDED,
      title:
        source === "protocol"
          ? "Added inclusion/exclusion criteria, read from the study protocol"
          : "Added a blank inclusion/exclusion criteria checklist",
      eventId: event.id,
      eventName: event.eventName,
      actor: actorRef.current,
      // "Where did this criterion come from?" is the first question asked when
      // one turns out to be wrong, and the answer has to survive in the record
      // rather than in a log line that rolls off. Which model read it, whether
      // the whole protocol was searched, and whether it was cut short.
      metadata: {
        source,
        ...(source === "protocol"
          ? {
              model: criteria.meta?.model || "",
              narrowed: criteria.meta?.narrowed === true,
              truncated: criteria.meta?.truncated === true,
              inclusionCount: criteria.inclusion?.length || 0,
              exclusionCount: criteria.exclusion?.length || 0,
            }
          : {}),
      },
    });

    // After the save, so the section is on screen behind the message.
    if (note) await showAlert(note);
  };

  // 🔹 Add new Event inside Section
  const addEvent = async () => {
    if (!selectedSection) {
      await showAlert("Please select a section first.");
      return;
    }
    const name = await askPrompt("Enter event name:", "", {
      title: "Add event",
      confirmLabel: "Add",
    });
    if (!name) return;

    const newEvent = makeEvent({ eventName: name, questions: [] });

    const updated = {
      ...template,
      sections: (template.sections || []).map((s) =>
        s.name === selectedSection
          ? { ...s, events: [...(s.events || []), newEvent] }
          : s
      ),
    };
    await saveTemplate(updated);
    // Named explicitly rather than through audit(), which fills in whichever
    // visit is SELECTED — that isn't this one yet.
    logTemplateAuditEvent({
      templateId: studyId,
      action: TEMPLATE_AUDIT_ACTIONS.VISIT_ADDED,
      title: `Added visit “${name}”`,
      eventId: newEvent.id,
      eventName: name,
      actor: actorRef.current,
    });
  };

  // Apply an immutable update to the currently selected event. Reads from the
  // ref (not the `template` closure) so a fast burst of edits can't drop one on
  // a stale value. Every mutating handler goes through here.
  const updateSelectedEvent = (fn) => {
    const t = templateRef.current || template;
    const updated = {
      ...t,
      sections: (t.sections || []).map((s) =>
        s.name !== selectedSection
          ? s
          : {
              ...s,
              events: (s.events || []).map((e) =>
                e.eventName !== selectedEvent ? e : fn(e)
              ),
            }
      ),
    };
    return saveTemplate(updated);
  };

  // 🔹 Reorder the selected SECTION among its siblings.
  const moveSection = (dir) => {
    const t = templateRef.current || template;
    const secs = [...(t.sections || [])];
    const i = secs.findIndex((s) => s.name === selectedSection);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= secs.length) return;
    [secs[i], secs[j]] = [secs[j], secs[i]];
    saveTemplate({ ...t, sections: secs });

    // Same confirmation a visit move gets, for the same reason — see
    // sectionMoveNotice.
    if (sectionMoveNoticeTimer.current) clearTimeout(sectionMoveNoticeTimer.current);
    setSectionMoveNotice(
      `Moved "${selectedSection}" ${dir < 0 ? "up" : "down"} — now section ${
        j + 1
      } of ${secs.length}.`
    );
    sectionMoveNoticeTimer.current = setTimeout(
      () => setSectionMoveNotice(null),
      2400
    );
  };

  // 🔹 Reorder an EVENT (visit) within the selected section.
  const moveEvent = (eventName, dir) => {
    const t = templateRef.current || template;
    let moved = false;
    let newPosition = 0;
    let total = 0;
    const updated = {
      ...t,
      sections: (t.sections || []).map((s) => {
        if (s.name !== selectedSection) return s;
        const evs = [...(s.events || [])];
        const i = evs.findIndex((e) => e.eventName === eventName);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= evs.length) return s;
        [evs[i], evs[j]] = [evs[j], evs[i]];
        moved = true;
        newPosition = j;
        total = evs.length;
        return { ...s, events: evs };
      }),
    };
    if (!moved) return;
    saveTemplate(updated);

    if (visitMoveNoticeTimer.current) clearTimeout(visitMoveNoticeTimer.current);
    setVisitMoveNotice(
      `Moved "${eventName}" ${dir < 0 ? "up" : "down"} — now visit ${newPosition + 1} of ${total}.`
    );
    visitMoveNoticeTimer.current = setTimeout(() => setVisitMoveNotice(null), 2400);
  };

  // 🔹 Drag-and-drop reorder of visits, dropped on `targetName` (inserted
  // just before it) or `null` for the end — same semantics as the outline's
  // own drag reorder for questions.
  const reorderEvents = (draggedName, targetName) => {
    if (!draggedName || draggedName === targetName) return;
    const t = templateRef.current || template;
    const updated = {
      ...t,
      sections: (t.sections || []).map((s) => {
        if (s.name !== selectedSection) return s;
        const evs = [...(s.events || [])];
        const di = evs.findIndex((e) => e.eventName === draggedName);
        if (di < 0) return s;
        const [moving] = evs.splice(di, 1);
        if (targetName === null) {
          evs.push(moving);
        } else {
          const ti = evs.findIndex((e) => e.eventName === targetName);
          evs.splice(ti < 0 ? evs.length : ti, 0, moving);
        }
        return { ...s, events: evs };
      }),
    };
    saveTemplate(updated);
  };

  // 🔹 Add a heading — a source-only group title that visually separates one
  // block of questions from the next. Assembly inserts these per form; here a
  // coordinator can add their own to reorganize the page.
  const addHeading = async () => {
    const label = await askPrompt("Heading text (e.g. Vital Signs):", "", {
      title: "Add heading",
      confirmLabel: "Add",
    });
    if (!label) return;
    const newH = makeQuestion({ question: label, type: "Header" });
    // Prepend, so a new section appears at the top instead of below a long form
    // the user would have to scroll to find.
    await updateSelectedEvent((e) => ({
      ...e,
      questions: [newH, ...(e.questions || [])],
    }));
    audit(TEMPLATE_AUDIT_ACTIONS.HEADING_ADDED, `Added section “${label}”`, {
      questionId: newH.id,
      questionLabel: label,
    });
    setJustAddedIds((prev) => new Set(prev).add(newH.id));
  };

  // 🔹 Insert a reusable question block ("Use from template"). Appends a heading
  // (the template's label) followed by its questions to the end of the selected
  // event. Everything is rebuilt through makeQuestion so each inserted field
  // gets a FRESH id and the canonical shape — the result is indistinguishable
  // from questions added by hand and is fully editable, movable and deletable.
  const insertTemplate = async (templateId) => {
    await insertTemplateAt(templateId, undefined);
  };

  // 🔹 Place a reusable block at a chosen spot. `targetId` decides where:
  //   • undefined -> the top of the visit (clicking the menu item; unchanged)
  //   • null      -> the very end (dropped on empty outline space)
  //   • a heading -> immediately ABOVE that section
  //   • a field   -> after the section that field belongs to
  // Dropping onto a field deliberately snaps to the end of its section rather
  // than landing mid-section: a heading inserted between two fields would
  // silently capture the ones below it into the new block.
  const insertTemplateAt = async (templateId, targetId) => {
    const tpl = SOURCE_QUESTION_TEMPLATES.find((t) => t.id === templateId);
    if (!tpl) return;
    // The single gate every insertion passes through — the menu already
    // disables a withdrawn block, but a drag that was already in flight, or
    // any future call site, must not be able to get one in behind that.
    if (tpl.unavailable) return;
    const heading = makeQuestion({ question: tpl.label, type: "Header" });
    const fields = (tpl.questions || []).map((q) => makeQuestion(q));
    const block = [heading, ...fields];

    await updateSelectedEvent((e) => {
      const qs = [...(e.questions || [])];
      if (targetId === undefined) return { ...e, questions: [...block, ...qs] };
      if (targetId === null) return { ...e, questions: [...qs, ...block] };

      const ti = qs.findIndex((q) => q.id === targetId);
      if (ti < 0) return { ...e, questions: [...qs, ...block] };

      let at = ti;
      if (qs[ti].type !== "Header") {
        // Walk to the end of this field's section.
        at = ti + 1;
        while (at < qs.length && qs[at].type !== "Header") at += 1;
      }
      return { ...e, questions: [...qs.slice(0, at), ...block, ...qs.slice(at)] };
    });

    // Same feedback as the menu insert: expanded and ringed green so it's
    // obvious what just landed and where.
    // The block lands EXPANDED even though every other section starts
    // collapsed — a freshly inserted standard block that appears as a single
    // closed title line reads as "nothing was added".
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      const qs =
        (templateRef.current?.sections || [])
          .find((s) => s.name === selectedSection)
          ?.events?.find((e) => e.eventName === selectedEvent)?.questions || [];
      const keys = sectionKeysOf(qs);
      const at = qs.findIndex((q) => q.id === heading.id);
      const key = keys.get(at);
      if (key) next.delete(key);
      return next;
    });
    setJustAddedIds((prev) => new Set(prev).add(heading.id));
    requestAnimationFrame(() => {
      const el = document.getElementById(anchorId(heading.id));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  // 🔹 Use a reusable block AS the question currently open in the builder —
  // the third way in, alongside the menu (top of the visit) and a drop on the
  // outline (a chosen spot). Dropped on the builder's answer zone, the block's
  // questions REPLACE the one being built, in place.
  //
  // No heading, deliberately. The other two entry points insert one because
  // they are placing a whole block into a document and it needs a title; here
  // the coordinator has already decided where this question goes — very often
  // under a heading they just made — and a second heading landing inside it is
  // the thing they would have to delete straight afterwards.
  //
  // Everything else matches insertTemplateAt: fresh ids through makeQuestion,
  // fully editable afterwards, ringed green so what landed is obvious.
  const applyTemplateToQuestion = async (questionId, templateId) => {
    const tpl = SOURCE_QUESTION_TEMPLATES.find((t) => t.id === templateId);
    // The same single gate insertTemplateAt uses — the menu disables a
    // withdrawn block, but a drag already in flight must not get one past it.
    if (!tpl || tpl.unavailable) return;
    const qs = selectedEventObj?.questions || [];
    const target = qs.find((q) => q.id === questionId);
    if (!target) return;

    // A blank question that was just added is what this is FOR, and replacing
    // it needs no ceremony. A question someone has already written is a
    // different matter: dropping a block on it throws that wording away.
    const isBlank =
      pendingNewId.current === questionId &&
      !String(target.question || "").trim();
    if (!isBlank) {
      const ok = await askConfirm(
        `“${target.question || "This question"}” is replaced by the ${
          tpl.questions.length
        } question${tpl.questions.length === 1 ? "" : "s"} in “${
          tpl.label
        }”. Its current wording and answer are not kept.`,
        { title: "Use the standard block here?", confirmLabel: "Replace" }
      );
      if (!ok) return;
    }

    const fields = (tpl.questions || []).map((seed) =>
      makeQuestion({
        ...seed,
        // Whatever revealed the question being replaced still has to reveal
        // what replaces it, or a conditional field silently becomes an
        // unconditional one.
        ...(target.visibleWhen ? { visibleWhen: target.visibleWhen } : {}),
      })
    );
    if (!fields.length) return;

    await updateSelectedEvent((e) => {
      const list = [...(e.questions || [])];
      const at = list.findIndex((q) => q.id === questionId);
      if (at < 0) return e;
      list.splice(at, 1, ...fields);
      // Anything that branched on the replaced question now branches on the
      // first one in, same as splitting a table. Left alone the reference
      // would dangle and that branch would silently never fire.
      return {
        ...e,
        questions: repointBranchRefs(list, questionId, fields[0].id),
      };
    });

    // The builder was open on a question that no longer exists. Closing it
    // through the ref rather than cancelEdit(), because cancelEdit deletes a
    // pending new question — which is now one of the inserted ones.
    pendingNewId.current = null;
    setEditingIndex(null);
    setJustAddedIds((prev) => {
      const next = new Set(prev);
      fields.forEach((f) => next.add(f.id));
      return next;
    });
    audit(
      TEMPLATE_AUDIT_ACTIONS.QUESTION_ADDED,
      `Used the standard block “${tpl.label}” — ${fields.length} question${
        fields.length === 1 ? "" : "s"
      }`,
      { questionId: fields[0].id, questionLabel: tpl.label }
    );
  };

  // 🔹 Move a question (or heading) up/down. This is how a coordinator groups
  // questions under a heading without drag-and-drop: reorder until they sit
  // beneath the heading they belong to. Reorders by id, never by a stale index.
  const moveQuestion = async (id, dir) => {
    const before = auditLabelFor(
      (selectedEventObj?.questions || []).find((q) => q.id === id)
    );
    let moved = false;
    await updateSelectedEvent((e) => {
      const qs = [...(e.questions || [])];
      const i = qs.findIndex((q) => q.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= qs.length) return e;
      [qs[i], qs[j]] = [qs[j], qs[i]];
      moved = true;
      return { ...e, questions: qs };
    });
    // Only when it actually moved — clicking "up" on the first question is a
    // no-op, and a trail full of no-ops is a trail nobody reads.
    if (moved) {
      audit(
        TEMPLATE_AUDIT_ACTIONS.QUESTION_MOVED,
        `Moved ${before} ${dir < 0 ? "up" : "down"}`,
        { questionId: id, questionLabel: before }
      );
    }
  };

  // 🔹 Move an entire HEADING BLOCK — the heading and every field under it, up
  // to the next heading — as one unit, swapping with the adjacent block. This is
  // what a coordinator means by "move this section up": the whole group of
  // questions travels with its heading, not just the title line.
  const moveHeadingBlock = (headingIndex, dir) => {
    updateSelectedEvent((e) => {
      const qs = [...(e.questions || [])];
      // Index where each heading starts a block.
      const starts = qs.reduce((acc, q, i) => {
        if (q.type === "Header") acc.push(i);
        return acc;
      }, []);
      const hi = Number(headingIndex);
      const bi = starts.indexOf(hi);
      const targetBi = bi + dir;
      if (bi < 0 || targetBi < 0 || targetBi >= starts.length) return e; // at an edge

      // [start, end) of the block at a given position in `starts`.
      const range = (idx) => [
        starts[idx],
        idx + 1 < starts.length ? starts[idx + 1] : qs.length,
      ];
      const lo = Math.min(bi, targetBi);
      const hiB = Math.max(bi, targetBi);
      const [aStart, aEnd] = range(lo); // earlier block
      const [bStart, bEnd] = range(hiB); // later (adjacent) block — aEnd === bStart
      const reordered = [
        ...qs.slice(0, aStart),
        ...qs.slice(bStart, bEnd), // later block first...
        ...qs.slice(aStart, aEnd), // ...then earlier block
        ...qs.slice(bEnd),
      ];
      return { ...e, questions: reordered };
    });
  };

  // 🔹 Merge a heading's whole block into ANOTHER heading — for a document
  // that ended up with too many small sections. Every field under
  // `sourceHeadingId` moves to the END of `targetHeadingId`'s block, and the
  // now-redundant source heading is removed (its fields already carry the
  // grouping that mattered; the title line doesn't need to survive too).
  const mergeHeadingInto = async (sourceHeadingId, targetHeadingId) => {
    if (sourceHeadingId === targetHeadingId) return;
    await updateSelectedEvent((e) => {
      const qs = [...(e.questions || [])];
      const si = qs.findIndex((q) => q.id === sourceHeadingId && q.type === "Header");
      if (si < 0) return e;
      let send = si + 1;
      while (send < qs.length && qs[send].type !== "Header") send += 1;
      const movingFields = qs.slice(si + 1, send); // the source heading's own fields, not the heading itself
      const rest = [...qs.slice(0, si), ...qs.slice(send)];
      const ti = rest.findIndex((q) => q.id === targetHeadingId && q.type === "Header");
      if (ti < 0) return e; // target no longer exists — abort rather than silently drop the fields
      let tend = ti + 1;
      while (tend < rest.length && rest[tend].type !== "Header") tend += 1;
      return {
        ...e,
        questions: [...rest.slice(0, tend), ...movingFields, ...rest.slice(tend)],
      };
    });
    if (editingIndex === sourceHeadingId) setEditingIndex(null);
  };

  // 🔹 Drag-and-drop reorder from the outline. Moves the dragged item to sit at
  // the drop target, in the SAME event.questions array the document renders
  // from — so the main area reflects it immediately. Semantics:
  //   • drag a HEADING → its whole block (heading + owned fields) moves as one,
  //     inserted before the target's block (or before a target field).
  //   • drag a FIELD → that field moves; dropped on a field it lands just before
  //     it, dropped on a heading it lands at the top of that section.
  // Uses the same immutable rebuild + debounced save as every other edit.
  //   • drop with NO target (the outline's tail zone) -> move to the very end.
  const reorderOutline = (draggedId, targetId) => {
    if (!draggedId || draggedId === targetId) return;
    updateSelectedEvent((e) => {
      const qs = [...(e.questions || [])];
      const di = qs.findIndex((q) => q.id === draggedId);
      if (di < 0) return e;

      // The span being moved: a heading carries its whole block; a field is one.
      let spanEnd = di + 1;
      if (qs[di].type === "Header") {
        while (spanEnd < qs.length && qs[spanEnd].type !== "Header") spanEnd += 1;
      }
      const moving = qs.slice(di, spanEnd);
      const rest = [...qs.slice(0, di), ...qs.slice(spanEnd)];

      // Target may have been inside the moved block (e.g. a heading's own field)
      // — then it's gone from `rest`; fall back to appending.
      const ti = rest.findIndex((q) => q.id === targetId);
      if (ti < 0) return { ...e, questions: [...rest, ...moving] };

      // A field dropped on a heading goes to the START of that section (just
      // after the heading). Everything else inserts BEFORE the target.
      const draggedIsField = qs[di].type !== "Header";
      const targetIsHeader = rest[ti].type === "Header";
      const insertAt = draggedIsField && targetIsHeader ? ti + 1 : ti;

      const next = [
        ...rest.slice(0, insertAt),
        ...moving,
        ...rest.slice(insertAt),
      ];
      return { ...e, questions: next };
    });
  };

  // 🔹 Add a question INSIDE a specific heading's group — no scrolling to a global
  // "Add" button. Inserts at the end of that section (just before the next
  // heading) and immediately opens its builder.
  const addFieldToSection = async (headingIndex) => {
    const newQ = makeQuestion({ question: "", type: "Text" });
    await updateSelectedEvent((e) => {
      const qs = [...(e.questions || [])];
      const hi = Number(headingIndex);
      if (hi < 0) return { ...e, questions: [...qs, newQ] };
      let j = hi + 1;
      while (j < qs.length && qs[j].type !== "Header") j += 1; // end of this group
      qs.splice(j, 0, newQ);
      return { ...e, questions: qs };
    });
    pendingNewId.current = newQ.id;
    setEditingIndex(newQ.id);
  };

  // 🔹 Insert a field directly BELOW a specific question, for precise placement
  // mid-section. Opens its builder immediately.
  const addFieldAfter = async (afterId) => {
    const newQ = makeQuestion({ question: "", type: "Text" });
    await updateSelectedEvent((e) => {
      const qs = [...(e.questions || [])];
      const i = qs.findIndex((q) => q.id === afterId);
      qs.splice(i < 0 ? qs.length : i + 1, 0, newQ);
      return { ...e, questions: qs };
    });
    pendingNewId.current = newQ.id;
    setEditingIndex(newQ.id);
  };

  const handleNotesChange = async (value) => {
    await updateSelectedEvent((e) => ({ ...e, notes: value }));
  };

  // 🔹 Open a question's builder. Keyed by question id, not array index — ids
  // are what patient answers are addressed by, so an edit must never depend
  // on position.
  const startEdit = (q) => {
    // Editing an existing field — it isn't a pending insertion, so cancel must
    // leave it untouched.
    pendingNewId.current = null;
    setEditingIndex(q.id);
  };
  const cancelEdit = () => {
    // Cancelling a just-inserted field discards it — never leave a blank field
    // behind as a structural problem the user didn't intend to create.
    const discardId = pendingNewId.current;
    pendingNewId.current = null;
    setEditingIndex(null);
    if (discardId) {
      updateSelectedEvent((e) => ({
        ...e,
        questions: (e.questions || []).filter((q) => q.id !== discardId),
      }));
    }
  };

  // 🔹 Persist what QuestionBuilder built for one question. Content only —
  // visibleWhen/appliesTo/sourceRef/aiSuggested are carried over untouched;
  // branching is edited separately (see the indent-arrow / conditional UI).
  const saveQuestionBuilder = async (questionId, built) => {
    const nextOptions = (Array.isArray(built.options) ? built.options : [])
      .map((o) => String(o).trim())
      .filter(Boolean);

    // Renaming a choice must carry every rule that depends on it along, or the
    // rule keeps matching a label that no longer exists and the dependent field
    // silently stops appearing. Renames are matched by position — the option
    // editor edits in place, so slot N before and after is the same choice.
    const priorQ = (selectedEventObj?.questions || []).find(
      (q) => q.id === questionId
    );
    const priorOptions = Array.isArray(priorQ?.options) ? priorQ.options : [];
    const renames = new Map();
    if (built.type === "Select" || built.type === "MultiSelect") {
      nextOptions.forEach((label, i) => {
        const before = priorOptions[i];
        if (before !== undefined && String(before) !== label) {
          renames.set(String(before), label);
        }
      });
    }

    await updateSelectedEvent((e) => ({
      ...e,
      questions: (e.questions || []).map((q) => {
        if (q.id !== questionId) {
          // Every OTHER field: follow the renames, leave the rest untouched.
          if (!renames.size || !q.visibleWhen) return q;
          const remapped = remapConditionValues(q.visibleWhen, questionId, renames);
          return remapped === q.visibleWhen ? q : { ...q, visibleWhen: remapped };
        }
        // Rebuild through the factory so per-type config (options, columns,
        // unit toggle, prefillRows) is created and pruned consistently, while
        // keeping the id and provenance fields the factory doesn't know about.
        //
        // applyQuestionEdit, not a plain `{...q, ...makeQuestion(…)}`: the
        // factory says "this question has no starting value" by OMITTING the
        // key, and a spread reads an absent key as "leave the old one alone".
        // That is what made a cleared value reappear on save.
        return applyQuestionEdit(
          q,
          makeQuestion({
            id: q.id,
            ...built,
            options: nextOptions,
            visibleWhen: q.visibleWhen,
            appliesTo: q.appliesTo,
            sourceRef: q.sourceRef,
            aiSuggested: q.aiSuggested,
            // Fields the question builder doesn't edit but the factory owns —
            // pass them through or they'd be dropped as "cleared".
            signatureRole: q.signatureRole,
            attestation: q.attestation,
            instruction: q.instruction,
          })
        );
      }),
    }));

    // The audit line. `wasNew` distinguishes "this question was just created"
    // from "this one was edited" — a brand new field diffed against the blank
    // placeholder it was inserted as would otherwise report every one of its
    // fields as a change, which buries the edits that matter.
    const wasNew = pendingNewId.current === questionId;
    const after = makeQuestion({ id: questionId, ...built, options: nextOptions });
    const label = auditLabelFor(after);
    if (wasNew) {
      audit(TEMPLATE_AUDIT_ACTIONS.QUESTION_ADDED, `Added ${label}`, {
        questionId,
        questionLabel: label,
        metadata: { type: after.type },
      });
    } else {
      const changes = buildQuestionDiff(priorQ || {}, after);
      // A save that changed nothing is not a change. Logging it would fill the
      // trail with entries a reviewer has to read to discover say nothing.
      if (changes.length) {
        audit(TEMPLATE_AUDIT_ACTIONS.QUESTION_EDITED, `Edited ${label}`, {
          questionId,
          questionLabel: label,
          changes,
        });
      }
    }

    // Saved — it's now a real field, no longer a pending insertion.
    pendingNewId.current = null;
    setEditingIndex(null);
  };

  // 🔹 One audit line, with the visit and the actor already filled in.
  //
  // Deliberately NOT awaited by its callers: the write is fail-soft inside the
  // service, and an audit round-trip must never sit between a coordinator's
  // click and their edit appearing. A lost line is bad; an editor that stalls
  // because the audit collection was slow is worse.
  const audit = (action, title, extra = {}) =>
    logTemplateAuditEvent({
      templateId: studyId,
      action,
      title,
      eventId: selectedEventObj?.id || "",
      eventName: selectedEvent || "",
      actor: actorRef.current,
      ...extra,
    });

  /** "1.2 Weight" — the outline number makes an entry findable in the document
   * months later, when the wording alone no longer identifies it. */
  const auditLabelFor = (q) => {
    const n = numberByIdRef.current?.get(q?.id);
    const text = q?.question || "(untitled)";
    return n ? `${n} ${text}` : text;
  };

  // 🔹 Set (or clear) one question's conditional-visibility rule directly —
  // the indent-arrow / conditional-badge popover's persistence, applied on
  // every click rather than staged behind a separate Save button, so what a
  // click does is immediately visible in the document.
  const setQuestionVisibility = async (questionId, nextVisibleWhen) => {
    await updateSelectedEvent((e) => ({
      ...e,
      questions: (e.questions || []).map((q) => {
        if (q.id !== questionId) return q;
        const next = { ...q };
        if (nextVisibleWhen) next.visibleWhen = nextVisibleWhen;
        else delete next.visibleWhen;
        return next;
      }),
    }));
    const target = (selectedEventObj?.questions || []).find((q) => q.id === questionId);
    audit(
      TEMPLATE_AUDIT_ACTIONS.CONDITION_CHANGED,
      nextVisibleWhen
        ? `${auditLabelFor(target)} only shows under a condition`
        : `${auditLabelFor(target)} always shows`,
      {
        questionId,
        questionLabel: auditLabelFor(target),
        changes: [
          {
            field: "visibleWhen",
            label: "Shown when",
            from: target?.visibleWhen ? "a condition" : "always",
            to: nextVisibleWhen ? "a condition" : "always",
          },
        ],
      }
    );
  };

  // 🔹 Open/close the branch popover for one question, seeding (or clearing)
  // its independent draft — see the note on branchDraft's declaration for why
  // the draft can't just be re-derived from q.visibleWhen on every render.
  const toggleBranchPopover = (q) => {
    setBranchPopoverFor((cur) => {
      if (cur === q.id) {
        setBranchDraft(null);
        return null;
      }
      setBranchDraft(parseCondition(q.visibleWhen));
      return q.id;
    });
  };
  const closeBranchPopover = () => {
    setBranchPopoverFor(null);
    setBranchDraft(null);
  };

  // 🔹 Delete a question

  // Split a Table back into the separate questions it should have been.
  //
  // The eCRF reader tags a field as part of a repeating log when it looks like a
  // column of one, and assembly then collapses the whole group into ONE Table.
  // It over-fires on a question that simply has a codelist — "Was study drug
  // administered? Yes / No" followed by "If No, what was the reason?" reads, as
  // plain text, exactly like a header with rows under it. The result is a table
  // whose columns are the questions, which is not what the form asks.
  //
  // A Table is a single question and the field editor deliberately skips Tables,
  // so until now an affected source document could not be corrected at all from
  // the UI. This is that correction, and it works on documents already built.

  // Copy a heading block into OTHER visits.
  //
  // A mis-extracted section is usually mis-extracted in every visit that
  // carries it, so fixing one and repeating the work fourteen times is the job
  // software should be doing. This appends the corrected block to whichever
  // visits are chosen, with fresh ids and branches re-pointed — see
  // cloneBlockForEvent for why that matters.
  const copyBlockToEvents = async (headingIndex, targetEventNames) => {
    const qs = selectedEventObj?.questions || [];
    const block = headingBlockAt(qs, headingIndex);
    if (!block.length || !targetEventNames.length) return;

    const label = block[0].question || "this section";
    const t = templateRef.current || template;
    const section = (t.sections || []).find((s) => s.name === selectedSection);

    // Copy per target: each visit needs its OWN ids, so the clone runs once per
    // destination rather than once overall.
    let droppedAnywhere = [];
    const updated = {
      ...t,
      sections: (t.sections || []).map((s) =>
        s.name !== selectedSection
          ? s
          : {
              ...s,
              events: (s.events || []).map((e) => {
                if (!targetEventNames.includes(e.eventName)) return e;
                const { questions, droppedBranches } = cloneBlockForEvent(block);
                droppedAnywhere = [...droppedAnywhere, ...droppedBranches];
                return { ...e, questions: [...(e.questions || []), ...questions] };
              }),
            }
      ),
    };

    await saveTemplate(updated);

    audit(
      TEMPLATE_AUDIT_ACTIONS.BLOCK_COPIED,
      `Copied section “${label}” into ${targetEventNames.length} visit${
        targetEventNames.length === 1 ? "" : "s"
      }: ${targetEventNames.join(", ")}`,
      { questionLabel: label }
    );

    setCopyBlockFor(null);
    // A branch that pointed outside the block cannot survive the move, and a
    // question whose rule quietly vanished is exactly the sort of thing nobody
    // notices until a visit is missing a field. Say it out loud.
    if (droppedAnywhere.length) {
      const names = [...new Set(droppedAnywhere)];
      await showAlert(
        `Copied. ${names.length} question${names.length === 1 ? "" : "s"} had a ` +
          `show/hide rule pointing at a question outside this section, which does not ` +
          `exist in the other visits — those rules were removed, so the questions are ` +
          `always shown: ${names.join(", ")}.`,
        { title: "Copied, with rules removed" }
      );
    }
  };
  // 🔹 Copy ONE question into a heading of this visit or of another visit in
  // this part of the template ("Copy question", 2026-10-29). A fresh id; its
  // show/hide rule survives only within the same visit (cloneQuestionForEvent).
  // Through saveTemplate, so it is undoable and validated like any edit.
  const copyQuestionTo = async (questionId, { visitName, targetKey }) => {
    const source = (selectedEventObj?.questions || []).find((q) => q.id === questionId);
    if (!source || !visitName || !targetKey) return;
    const sameEvent = visitName === selectedEvent;
    const t = templateRef.current || template;
    let copied = null;
    let droppedBranch = "";
    let placeLabel = "";
    const updated = {
      ...t,
      sections: (t.sections || []).map((s) =>
        s.name !== selectedSection
          ? s
          : {
              ...s,
              events: (s.events || []).map((e) => {
                if (e.eventName !== visitName || copied) return e;
                const qs = e.questions || [];
                const sourceIndex = sameEvent ? qs.findIndex((q) => q.id === questionId) : undefined;
                const place = questionCopyTargets(qs, { sourceIndex }).find((p) => p.key === targetKey);
                if (!place) return e;
                const clone = cloneQuestionForEvent(source, { sameEvent });
                copied = clone.question;
                droppedBranch = clone.droppedBranch;
                placeLabel = place.label;
                const next = [...qs];
                next.splice(place.insertAt, 0, clone.question);
                return { ...e, questions: next };
              }),
            }
      ),
    };
    setCopyQuestionFor(null);
    if (!copied) {
      await showAlert("That place no longer exists in the visit, so nothing was copied. Try again.", {
        title: "Not copied",
      });
      return;
    }

    await saveTemplate(updated);

    const where = sameEvent ? `under “${placeLabel}” in this visit` : `into ${visitName}, under “${placeLabel}”`;
    audit(TEMPLATE_AUDIT_ACTIONS.QUESTION_COPIED, `Copied ${auditLabelFor(source)} ${where}`, {
      questionId: copied.id,
      questionLabel: auditLabelFor(source),
    });

    if (droppedBranch) {
      await showAlert(
        `Copied. “${droppedBranch}” had a show/hide rule pointing at a question in this visit, ` +
          `which doesn't exist in ${visitName}, so the copy's rule was removed and it is always shown.`,
        { title: "Copied, with its rule removed" }
      );
    }
  };
  const splitTable = async (questionId) => {
    const qs = selectedEventObj?.questions || [];
    const idx = qs.findIndex((q) => q.id === questionId);
    const table = idx >= 0 ? qs[idx] : null;
    const result = splitTableIntoQuestions(table);
    if (!result) return;

    const n = result.questions.length;
    const ok = await askConfirm(
      `“${table.question || "This table"}” becomes ${n} separate question${
        n === 1 ? "" : "s"
      }, one per column. Any rows already filled in on the table are not carried over.`,
      { title: "Split into separate questions?", confirmLabel: "Split" }
    );
    if (!ok) return;

    await updateSelectedEvent((e) => {
      const list = [...(e.questions || [])];
      const at = list.findIndex((q) => q.id === questionId);
      if (at < 0) return e;
      // In place, so the questions land where the table was rather than at the
      // end of the visit.
      list.splice(at, 1, ...result.questions);
      // Anything that branched on the TABLE now branches on the first question,
      // which is nearly always the controlling one ("Was study drug
      // administered?" precedes "If No, why not?"). Left alone the reference
      // would dangle and the branch would silently never fire.
      return { ...e, questions: repointBranchRefs(list, questionId, result.controllingId) };
    });

    audit(
      TEMPLATE_AUDIT_ACTIONS.TABLE_SPLIT,
      `Split table “${table.question || "table"}” into ${n} question${n === 1 ? "" : "s"}`,
      { questionId, questionLabel: table.question || "" }
    );
    if (editingIndex === questionId) setEditingIndex(null);
  };
  const deleteQuestion = async (questionId) => {
    const ok = await askConfirm("Delete this question?", {
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    // Captured before it goes — an audit line naming only an id is useless.
    const gone = (selectedEventObj?.questions || []).find((q) => q.id === questionId);
    await updateSelectedEvent((e) => ({
      ...e,
      questions: (e.questions || []).filter((q) => q.id !== questionId),
    }));
    audit(
      TEMPLATE_AUDIT_ACTIONS.QUESTION_DELETED,
      `Deleted ${auditLabelFor(gone)}`,
      { questionId, questionLabel: auditLabelFor(gone) }
    );
    if (editingIndex === questionId) setEditingIndex(null);
  };

  // 🔹 Delete a whole HEADING BLOCK — the heading and every field beneath it,
  // up to the next heading. Deleting a heading alone would orphan its fields
  // under whatever heading precedes it, which is never what "delete this
  // section" means. Same block boundary as moveHeadingBlock.
  // Takes the heading's POSITION, not its id.
  //
  // With findIndex-by-id, clicking delete on the SECOND of two headings that
  // share an id found the FIRST one and removed that block instead — and then
  // filtering the survivors by id removed every copy, so one click deleted both
  // sections. To the user that looked like the section being wiped out of the
  // source. Splicing a position range removes exactly the block that was
  // clicked and leaves its twin alone, which is what makes de-duplicating by
  // hand possible at all.
  const deleteHeadingBlock = async (headingIndex) => {
    const t = templateRef.current || template;
    const ev = t?.sections
      ?.find((s) => s.name === selectedSection)
      ?.events?.find((e) => e.eventName === selectedEvent);
    const qs = ev?.questions || [];
    const hi = Number(headingIndex);
    if (!Number.isInteger(hi) || hi < 0 || hi >= qs.length) return;
    if (qs[hi]?.type !== "Header") return;
    let end = hi + 1;
    while (end < qs.length && qs[end].type !== "Header") end += 1;
    const count = end - hi - 1; // fields owned by this heading
    const label = qs[hi].question || "this section";
    const ok = await askConfirm(
      count > 0
        ? `“${label}” and the ${count} field${count === 1 ? "" : "s"} under it will be removed.`
        : `“${label}” will be removed.`,
      { title: "Delete this section?", confirmLabel: "Delete", tone: "danger" }
    );
    if (!ok) return;

    const headingId = qs[hi].id;
    const removedIds = new Set(qs.slice(hi, end).map((q) => q.id));
    // Splice the RANGE. Filtering by id would also take any question elsewhere
    // in the event that happens to share an id with one in this block — which
    // is exactly the duplicate case this delete is being used to clean up.
    await updateSelectedEvent((e) => {
      const next = [...(e.questions || [])];
      next.splice(hi, end - hi);
      return { ...e, questions: next };
    });
    audit(
      TEMPLATE_AUDIT_ACTIONS.HEADING_DELETED,
      `Deleted section “${label}”${
        count ? ` and its ${count} question${count === 1 ? "" : "s"}` : ""
      }`,
      { questionId: headingId, questionLabel: label }
    );
    // Collapse state is keyed by the heading itself (see sectionKeysOf), not by
    // position, so removing a block needs no rebasing — the surviving sections
    // keep the keys they already had. The removed heading's key just stops
    // matching anything.

    // If the inline editor was open on a field we just removed, close it.
    if (removedIds.has(editingIndex)) setEditingIndex(null);
  };

  // 🔹 Rename a heading. A heading isn't an "answer field" — it has no
  // pieces/type to build — so it gets its own small rename flow rather than
  // opening QuestionBuilder (which is for answer-bearing questions).
  const renameHeading = async (q) => {
    const name = await askPrompt("Heading text:", q.question || "", {
      title: "Rename heading",
      confirmLabel: "Save",
    });
    if (!name || !name.trim()) return;
    await updateSelectedEvent((e) => ({
      ...e,
      questions: (e.questions || []).map((qq) =>
        qq.id === q.id ? { ...qq, question: name.trim() } : qq
      ),
    }));
    audit(TEMPLATE_AUDIT_ACTIONS.HEADING_RENAMED, `Renamed a heading`, {
      questionId: q.id,
      questionLabel: name.trim(),
      changes: [
        {
          field: "question",
          label: "Heading text",
          from: q.question || "—",
          to: name.trim(),
        },
      ],
    });
  };

  // 🔹 Rename the selected visit. Approvals and versions are keyed by the
  // event's ID (see eventNameById), so a rename is only a relabel — nothing
  // signed off is orphaned by it. `selectedEvent` IS the name though, so the
  // selection has to follow the rename or the editor would lose the visit it
  // is showing.
  const renameEvent = async () => {
    if (!selectedEvent) return;
    const current = selectedEvent;
    const name = await askPrompt("Visit name:", current, {
      title: "Rename visit",
      confirmLabel: "Save",
    });
    const next = (name || "").trim();
    if (!next || next === current) return;
    // Two visits sharing a name in one section would collide: updateSelectedEvent
    // matches by name, so every later edit would land on whichever came first.
    const siblings =
      (templateRef.current || template).sections?.find(
        (s) => s.name === selectedSection
      )?.events || [];
    if (siblings.some((e) => e.eventName === next)) {
      await showAlert(`“${next}” is already a visit in this section.`);
      return;
    }
    const evId = siblings.find((e) => e.eventName === current)?.id || "";
    await updateSelectedEvent((e) => ({ ...e, eventName: next }));
    setSelectedEvent(next);
    logTemplateAuditEvent({
      templateId: studyId,
      action: TEMPLATE_AUDIT_ACTIONS.VISIT_RENAMED,
      title: `Renamed visit “${current}” to “${next}”`,
      eventId: evId,
      eventName: next,
      actor: actorRef.current,
      changes: [
        { field: "eventName", label: "Visit name", from: current, to: next },
      ],
    });
  };

  // 🔹 Mark (or unmark) this visit's source as finalized ON PAPER.
  //
  // SourceX arrived mid-study, so a site can have Screening already printed,
  // filled, signed and filed, and be using SourceX from Visit 2 on. Nothing in
  // the template said so, which is how a second coordinator exports Screening
  // from here, files that too, and the study ends up with two source documents
  // for one visit.
  //
  // A marker, nothing more: it changes no content, blocks no export, and has
  // nothing to do with approval — it records that somebody knows where the real
  // copy is, and who to ask for it. Deliberately reversible: it can be ticked
  // by mistake, and being unable to correct that would be worse than the
  // mistake. Both directions are audited.
  const togglePaperFinal = async () => {
    const ev = selectedEventObj;
    if (!ev) return;
    const turningOn = !eventIsPaperFinal(ev);
    if (turningOn) {
      const ok = await askConfirm(
        `Mark “${selectedEvent}” as finalized on paper?\n\nAnyone exporting it from RegX will be told to check with you for the final copy. The document itself is unchanged, and it stays exportable.`,
        { title: "Finalized on paper?", confirmLabel: "Mark as paper final" }
      );
      if (!ok) return;
    }
    const actor = await resolveCurrentActor();
    actorRef.current = actor;
    await updateSelectedEvent((e) => {
      // Cleared by REMOVING the fields, not by writing `paperFinal: false` —
      // makeEvent only carries them when the flag is on, so a false would be
      // dropped on the next save anyway and leave a stale marker behind it.
      const { paperFinal, paperFinalAt, paperFinalBy, ...rest } = e;
      return turningOn
        ? {
            ...rest,
            paperFinal: true,
            paperFinalAt: new Date().toISOString(),
            paperFinalBy: actor,
          }
        : rest;
    });
    audit(
      turningOn
        ? TEMPLATE_AUDIT_ACTIONS.PAPER_FINAL_MARKED
        : TEMPLATE_AUDIT_ACTIONS.PAPER_FINAL_CLEARED,
      turningOn
        ? `Marked “${selectedEvent}” as finalized on paper`
        : `Removed the paper-final mark from “${selectedEvent}”`,
      {
        changes: [
          {
            field: "paperFinal",
            label: "Finalized on paper",
            from: turningOn ? "no" : "yes",
            to: turningOn ? "yes" : "no",
          },
        ],
      }
    );
  };

  // 🔹 Delete an event (and its questions)
  const deleteEvent = async (eventName) => {
    const ok = await askConfirm(
      `“${eventName}” and all of its questions will be removed.`,
      { title: "Delete this event?", confirmLabel: "Delete", tone: "danger" }
    );
    if (!ok) return;
    const doomed = (template.sections || [])
      .find((s) => s.name === selectedSection)
      ?.events?.find((e) => e.eventName === eventName);
    const updated = {
      ...template,
      sections: (template.sections || []).map((s) =>
        s.name !== selectedSection
          ? s
          : { ...s, events: (s.events || []).filter((e) => e.eventName !== eventName) }
      ),
    };
    await saveTemplate(updated);
    logTemplateAuditEvent({
      templateId: studyId,
      action: TEMPLATE_AUDIT_ACTIONS.VISIT_DELETED,
      title: `Deleted visit “${eventName}” and its ${
        doomed?.questions?.length || 0
      } question${(doomed?.questions?.length || 0) === 1 ? "" : "s"}`,
      eventId: doomed?.id || "",
      eventName,
      actor: actorRef.current,
    });
    if (selectedEvent === eventName) setSelectedEvent(null);
  };

  // 🔹 Delete the selected section (and everything in it)
  const deleteSection = async () => {
    if (!selectedSection) return;
    const ok = await askConfirm(
      `“${selectedSection}” and everything in it will be removed.`,
      { title: "Delete this section?", confirmLabel: "Delete", tone: "danger" }
    );
    if (!ok) return;
    const updated = {
      ...template,
      sections: (template.sections || []).filter((s) => s.name !== selectedSection),
    };
    await saveTemplate(updated);
    setSelectedSection("");
    setSelectedEvent(null);
  };

  if (!template)
    return (
      <div className="sourcex-page relative min-h-screen w-full text-slate-300 font-[Manrope] flex items-center justify-center">
        <PageBackground />
        Loading…
      </div>
    );

  const selectedSectionObj = template.sections?.find((s) => s.name === selectedSection);
  const selectedEventObj = selectedSectionObj?.events?.find(
    (e) => e.eventName === selectedEvent
  );

  // A LOG section holds exactly one thing — the log itself — and its name is
  // the section name. The eligibility CHECKLIST is the same shape. So the visit
  // row shows nothing for either: no picker (there is nothing to pick), no name
  // (the section picker already says it), and no add / reorder / delete. Every
  // ordinary section keeps the full picker.
  const sectionIsLog =
    selectedSectionObj?.kind === "log" ||
    selectedSectionObj?.kind === "criteria";

  // Question id -> label, so a conditional field can name what controls it —
  // the same map the export builds for its "only if …" notes.
  const labelById = new Map(
    (selectedEventObj?.questions || []).map((q) => [q.id, q.question])
  );

  // Fields a branch can be based on, and the reverse index of what each field
  // reveals — so a dependency is visible from BOTH ends: on the conditional
  // field ("shown only when…") and on the field that controls it ("reveals…").
  const branchCandidates = (selectedEventObj?.questions || []).filter(canControlBranch);
  const dependentsMap = buildDependentsMap(selectedEventObj);
  // Indent level per field, so branching reads as nesting in the document.
  const branchDepth = buildBranchDepth(selectedEventObj?.questions || []);

  // Outline numbers — see buildOutlineNumbers. Editor-side only: they are
  // never written into a question's text or into an export.
  const numberById = buildOutlineNumbers(
    selectedEventObj?.questions || [],
    branchDepth
  );
  numberByIdRef.current = numberById;

  // Sections that RegX already has a standard block for, but which were built
  // by hand (or drafted by the AI) instead. Advice only — it highlights and
  // says where the standard lives; nothing here blocks an edit, an approval or
  // an export. See services/standardBlockAdvice.js, which also explains how a
  // section that already IS the standard block is recognised and left alone.
  //
  // Recomputed per render like every other derived value in this block — the
  // early return above rules out a hook here, and the keyword patterns are
  // compiled once in the module, so this is one pass over the visit's
  // questions against a prebuilt list.
  const standardAdvice = buildStandardAdvice(selectedEventObj?.questions || []);

  // Notices a coordinator has deliberately silenced. A map on the TEMPLATE doc
  // rather than a field on the question: a question is the regulated record —
  // it is what versions, redlines and "has anything changed since approval?"
  // are computed from — and a UI preference has no business showing up in a
  // redline or re-enabling the Approve button. Top-level, so `sameVisitContent`
  // (which reads `sections`) never sees it.
  const dismissedAdvice = template.standardAdviceDismissed || {};
  const adviceDismissed = (a) => isStandardAdviceDismissed(dismissedAdvice, a);
  // What the OUTLINE gets: only notices still showing. It carries no dismiss
  // control of its own — the document is where the decision is taken — so a
  // dismissed section simply reads as an ordinary one there.
  const outlineAdvice = new Map(
    [...standardAdvice].filter(([, a]) => !adviceDismissed(a))
  );

  /**
   * Hide or bring back one section's standard-block notice.
   *
   * Goes through saveTemplate like every other edit, so it is undoable, and is
   * written to the audit trail: "this study keeps its own version of a section
   * the site standardizes" is a decision, and the trail is where a decision
   * belongs. Nothing about the section's own content is touched.
   */
  const setAdviceDismissed = (advice, dismissed) => {
    const key = standardAdviceKey(advice);
    if (!key) return;
    // From the REF, not from the render's `dismissedAdvice`: a previous
    // dismissal's write may still be in flight, and rebuilding the map from a
    // render-old copy would drop it. Same reason every other mutation here
    // reads templateRef.
    const latest = templateRef.current || template;
    const next = { ...(latest.standardAdviceDismissed || {}) };
    if (dismissed) next[key] = true;
    else delete next[key];
    saveTemplate({ ...latest, standardAdviceDismissed: next });
    audit(
      TEMPLATE_AUDIT_ACTIONS.STANDARD_BLOCK_NOTICE,
      dismissed
        ? `Kept this study's own “${advice.name}” instead of the standard block`
        : `Restored the standard-block notice for “${advice.name}”`,
      {
        questionId: advice.headingId,
        metadata: { topicId: advice.topicId, dismissed },
      }
    );
  };

  // Approval is per visit, so every control that talks about it names the
  // visit. Without that, "Approve" on a page showing one visit while four
  // exist reads as approving all four — which is what it used to do.
  const currentVisitApproved = eventIsApproved(selectedEventObj);
  const approveLabel = !selectedEventObj
    ? "Pick a visit to approve"
    : `${currentVisitApproved ? "Re-approve" : "Approve"} ${selectedEvent}`;

  // Nothing to approve when the visit is already exactly what was approved.
  //
  // A re-approval that changes nothing still writes a version, moves the visit
  // to "Version 3.0" and stamps a fresh sign-off date on a document nobody
  // edited — three false claims in the part of the record a monitor reads
  // first. Compared by CONTENT, so an edit that is undone leaves nothing to
  // approve: the record has to agree with what is on screen, not with how many
  // times someone typed.
  //
  // Gated on the visit currently being approved. A draft that happens to match
  // an old approved version still needs its sign-off — blocking that would
  // strand it as a draft with no way back.
  const latestApprovedForVisit = latestApprovedVersionFor(
    templateVersions,
    selectedEventObj?.id || ""
  );
  // `currentVisitApproved &&` first, so the comparison only runs for a visit
  // that could actually be re-approved.
  const unchangedSinceApproval =
    currentVisitApproved &&
    sameVisitContent(versionEvent(latestApprovedForVisit), selectedEventObj);
  const approvedVersionName = versionLabel(
    approvalNumberOf(templateVersions, latestApprovedForVisit)
  );
  const approveDisabled = approving || !selectedEventObj || unchangedSinceApproval;
  // Exactly ONE `disabled:cursor-*` utility, chosen here rather than stacked on
  // the button. The buttons used to carry `disabled:cursor-wait` in their base
  // class AND `disabled:cursor-not-allowed` in the "nothing to approve" suffix;
  // those have equal specificity, so which one applies is decided by their order
  // in Tailwind's generated CSS, not by the order they appear in the string.
  // `cursor-wait` won, and a greyed-out button that can't be clicked until you
  // edit something sat under a spinning "please wait" cursor, which reads as
  // "this is working on it" — the opposite of what is true.
  //
  // Waiting is the ONLY state a wait cursor is honest about, so it is the only
  // state that gets one. Every other disabled reason (nothing to approve, no
  // visit selected) is "not allowed", and shows the 🚫 crossed circle.
  const approveCursorCls = approving
    ? "disabled:cursor-wait"
    : "disabled:cursor-not-allowed";
  const approveTitle = unchangedSinceApproval
    ? `${selectedEvent} is identical to ${
        approvedVersionName || "the approved version"
      } — there is nothing to approve. Make a change first.`
    : undefined;

  // The same labels with their outline number in front, for the branching
  // notes: "only shows when 4.4 Were any medications taken? is Yes" points at
  // one specific question on the page, where the text alone left the reader to
  // go and find which of several similarly-worded questions was meant.
  //
  // A SEPARATE map from labelById on purpose. The plain one is what the printed
  // preview uses, and an editor-only number must never appear on a form.
  const numberedLabelById = new Map(
    [...labelById].map(([id, textLabel]) => {
      const n = numberById.get(id);
      return [id, n ? `${n} ${textLabel || ""}`.trim() : textLabel];
    })
  );

  // Structural problems keyed by question id (validated across the whole
  // template so duplicate ids are caught), so the editor can flag the exact
  // field and its heading in red.
  const problemById = collectQuestionProblems(template);

  // Fields hidden because the heading above them is collapsed, how many each
  // heading owns, and whether any field under a heading has a problem (so the
  // heading itself can be flagged even while collapsed).
  const hiddenQuestionIndexes = new Set();
  const sectionFieldCount = new Map();
  const sectionHasProblem = new Map();
  // Which heading owns each field. Also what scopes the branch picker: a rule
  // is nearly always about a question in the same block, and offering every
  // field in the visit made a long form's dropdown unusable.
  const headingOwnerByQ = new Map();
  // Sections are ADDRESSED by their heading's position (every handler here
  // takes an index) but their collapse state is STORED under a stable key —
  // see sectionKeysOf for why neither position nor id works on its own.
  const sectionKeyByIndex = sectionKeysOf(selectedEventObj?.questions);
  {
    let owner = null;
    (selectedEventObj?.questions || []).forEach((q, qi) => {
      if (q.type === "Header") {
        owner = qi;
        sectionFieldCount.set(owner, 0);
        sectionHasProblem.set(owner, problemById.has(q.id));
      } else {
        // Fields before the first heading share one implicit group (null).
        headingOwnerByQ.set(q.id, owner);
        if (owner !== null) {
          sectionFieldCount.set(owner, (sectionFieldCount.get(owner) || 0) + 1);
          if (collapsedSections.has(sectionKeyByIndex.get(owner)))
            hiddenQuestionIndexes.add(qi);
          if (problemById.has(q.id)) sectionHasProblem.set(owner, true);
        }
      }
    });
  }

  // Which heading owns the question currently open in the editor, so that
  // section is marked too. An open editor deep in a forty-field form says
  // nothing about WHERE you are; marking the heading answers that without
  // scrolling. null when nothing is being edited, or when the question sits
  // above the first heading (which has no heading to mark).
  const editingHeadingIndex =
    editingIndex && headingOwnerByQ.has(editingIndex)
      ? headingOwnerByQ.get(editingIndex)
      : null;

  // Heading text a field sits under, used to label the picker's groups when a
  // user opens it up to the whole visit.
  const headingLabelById = new Map(
    (selectedEventObj?.questions || [])
      .filter((q) => q.type === "Header")
      .map((h) => [h.id, h.question || "(untitled section)"])
  );
  const sectionLabelOf = (id) => {
    const owner = headingOwnerByQ.get(id);
    return owner
      ? headingLabelById.get(owner) || "(untitled section)"
      : "Before the first heading";
  };

  // Fields offered in the branch picker for `target`: the ones under the SAME
  // heading. A rule that reaches across sections is rare, and listing every
  // field in the visit turned the dropdown into a wall on a long form.
  //
  // Any question an EXISTING rule already points at is kept in the list even
  // when it lives elsewhere — otherwise that rule would render against an
  // empty dropdown and look broken.
  const branchCandidatesFor = (target) => {
    const owner = headingOwnerByQ.get(target.id);
    const referenced = new Set(
      conditionLeaves(target.visibleWhen).map((leaf) => leaf.questionId)
    );
    return branchCandidates.filter(
      (c) =>
        c.id !== target.id &&
        (headingOwnerByQ.get(c.id) === owner || referenced.has(c.id))
    );
  };

  // Which question a new rule starts on. Walking back to the nearest preceding
  // field picked whatever happened to sit directly above — often another
  // conditional free-text field, which has no answers to branch on and can
  // never satisfy a rule. So a field with actual answer CHOICES wins, and the
  // plain nearest-preceding field is only the fallback.
  const seedBranchQuestion = (target) => {
    const all = selectedEventObj?.questions || [];
    const idx = all.findIndex((x) => x.id === target.id);
    const scoped = new Set(branchCandidatesFor(target).map((c) => c.id));
    const preceding = all
      .slice(0, Math.max(0, idx))
      .filter((x) => scoped.has(x.id));
    const withChoices = [...preceding]
      .reverse()
      .find((x) => choicesForQuestion(x).length > 0);
    return (withChoices || preceding[preceding.length - 1])?.id || "";
  };

  return (
    <div className="sourcex-page relative min-h-screen w-full text-slate-100 font-[Manrope] px-4 sm:px-6 py-6">
      <PageBackground />
      {/* Centered alert / confirm / prompt for this page. */}
      {dialog}
      {/* A newer protocol version than these documents: asks for edits, not a rebuild. */}
      {tourOpen && <SourceXTour steps={TOUR_STEPS} onPrepare={prepareTourStep} onClose={endTour} />}
      <SourceDocsVersionNotice
        // Waits while the tour is asked or running, then shows as before.
        paused={tourOpen || tourAsking}
        studyKey={exportStudyIdFor(template, studyId)}
        template={template}
        visit={selectedEvent}
        visits={(template.sections || []).flatMap((sec) => (sec.events || []).map((e) => e.eventName))}
      />
      {/* Reading a protocol for its eligibility criteria is a slow server call.
          The same full-screen wait ProtocolsPage and EcrfPage use, for the same
          reason: a spinner on a toolbar button leaves a coordinator staring at
          a page that looks unchanged, wondering whether their click landed.
          `keepTabOpen` is deliberately FALSE — nothing about this run lives in
          the tab except the wait, and the section is only written once the
          criteria are back, so closing the tab loses the run but corrupts
          nothing. Warning where it isn't true is what teaches people to ignore
          the warning where it is. */}
      <StepProgressOverlay
        open={eligibilityBusy}
        title="Reading the protocol"
        detail={eligibilityStep}
        typical="usually under a minute"
      />
      {/* Copy this section into other visits. A section the eCRF read wrongly
          is wrong in every visit that carries it, so the useful unit of work is
          "all the visits that have this", not one at a time. */}
      {copyBlockFor !== null && (() => {
        const block = headingBlockAt(selectedEventObj?.questions || [], copyBlockFor);
        const label = block[0]?.question || "this section";
        const fields = Math.max(block.length - 1, 0);
        const others = (
          (template?.sections || []).find((s) => s.name === selectedSection)?.events || []
        )
          .map((e) => e.eventName)
          .filter((n) => n && n !== selectedEvent);
        const already = new Set(
          (
            (template?.sections || []).find((s) => s.name === selectedSection)?.events || []
          )
            .filter((e) =>
              (e.questions || []).some(
                (q) => q.type === "Header" && q.question === block[0]?.question
              )
            )
            .map((e) => e.eventName)
        );
        return (
          <div
            className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setCopyBlockFor(null);
            }}
          >
            <div className="w-full max-w-md rounded-2xl border border-white/12 bg-[#0c0826] shadow-[0_24px_70px_-20px_rgba(0,0,0,0.9)] overflow-hidden">
              <div className="px-5 pt-4">
                <h3 className="text-sm font-semibold text-white">Copy “{label}”</h3>
                <p className="mt-1 text-[12px] text-slate-400">
                  {fields} field{fields === 1 ? "" : "s"} will be added to the end of each
                  visit you pick.
                </p>
              </div>
              <div className="mt-3 max-h-[46vh] overflow-auto px-5">
                {others.length === 0 ? (
                  <p className="py-6 text-center text-[12px] text-slate-400">
                    There are no other visits in this section.
                  </p>
                ) : (
                  <ul className="space-y-1 pb-1">
                    {others.map((name) => (
                      <li key={name}>
                        <label className="flex items-start gap-2 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-white/[0.05]">
                          <input
                            type="checkbox"
                            checked={copyTargets.includes(name)}
                            onChange={(e) =>
                              setCopyTargets((prev) =>
                                e.target.checked
                                  ? [...prev, name]
                                  : prev.filter((x) => x !== name)
                              )
                            }
                            className="mt-0.5 accent-sky-400"
                          />
                          <span className="min-w-0">
                            <span className="block text-[12px] text-slate-200">{name}</span>
                            {already.has(name) && (
                              /* Not blocked — a visit can legitimately carry two
                                 sections with one name — but saying so stops a
                                 second copy being made by accident. */
                              <span className="block text-[10px] text-amber-300/90">
                                already has a section with this name
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 px-5 py-3 mt-2 border-t border-white/10 bg-white/[0.02]">
                <button
                  onClick={() =>
                    setCopyTargets(copyTargets.length === others.length ? [] : others)
                  }
                  disabled={!others.length}
                  className="text-[11px] text-slate-400 hover:text-white transition disabled:opacity-40"
                >
                  {copyTargets.length === others.length && others.length
                    ? "Clear all"
                    : "Select all"}
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCopyBlockFor(null)}
                    className="px-3 py-2 rounded-xl text-sm text-slate-300 bg-white/[0.05] border border-white/10 hover:bg-white/[0.12] transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => copyBlockToEvents(copyBlockFor, copyTargets)}
                    disabled={!copyTargets.length}
                    className="px-3 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-sky-500 to-blue-500 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Copy to {copyTargets.length || ""} visit
                    {copyTargets.length === 1 ? "" : "s"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {copyQuestionFor !== null &&
        (() => {
          const qs = selectedEventObj?.questions || [];
          const sourceIndex = qs.findIndex((q) => q.id === copyQuestionFor);
          if (sourceIndex < 0) return null;
          return (
            <CopyQuestionModal
              question={qs[sourceIndex]}
              sourceIndex={sourceIndex}
              currentVisit={selectedEvent}
              visits={(template?.sections || []).find((s) => s.name === selectedSection)?.events || []}
              onCancel={() => setCopyQuestionFor(null)}
              onCopy={(target) => copyQuestionTo(copyQuestionFor, target)}
            />
          );
        })()}
      <div className="max-w-[1800px] mx-auto flex flex-col gap-4">
        {/* Header */}
        <div className="flex justify-between items-center rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            {/* Back sits top-left on every SourceX page. */}
            <button
              onClick={() => navigate(-1)}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-300 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <FileText className="w-5 h-5 text-purple-300 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold leading-none">
                {studyId.endsWith("__assembled") ? "Built Source Documents" : "Source Template"}
              </div>
              <h2 className="text-base font-semibold text-white truncate">
                {studyId.replace(/__assembled$/, "")}
              </h2>
            </div>
          </div>
          {PROTOCOL_COMPARE_ENABLED && studyId.endsWith("__assembled") && (
            <AiHelperNotice compact>These documents were built from the protocol and eCRF as read by AI. Check each visit before use.</AiHelperNotice>
          )}
          <div className="flex items-center gap-2">
            {/* The document as it prints. This is where the per-question grey
                preview boxes went: one page you can read, instead of a preview
                under every field that tripled the scroll and never showed the
                page. Purple so it reads as the primary "check my work" action
                next to the two quieter utilities. */}
            {/* Guided tour (built documents only, flag SOURCEX_TOUR_ENABLED):
                explains each feature; the tour's last step points back here. */}
            {tourAvailable && (
              <button
                type="button"
                onClick={startTour}
                data-tour="tour-button"
                title="A quick guided tour of this page"
                className="sx-tour-trigger inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-sky-500/15 border border-sky-300/30 hover:bg-sky-500/25 text-xs text-sky-100 transition"
              >
                <Compass className="w-3.5 h-3.5" /> Tour
              </button>
            )}
            <button
              onClick={() => setPreviewing(true)}
              data-tour="preview"
              disabled={!selectedEventObj}
              title={
                selectedEventObj
                  ? "See this visit exactly as it exports"
                  : "Pick a visit first"
              }
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-500/15 border border-purple-300/30 hover:bg-purple-500/25 disabled:opacity-40 disabled:hover:bg-purple-500/15 text-xs text-purple-100 transition"
            >
              <Eye className="w-3.5 h-3.5" /> Preview
            </button>
            {/* Disabled without a visit: a version now covers one visit, so
                with none selected there is no history to show and nothing a
                restore could put back. */}
            <button
              onClick={openHistory}
              data-tour="version-history"
              disabled={!selectedEventObj}
              title={
                selectedEventObj
                  ? `Versions of ${selectedEvent}`
                  : "Pick a visit to see its version history"
              }
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/[0.04] text-xs text-slate-300 hover:text-white transition"
            >
              <History className="w-3.5 h-3.5" /> Version History
            </button>
            <button
              onClick={openAudit}
              data-tour="audit-trail"
              title="Every change made to this template, and who made it"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-300 hover:text-white transition"
            >
              <ScrollText className="w-3.5 h-3.5" /> Audit Trail
            </button>
            <button
              onClick={() => setExporting(true)}
              data-tour="export"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-300 hover:text-white transition"
            >
              <Printer className="w-3.5 h-3.5" /> Export / Print
            </button>
          </div>
        </div>

        {historyOpen && (
          <VersionHistoryModal
            versions={templateVersions}
            eventId={selectedEventObj?.id || ""}
            eventName={selectedEvent || ""}
            onRestore={restoreVersion}
            onExport={exportVersion}
            onRedline={setRedlineVersion}
            restoringId={restoringVersionId}
            exportingId={exportingVersionId}
            loading={historyLoading}
            onClose={() => setHistoryOpen(false)}
          />
        )}

        {/* What a version changed. The baseline is the previous APPROVED
            version of this same visit — resolved from the history already
            loaded, so nothing extra is fetched and nothing extra is stored.
            The modal can also switch to comparing against the visit as it
            stands now. See VersionRedlineModal. */}
        {redlineVersion && (
          <VersionRedlineModal
            version={redlineVersion}
            versionQuestions={versionEvent(redlineVersion)?.questions || []}
            previousVersion={redlineBaseline}
            previousQuestions={versionEvent(redlineBaseline)?.questions || []}
            currentQuestions={selectedEventObj?.questions || []}
            versionNumber={approvalNumberOf(templateVersions, redlineVersion)}
            previousNumber={approvalNumberOf(templateVersions, redlineBaseline)}
            eventName={redlineVersion.eventName || selectedEvent || ""}
            labelById={labelById}
            onClose={() => setRedlineVersion(null)}
          />
        )}

        {auditOpen && (
          <TemplateAuditModal
            events={auditEvents}
            eventId={selectedEventObj?.id || ""}
            eventName={selectedEvent || ""}
            loading={auditLoading}
            onClose={() => setAuditOpen(false)}
          />
        )}

        {exporting && template && (
          <ExportSourceDocModal
            studyId={studyId}
            template={template}
            defaultEventId={selectedEventObj?.id}
            onClose={() => setExporting(false)}
          />
        )}

        {previewing && template && selectedEventObj && (
          <SourceDocPreviewModal
            studyId={studyId}
            template={template}
            event={selectedEventObj}
            onClose={() => setPreviewing(false)}
          />
        )}

        {/* AI draft review banner */}
        {template.aiGenerated && template.status !== "approved" && (
          <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.08] backdrop-blur-xl p-4 flex items-start justify-between gap-3">
            <div className="text-sm text-amber-100/90">
              <div className="flex items-center gap-1.5 font-semibold text-amber-200">
                <Sparkles className="w-4 h-4" /> AI-generated draft
              </div>
              <p className="mt-1 text-amber-100/80">
                Review each field against the protocol (the source reference is shown
                under every question), edit as needed, then approve. This draft is{" "}
                <strong>not active</strong> until approved.
              </p>
              {template.aiProvenanceNote && (
                <p className="text-xs mt-1 text-amber-200/70">
                  What the AI used: {template.aiProvenanceNote}
                </p>
              )}
            </div>
            {/* This visit's sign-off state, beside the button that changes
                it — approval is per visit, so "approved" has to say WHICH
                thing is approved or it reads as the whole study. */}
            <span
              title={
                selectedEventObj?.approvedBy
                  ? `Approved by ${describeActor(selectedEventObj.approvedBy)}`
                  : undefined
              }
              className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${
                currentVisitApproved
                  ? "border-emerald-400/40 bg-emerald-400/15 text-emerald-200"
                  : "border-white/15 bg-white/[0.05] text-slate-400"
              }`}
            >
              {currentVisitApproved ? "Approved" : "Draft"}
            </span>
            {/* Why the button is grey. Without this the only difference
                between "nothing to approve" and a broken button is a
                tooltip nobody hovers. */}
            {unchangedSinceApproval && (
              <span className="shrink-0 text-[11px] text-slate-400">
                No changes since {approvedVersionName || "the approved version"}
              </span>
            )}
            <button
              onClick={markApproved}
              data-tour="approve"
              disabled={approveDisabled}
              title={approveTitle}
              className={`shrink-0 inline-flex items-center gap-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:brightness-110 disabled:opacity-70 ${approveCursorCls} text-white px-3.5 py-2 rounded-xl text-sm font-medium shadow transition ${
                unchangedSinceApproval ? "grayscale opacity-60" : ""
              }`}
            >
              {approving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Approving…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" /> {approveLabel}
                </>
              )}
            </button>
          </div>
        )}
        {/* Built (assembled) source documents — review then promote to the
            active patient template. This is the only place the assembled
            template can be made the one the SourceX workflow enrolls against. */}
        {studyId.endsWith("__assembled") && template.status !== "approved" && (
          <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.07] backdrop-blur-xl p-4 flex items-start justify-between gap-3">
            <div className="text-sm text-emerald-100/90">
              <div className="flex items-center gap-1.5 font-semibold text-emerald-200">
                <FileText className="w-4 h-4" /> Built source documents
              </div>
              <p className="mt-1 text-emerald-100/80">
                Built from the Protocol → eCRF mapping. Review and edit each section, then
                approve to make these the <strong>active source template</strong> every new
                patient in this study is enrolled against. Until then they are a draft.
              </p>
            </div>
            {/* This visit's sign-off state, beside the button that changes
                it — approval is per visit, so "approved" has to say WHICH
                thing is approved or it reads as the whole study. */}
            <span
              title={
                selectedEventObj?.approvedBy
                  ? `Approved by ${describeActor(selectedEventObj.approvedBy)}`
                  : undefined
              }
              className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${
                currentVisitApproved
                  ? "border-emerald-400/40 bg-emerald-400/15 text-emerald-200"
                  : "border-white/15 bg-white/[0.05] text-slate-400"
              }`}
            >
              {currentVisitApproved ? "Approved" : "Draft"}
            </span>
            {/* Why the button is grey. Without this the only difference
                between "nothing to approve" and a broken button is a
                tooltip nobody hovers. */}
            {unchangedSinceApproval && (
              <span className="shrink-0 text-[11px] text-slate-400">
                No changes since {approvedVersionName || "the approved version"}
              </span>
            )}
            <button
              onClick={markApproved}
              data-tour="approve"
              disabled={approveDisabled}
              title={approveTitle}
              className={`shrink-0 inline-flex items-center gap-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:brightness-110 disabled:opacity-70 ${approveCursorCls} text-white px-3.5 py-2 rounded-xl text-sm font-medium shadow transition ${
                unchangedSinceApproval ? "grayscale opacity-60" : ""
              }`}
            >
              {approving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Approving…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" /> {approveLabel}
                </>
              )}
            </button>
          </div>
        )}
        {/* Structural problems block approval — see markApproved. */}
        {problems.length > 0 && (
          <div className="rounded-2xl border border-rose-400/30 bg-rose-500/[0.08] backdrop-blur-xl p-4">
            <div className="flex items-center gap-1.5 font-semibold text-rose-200 text-sm">
              <AlertTriangle className="w-4 h-4" />
              {problems.length} structural problem
              {problems.length === 1 ? "" : "s"} — must be fixed before approval
            </div>
            <ul className="mt-1.5 text-xs text-rose-100/80 list-disc list-inside space-y-0.5">
              {problems.slice(0, 10).map((p) => (
                <li key={p}>{p}</li>
              ))}
              {problems.length > 10 && (
                <li className="list-none italic opacity-70">
                  …and {problems.length - 10} more.
                </li>
              )}
            </ul>
          </div>
        )}

        {template.status === "approved" && (
          <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.08] px-3 py-2.5 text-sm text-emerald-200 flex flex-wrap items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span className="min-w-0">
              Approved template — active for patient source documents. You can
              still edit it; new patients only pick up your changes after you{" "}
              <strong>re-approve</strong>.
            </span>
            {/* This visit's sign-off state, beside the button that changes
                it — approval is per visit, so "approved" has to say WHICH
                thing is approved or it reads as the whole study. */}
            <span
              title={
                selectedEventObj?.approvedBy
                  ? `Approved by ${describeActor(selectedEventObj.approvedBy)}`
                  : undefined
              }
              className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${
                currentVisitApproved
                  ? "border-emerald-400/40 bg-emerald-400/15 text-emerald-200"
                  : "border-white/15 bg-white/[0.05] text-slate-400"
              }`}
            >
              {currentVisitApproved ? "Approved" : "Draft"}
            </span>
            {/* Why the button is grey. Without this the only difference
                between "nothing to approve" and a broken button is a
                tooltip nobody hovers. */}
            {unchangedSinceApproval && (
              <span className="shrink-0 text-[11px] text-slate-400">
                No changes since {approvedVersionName || "the approved version"}
              </span>
            )}
            <button
              onClick={markApproved}
              data-tour="approve"
              disabled={approveDisabled}
              title={approveTitle}
              className={`ml-auto shrink-0 inline-flex items-center gap-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:brightness-110 disabled:opacity-70 ${approveCursorCls} text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                unchangedSinceApproval ? "grayscale opacity-60" : ""
              }`}
            >
              {approving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Approving…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {studyId.endsWith("__assembled")
                    ? approveLabel
                    : approveLabel}
                </>
              )}
            </button>
          </div>
        )}

        {saveError && (
          <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-200 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> A change could not be saved. Check your
            connection — your last edits may not have been stored.
          </div>
        )}

        {/* Section selector. `relative z-20`: `backdrop-blur-xl` gives this
            row its own stacking context, so the visit dropdown's z-50 only
            wins WITHIN that context — it can't lift the row's paint order
            above its sibling below (the document panel, which paints later
            in DOM order and would otherwise cover the open dropdown
            entirely). Explicitly stacking this whole row above that sibling
            fixes it. */}
        <div className="relative z-20 flex flex-wrap gap-3 items-center rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-3">
          <div className="relative">
            <select
              value={selectedSection}
              onChange={(e) => {
                setSelectedSection(e.target.value);
                setSelectedEvent(null);
              }}
              data-tour="section-picker"
              className={`${selectCls} min-w-[220px]`}
            >
              <option value="" className="bg-[#0b0820]">Select Section</option>
              {(template.sections || []).map((s) => (
                <option key={s.name} value={s.name} className="bg-[#0b0820]">
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          {/* Add Section — one menu for every kind of section this editor can
              add: a blank one, the eligibility checklist, and the standing logs
              (con meds, medical history), which run for the whole study rather
              than belonging to one visit and so land in their own SECTION.
              These were three separate controls; the actions are unchanged, the
              row is just no longer three-quarters "add" buttons.
              It has to live in THIS row, not the page header: the header has no
              z-index, so a menu opened there paints under the panels below it.
              This row is explicitly z-20 (see the comment on it), and is the
              same place the visit picker opens from. */}
          <div className="relative" ref={logMenuRef}>
            <button
              onClick={() => {
                setLogMenuOpen((v) => !v);
                setLogSubmenuOpen(false);
              }}
              title="Add a section to this template"
              data-tour="add-section"
              className="inline-flex items-center gap-1.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:brightness-110 text-white px-3 py-2 rounded-xl text-sm font-medium transition"
            >
              <Plus className="w-4 h-4" /> Add Section
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${
                  logMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {logMenuOpen && (
              // Solid, not translucent, and z-50 — same reasons as the visit
              // picker beside it: the document scrolls behind this menu, and
              // the panels below sit later in the DOM.
              <div className="solid-dropdown-menu absolute left-0 z-50 mt-1.5 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-white/15 bg-[#0c0826] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.85)] overflow-hidden">
                <div className="px-3 py-2 border-b border-white/10">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                    Add a section
                  </div>
                  <div className="mt-0.5 text-[10px] text-slate-500">
                    A section holds visits, or stands on its own.
                  </div>
                </div>
                <ul className="max-h-[70vh] overflow-y-auto scrollbar-thin py-1">
                  {/* Blank section — the plain "Add Section" this button used
                      to be on its own. */}
                  <li>
                    <button
                      onClick={() => {
                        setLogMenuOpen(false);
                        setLogSubmenuOpen(false);
                        addSection();
                      }}
                      title="Add an empty section you name yourself"
                      className="w-full text-left px-3 py-2 transition hover:bg-white/[0.06]"
                    >
                      <span className="flex items-center gap-1.5 text-sm text-slate-100">
                        <Plus className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                        New blank section
                      </span>
                      <span className="block pl-5 text-[11px] text-slate-500">
                        Empty — add visits to it yourself.
                      </span>
                    </button>
                  </li>

                  {/* Inclusion/Exclusion — the eligibility checklist, read out
                      of the study's own protocol. One row rather than a
                      sub-list: unlike the logs there is exactly one of these,
                      and a template can only ever hold one. */}
                  {(() => {
                    const added = (template.sections || []).some(
                      (s) => s.name === ELIGIBILITY_SECTION_NAME
                    );
                    // Where the feature isn't switched on yet (production), the
                    // row is listed but un-pickable rather than absent —
                    // exactly how a withdrawn standing log is handled below. A
                    // menu that quietly has one fewer item in one environment
                    // is how a coordinator concludes the feature was removed.
                    const soon = !ELIGIBILITY_EXTRACTION_ENABLED;
                    const blocked = soon || added || eligibilityBusy;
                    return (
                      <li>
                        <button
                          disabled={blocked}
                          onClick={() => {
                            if (blocked) return;
                            setLogMenuOpen(false);
                            setLogSubmenuOpen(false);
                            addEligibility();
                          }}
                          title={
                            soon
                              ? "Coming soon."
                              : added
                              ? "Already in this template."
                              : "Read the inclusion/exclusion criteria out of this study's protocol"
                          }
                          className={`w-full text-left px-3 py-2 transition ${
                            blocked
                              ? "opacity-45 cursor-not-allowed"
                              : "hover:bg-white/[0.06]"
                          }`}
                        >
                          <span className="flex items-center gap-1.5 text-sm text-slate-100">
                            {eligibilityBusy ? (
                              <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-slate-400" />
                            ) : (
                              <ListChecks className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                            )}
                            Inclusion/Exclusion criteria
                            {soon && (
                              <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md border border-amber-400/30 bg-amber-400/15 text-[9px] font-semibold uppercase tracking-wide text-amber-200">
                                Coming soon
                              </span>
                            )}
                            {!soon && added && (
                              <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md border border-emerald-400/30 bg-emerald-400/15 text-[9px] font-semibold uppercase tracking-wide text-emerald-200">
                                Added
                              </span>
                            )}
                          </span>
                          <span className="block pl-5 text-[11px] text-slate-500">
                            {soon
                              ? "Not available here yet."
                              : added
                              ? "Already in this template."
                              : eligibilityBusy
                              ? "Reading the protocol…"
                              : "Read from this study's protocol."}
                          </span>
                        </button>
                      </li>
                    );
                  })()}

                  {/* Standing logs — a sub-list, because there are several and
                      which one is a choice. Expanded in place; the menu stays
                      open until a log is actually picked.
                      Amber like "Use from template", because it is the other
                      half of the same thing: a "Standard block exists" notice
                      names whichever of the two menus carries that standard
                      (see StandardBlockNote's `useNow` / `coming`), so both
                      have to be recognizable from that notice's colour. */}
                  <li className="border-t border-white/10 mt-1 pt-1">
                    <button
                      onClick={() => setLogSubmenuOpen((v) => !v)}
                      aria-expanded={logSubmenuOpen}
                      title="Add a standing log that runs for the whole study"
                      className="w-full text-left px-3 py-2 transition hover:bg-amber-400/10"
                    >
                      <span className="flex items-center gap-1.5 text-sm text-amber-100">
                        <ClipboardList className="w-3.5 h-3.5 shrink-0 text-amber-300" />
                        Standing log
                        <ChevronRight
                          className={`w-3.5 h-3.5 ml-auto shrink-0 text-amber-300/70 transition-transform ${
                            logSubmenuOpen ? "rotate-90" : ""
                          }`}
                        />
                      </span>
                      <span className="block pl-5 text-[11px] text-slate-500">
                        Runs for the whole study — not tied to a visit.
                      </span>
                    </button>
                    {logSubmenuOpen && (
                      <ul className="mb-1 ml-3 border-l border-white/10 pl-1">
                        {SOURCE_LOG_TEMPLATES_MENU.map((t) => {
                          // Already in this template. Listed but un-pickable,
                          // the same as an unavailable one — adding it twice
                          // would make two sections share a name, and selection
                          // here is by name (see addLog).
                          const added = (template.sections || []).some(
                            (s) => s.name === t.label
                          );
                          const blocked = !!t.unavailable || added;
                          const note = t.unavailable
                            ? t.unavailable
                            : added
                            ? "Already in this template."
                            : "Prints on its own landscape page.";
                          return (
                            <li key={t.id}>
                              <button
                                disabled={blocked}
                                onClick={() => {
                                  if (blocked) return;
                                  setLogMenuOpen(false);
                                  setLogSubmenuOpen(false);
                                  addLog(t.id);
                                }}
                                title={t.unavailable || t.label}
                                className={`w-full text-left px-3 py-2 transition ${
                                  blocked
                                    ? "opacity-45 cursor-not-allowed"
                                    : "hover:bg-white/[0.06]"
                                }`}
                              >
                                <span className="flex items-center gap-1.5">
                                  <span className="block text-sm text-slate-100">
                                    {t.label}
                                  </span>
                                  {t.unavailable && (
                                    <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md border border-amber-400/30 bg-amber-400/15 text-[9px] font-semibold uppercase tracking-wide text-amber-200">
                                      Unavailable
                                    </span>
                                  )}
                                  {!t.unavailable && added && (
                                    <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md border border-emerald-400/30 bg-emerald-400/15 text-[9px] font-semibold uppercase tracking-wide text-emerald-200">
                                      Added
                                    </span>
                                  )}
                                </span>
                                <span className="block text-[11px] text-slate-500">
                                  {note}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                </ul>
              </div>
            )}
          </div>
          {selectedSection && (template.sections || []).length > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => moveSection(-1)}
                title="Move section up"
                className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/[0.05] border border-white/10 hover:bg-white/[0.12] text-slate-200 transition"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => moveSection(1)}
                title="Move section down"
                className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/[0.05] border border-white/10 hover:bg-white/[0.12] text-slate-200 transition"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}
          {/* What that click just did — same confirmation, same wording and
              same place as the visit arrows' notice below. */}
          {sectionMoveNotice && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-400/30">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              {sectionMoveNotice}
            </span>
          )}
          {selectedSection && (
            <button
              onClick={deleteSection}
              data-tour="section-manage"
              className="inline-flex items-center gap-1.5 bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 text-rose-200 px-3 py-2 rounded-xl text-sm transition"
            >
              <Trash2 className="w-4 h-4" /> Delete Section
            </button>
          )}

          {/* Visit — used to live in its own left-hand panel. Folded in here so
              that panel's width goes back to the document, and the answer-type
              palette can take its place. */}
          {/* A LOG section shows nothing here at all: it holds one event, whose
              name is the section name already shown in the picker to the left.
              Its single event is auto-selected (see the effect above), and the
              visit controls are hidden — a second event in a log section would
              print as a stray landscape page, and deleting the only one would
              leave an empty section. "Delete Section" removes a log. */}
          {selectedSectionObj && !sectionIsLog && (
            <>
              <span className="w-px self-stretch bg-white/10 mx-0.5" />
              {/* A custom dropdown, not a plain <select> — a native <select>
                  can't have draggable rows, and dragging is how visits get
                  reordered here (drag handle on each row), not just picked. */}
              <div className="relative" ref={visitMenuRef}>
                <button
                  type="button"
                  data-tour="visit-picker"
                  onClick={() => setVisitMenuOpen((v) => !v)}
                  className={`${selectCls} min-w-[220px] flex items-center justify-between gap-2`}
                >
                  <span className="truncate">{selectedEvent || "Select Visit"}</span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 shrink-0 transition-transform ${
                      visitMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {visitMenuOpen && (
                  <div className="solid-dropdown-menu absolute left-0 z-50 mt-1.5 w-64 max-w-[calc(100vw-2rem)] rounded-xl border border-white/15 bg-[#0c0826] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.85)] overflow-hidden">
                    <div className="px-3 py-1.5 border-b border-white/10 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                      Drag to reorder
                    </div>
                    <ul className="max-h-72 overflow-y-auto scrollbar-thin py-1">
                      {(selectedSectionObj.events || []).map((e) => (
                        <li
                          key={e.eventName}
                          draggable
                          onDragStart={(ev) => {
                            ev.stopPropagation();
                            setDraggingVisit(e.eventName);
                            ev.dataTransfer.effectAllowed = "move";
                            try {
                              ev.dataTransfer.setData("text/plain", e.eventName);
                            } catch {
                              /* some browsers restrict setData outside a user gesture */
                            }
                          }}
                          onDragOver={(ev) => {
                            if (!draggingVisit || draggingVisit === e.eventName) return;
                            ev.preventDefault();
                            ev.dataTransfer.dropEffect = "move";
                            if (visitDragOver !== e.eventName) setVisitDragOver(e.eventName);
                          }}
                          onDrop={(ev) => {
                            ev.preventDefault();
                            const dragged = draggingVisit;
                            setDraggingVisit(null);
                            setVisitDragOver(null);
                            reorderEvents(dragged, e.eventName);
                          }}
                          onDragEnd={() => {
                            setDraggingVisit(null);
                            setVisitDragOver(null);
                          }}
                          className={`flex items-center gap-1.5 px-2 py-1 transition ${
                            draggingVisit === e.eventName ? "opacity-50" : ""
                          } ${
                            visitDragOver === e.eventName
                              ? "outline outline-2 outline-purple-400/70 outline-offset-[-2px]"
                              : ""
                          }`}
                        >
                          <span className="shrink-0 w-5 h-5 flex items-center justify-center text-slate-500 cursor-grab active:cursor-grabbing">
                            <GripVertical className="w-3.5 h-3.5" />
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedEvent(e.eventName);
                              setVisitMenuOpen(false);
                            }}
                            className={`flex-1 min-w-0 text-left px-2 py-1.5 rounded-lg text-sm truncate transition ${
                              selectedEvent === e.eventName
                                ? "bg-purple-500/20 text-white"
                                : "text-slate-200 hover:bg-white/[0.06]"
                            }`}
                          >
                            {e.eventName}
                          </button>
                        </li>
                      ))}
                      {(selectedSectionObj.events || []).length === 0 && (
                        <li className="px-3 py-2 text-xs text-slate-500">No visits yet.</li>
                      )}
                      {/* Tail drop target, same purpose as the outline's own
                          — dragging past the last row means "move to the end". */}
                      {draggingVisit && (
                        <li
                          onDragOver={(ev) => {
                            ev.preventDefault();
                            ev.dataTransfer.dropEffect = "move";
                          }}
                          onDrop={(ev) => {
                            ev.preventDefault();
                            const dragged = draggingVisit;
                            setDraggingVisit(null);
                            setVisitDragOver(null);
                            reorderEvents(dragged, null);
                          }}
                          className="mx-2 mt-1 px-2 py-2 rounded-lg border border-dashed border-purple-300/40 text-[11px] text-purple-200/80 text-center"
                        >
                          Drop here to move to the end
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <button
                onClick={addEvent}
                data-tour="visit-manage"
                className="inline-flex items-center gap-1.5 bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] text-slate-100 px-3 py-2 rounded-xl text-sm transition"
              >
                <Plus className="w-4 h-4" /> Add Visit
              </button>
              {selectedEvent && (selectedSectionObj.events || []).length > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveEvent(selectedEvent, -1)}
                    title="Move visit up"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/[0.05] border border-white/10 hover:bg-white/[0.12] text-slate-200 transition"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveEvent(selectedEvent, 1)}
                    title="Move visit down"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/[0.05] border border-white/10 hover:bg-white/[0.12] text-slate-200 transition"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              )}
              {visitMoveNotice && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-400/30">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                  {visitMoveNotice}
                </span>
              )}
              {selectedEvent && (
                <button
                  onClick={() => deleteEvent(selectedEvent)}
                  className="inline-flex items-center gap-1.5 bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 text-rose-200 px-3 py-2 rounded-xl text-sm transition"
                >
                  <Trash2 className="w-4 h-4" /> Delete Visit
                </button>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left: the answer-type palette — used to be the Events panel;
              visits moved into the toolbar above so this space could go to
              drag-and-drop field types instead. No `self-start` here — it
              needs to STRETCH to match the document column's full height, or
              its sticky child has nothing to stay pinned within once you
              scroll past a short box, and the palette vanishes long before
              the last heading (same pattern the outline aside uses). */}
          <aside className="lg:w-1/5 shrink-0">
            {/* The rail is what sticks, not each box in it — so the tools stay
                on screen for the whole length of a long form, which is the
                point of moving them here. `relative z-10` because this column
                holds a dropdown and the document column paints later in DOM
                order; without it the menu opens underneath the document. It
                must stay BELOW the toolbar row's z-20 — above it, this rail
                covered the Add Section menu opening from that row. */}
            <div className="sticky top-4 relative z-10 flex flex-col gap-3">
              {/* Tools — Undo, Use from template, Add Heading. They used to sit
                  at the top-right of the document panel and scrolled away with
                  it; here they are always reachable. Separated from the answer
                  types below by being their own box: these act on the visit,
                  the palette below is something you drag. */}
              {selectedEventObj && (
                // `relative z-20` so the "Use from template" menu clears the
                // answer-type palette below. `backdrop-blur-xl` makes each of
                // these boxes its own stacking context at z-index 0, so the
                // palette — a later sibling — painted over this whole box,
                // menu and all, however high the menu's own z-index went.
                <div className="relative z-20 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-3">
                  <div className="px-1 pb-2 mb-2 border-b border-white/10">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white">
                      Tools
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Always here — they don't scroll away with the form.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button
                      onClick={undoEdit}
                      data-tour="undo"
                      disabled={!canUndo}
                      title={canUndo ? "Undo the last change" : "Nothing to undo"}
                      className="w-full inline-flex items-center gap-2 bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/[0.06] text-slate-100 px-2.5 py-2 rounded-xl text-xs transition"
                    >
                      <Undo2 className="w-3.5 h-3.5 shrink-0" /> Undo
                    </button>
                    {/* Use from template — insert a standard reusable question
                        block. Inserted questions are fully editable afterward.
                        Amber, matching the "Standard block exists" notice on a
                        heading: that notice's whole instruction is to come
                        here, so the button it sends you to has to be the thing
                        you recognize when you arrive. */}
                    <div className="relative" ref={templateMenuRef}>
                      <button
                        onClick={() => setTemplateMenuOpen((v) => !v)}
                        title="Insert a standard question block"
                        data-tour="use-template"
                        className="w-full inline-flex items-center gap-2 bg-amber-400/15 border border-amber-400/35 hover:bg-amber-400/25 text-amber-100 px-2.5 py-2 rounded-xl text-xs transition"
                      >
                        <LibraryBig className="w-3.5 h-3.5 shrink-0" /> Use from template
                        <ChevronDown
                          className={`w-3.5 h-3.5 ml-auto shrink-0 transition-transform ${
                            templateMenuOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {templateMenuOpen && (
                        // Solid, not translucent: the document scrolls behind
                        // this menu and a see-through panel made the question
                        // labels unreadable. z-50 clears the sticky outline and
                        // events rails, which sit later in the DOM. Opens to the
                        // LEFT edge now that the button lives in the left rail —
                        // anchored right it would hang off the panel.
                        <div className="template-dropdown-menu absolute left-0 z-50 mt-1.5 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-white/15 bg-[#0c0826] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.85)] overflow-hidden">
                          <div className="px-3 py-2 border-b border-white/10">
                            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                              Standard question blocks
                            </div>
                            <div className="mt-0.5 text-[10px] text-slate-500">
                              Click to add at the top, drag one into the outline
                              to place it, or drop it on an open question to use
                              it there — no heading.
                            </div>
                          </div>
                          <ul className="max-h-72 overflow-y-auto scrollbar-thin py-1">
                            {SOURCE_QUESTION_TEMPLATES_MENU.map((t) => (
                              <li key={t.id}>
                                <button
                                  // A withdrawn block (see `unavailable` in
                                  // sourceQuestionTemplates.js) is listed but
                                  // can't be picked or dragged — it stays
                                  // visible so it's clear it exists and is
                                  // coming back, rather than appearing to have
                                  // been deleted.
                                  disabled={!!t.unavailable}
                                  draggable={!t.unavailable}
                                  onDragStart={(ev) => {
                                    if (t.unavailable) {
                                      ev.preventDefault();
                                      return;
                                    }
                                    ev.dataTransfer.effectAllowed = "copy";
                                    setDraggingTemplateId(t.id);
                                    try {
                                      ev.dataTransfer.setData(TEMPLATE_DRAG_TYPE, t.id);
                                      ev.dataTransfer.setData("text/plain", t.label);
                                    } catch {
                                      /* some browsers restrict setData */
                                    }
                                  }}
                                  onDragEnd={() => {
                                    setDraggingTemplateId(null);
                                    setTemplateMenuOpen(false);
                                  }}
                                  onClick={() => {
                                    if (t.unavailable) return;
                                    setTemplateMenuOpen(false);
                                    insertTemplate(t.id);
                                  }}
                                  title={
                                    t.unavailable ||
                                    `${t.label} — click to add at the top, drag into the outline to place it, or drop it on an open question to use it there`
                                  }
                                  className={`w-full text-left px-3 py-2 transition ${
                                    t.unavailable
                                      ? "opacity-45 cursor-not-allowed"
                                      : "hover:bg-white/[0.06] cursor-grab active:cursor-grabbing"
                                  }`}
                                >
                                  <span className="flex items-center gap-1.5">
                                    <span className="block text-sm text-slate-100">
                                      {t.label}
                                    </span>
                                    {t.unavailable && (
                                      <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md border border-amber-400/30 bg-amber-400/15 text-[9px] font-semibold uppercase tracking-wide text-amber-200">
                                        Unavailable
                                      </span>
                                    )}
                                  </span>
                                  <span className="block text-[11px] text-slate-500">
                                    {t.unavailable
                                      ? t.unavailable
                                      : `${t.questions.length} question${
                                          t.questions.length === 1 ? "" : "s"
                                        }`}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {/* Only headings are added at the top level — every question
                        lives under a heading, added via its "Add question" button. */}
                    <button
                      onClick={addHeading}
                      title="Add a group heading"
                      data-tour="add-heading"
                      className="w-full inline-flex items-center gap-2 bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] text-slate-100 px-2.5 py-2 rounded-xl text-xs transition"
                    >
                      <Plus className="w-3.5 h-3.5 shrink-0" /> Add Heading
                    </button>
                  </div>
                </div>
              )}
              <div data-tour="answer-types">
                <FieldTypePalette />
              </div>
            </div>
          </aside>

          {/* Right: Questions */}
          <div className="flex-1 min-w-0 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-4">
            {selectedEventObj ? (
              <>
                {/* Just the visit's title now. Undo / Use from template / Add
                    Heading used to sit at the right of this row, where they
                    scrolled out of reach as soon as the form got long; they
                    live in the left rail's sticky Tools panel instead. */}
                <div className="flex flex-wrap justify-between items-start gap-2 mb-4">
                  {/* Just the visit's name. A coordinator works one visit at a
                      time, so how the rest of the study is progressing is not
                      what they came to this heading for — the visit's own
                      sign-off state sits next to the Approve button instead,
                      where it describes the thing about to be clicked. */}
                  <div className="min-w-0 flex-1 basis-64">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="font-semibold text-lg text-white min-w-0 truncate">
                        {selectedEvent}
                      </h3>
                      {/* A log / criteria section takes its single event's name
                          from the SECTION, so renaming it here would split the
                          two apart — the pencil is for ordinary visits only. */}
                      {!sectionIsLog && selectedEvent && (
                        <button
                          type="button"
                          onClick={renameEvent}
                          title="Rename this visit"
                          data-tour="rename-visit"
                          aria-label="Rename this visit"
                          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] text-slate-300 hover:text-white transition"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Shown exactly as it prints — option boxes, fill lines and blank
                      table rows are what the Word / PDF export produces.
                    </p>

                    {/* "Finalized on paper" — for a visit whose source was
                        completed on paper before this study moved into SourceX.
                        Offered on every kind of document (visit, log,
                        checklist), because any of them can predate SourceX.
                        A label wrapping the box so the whole line is the hit
                        target. See togglePaperFinal. */}
                    <label
                      data-tour="paper-final"
                      className={`mt-2 inline-flex items-start gap-2 rounded-lg border px-2 py-1.5 cursor-pointer transition ${
                        eventIsPaperFinal(selectedEventObj)
                          ? "border-amber-300/40 bg-amber-400/[0.1] hover:bg-amber-400/[0.16]"
                          : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={eventIsPaperFinal(selectedEventObj)}
                        onChange={togglePaperFinal}
                        className="mt-[2px] w-3.5 h-3.5 shrink-0 rounded border-white/25 bg-white/[0.06] accent-amber-500"
                      />
                      <span className="min-w-0">
                        <span
                          className={`block text-[11px] font-medium ${
                            eventIsPaperFinal(selectedEventObj)
                              ? "text-amber-100"
                              : "text-slate-300"
                          }`}
                        >
                          Finalized on paper
                        </span>
                        <span className="block text-[10px] text-slate-500">
                          {eventIsPaperFinal(selectedEventObj)
                            ? `Marked by ${describeActor(
                                selectedEventObj.paperFinalBy
                              )}${
                                selectedEventObj.paperFinalAt
                                  ? ` · ${formatDocDate(selectedEventObj.paperFinalAt)}`
                                  : ""
                              }. Anyone exporting this is told to check with them.`
                            : "Tick if this visit's source was completed on paper before SourceX. Nothing here changes — exports just warn first."}
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

                {(selectedEventObj.questions || []).map((q, qIndex) =>
                  // Hidden by a collapsed heading — the data is untouched, it
                  // just isn't drawn.
                  hiddenQuestionIndexes.has(qIndex) ? null : editingIndex === q.id ? (
                    // The open editor is framed and labelled with the question's
                    // own number, so a coordinator who scrolled away and back —
                    // or opened one editor in a form of forty fields — can see
                    // at a glance WHICH question is the one they are editing.
                    // White, not one of the palette hues, for the same reason a
                    // jump flashes white: it says "this is the one you are on",
                    // which is about you and not about the question. Every hue
                    // is already spoken for anyway — purple a resting question,
                    // rose broken, amber "a standard block exists", sky "reveals
                    // others", fuchsia conditional, emerald a heading.
                    <div
                      key={q.id}
                      id={anchorId(q.id)}
                      className="relative mb-4 -mx-1 scroll-mt-24 rounded-2xl border-2 border-white/80 bg-white/[0.11] ring-4 ring-white/25 shadow-[0_20px_60px_-15px_rgba(255,255,255,0.35)] px-2.5 pt-2 pb-2.5"
                    >
                      <div className="flex items-center gap-2 px-1 pb-2 mb-1 border-b border-white/25">
                        <span className="shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-white text-[11px] font-bold uppercase tracking-wider text-[#0c0826]">
                          <Pencil className="w-3 h-3 shrink-0" />
                          Editing
                        </span>
                        <span className="min-w-0 truncate text-xs font-semibold text-white">
                          Question {numberById.get(q.id)}
                          {q.question ? ` · ${q.question}` : ""}
                        </span>
                      </div>
                      <QuestionBuilder
                        initial={q}
                        startEmpty={pendingNewId.current === q.id}
                        onSave={(built) => saveQuestionBuilder(q.id, built)}
                        onCancel={cancelEdit}
                        confirm={askConfirm}
                        usedByOption={(option) =>
                          findFieldsUsingOption(selectedEventObj, q.id, option)
                        }
                        visibleWhen={q.visibleWhen}
                        onVisibilityChange={(next) => setQuestionVisibility(q.id, next)}
                        branchCandidates={branchCandidatesFor(q)}
                        branchAllCandidates={branchCandidates.filter((c) => c.id !== q.id)}
                        sectionLabelOf={sectionLabelOf}
                        defaultBranchQuestionId={seedBranchQuestion(q)}
                        onDropTemplate={(tplId) => {
                          setDraggingTemplateId(null);
                          setTemplateMenuOpen(false);
                          applyTemplateToQuestion(q.id, tplId);
                        }}
                      />
                    </div>
                  ) : (
                    (() => {
                      // Reorder/edit/delete controls, shared by both a heading
                      // and a normal question. Moving a question up/down is how
                      // a coordinator files it under the right heading.
                      // Move buttons parameterized by what "up/down" means here:
                      // for a heading it moves the whole block (heading + its
                      // fields); for a field it moves just that field.
                      const mkMoveBtns = (onUp, onDown, upTitle, downTitle) => (
                        <>
                          <button
                            onClick={onUp}
                            title={upTitle}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={onDown}
                            title={downTitle}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </>
                      );
                      const editBtn = (
                        <button
                          onClick={() => startEdit(q)}
                          title="Edit"
                          data-tour="question-edit"
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      );
                      // `onDelete` differs by row: a heading removes its whole
                      // block, a field removes only itself.
                      const deleteBtn = (onDelete, title) => (
                        <button
                          onClick={onDelete}
                          title={title}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-300 hover:bg-rose-500/15 transition"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      );
                      // A heading isn't built from answer pieces, so it gets a
                      // plain rename instead of opening QuestionBuilder.
                      const headerEditBtn = (
                        <button
                          onClick={() => renameHeading(q)}
                          title="Rename"
                          data-tour="heading-rename"
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      );

                      // No branch button on any row any more, heading or
                      // question. A question's condition is set in the question
                      // editor; a heading's is reached from its own "conditional"
                      // badge, which only exists once it has a rule. Setting a
                      // NEW condition on a heading is therefore no longer
                      // possible from the UI — a deliberate trade for a toolbar
                      // that isn't three-quarters branch controls.
                      const branchPopoverBlock = branchPopoverFor === q.id && (
                        (() => {
                          // Driven by the DRAFT, not q.visibleWhen — see
                          // branchDraft's declaration. Re-deriving from the
                          // persisted value here is exactly what silently
                          // reset the picker after choosing a controlling
                          // question but before choosing its answer.
                          const cond = branchDraft || parseCondition(q.visibleWhen);
                          return (
                            <div className="mt-1.5">
                              <ConditionBuilder
                                mode={cond.mode}
                                rules={cond.rules}
                                complex={cond.complex}
                                candidates={branchCandidatesFor(q)}
                                allCandidates={branchCandidates.filter((c) => c.id !== q.id)}
                                sectionLabelOf={sectionLabelOf}
                                currentSectionLabel={sectionLabelOf(q.id)}
                                defaultQuestionId={seedBranchQuestion(q)}
                                onChange={(mode, rules) => {
                                  setBranchDraft({ mode, rules, complex: false });
                                  setQuestionVisibility(q.id, buildCondition(mode, rules));
                                }}
                                onClear={() => {
                                  setBranchDraft({ mode: "any", rules: [], complex: false });
                                  setQuestionVisibility(q.id, undefined);
                                }}
                              />
                              <button
                                onClick={closeBranchPopover}
                                className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-slate-300 bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] transition"
                              >
                                Done
                              </button>
                            </div>
                          );
                        })()
                      );

                      // A heading renders as a full-width group title (via
                      // QuestionField's Header divider). Its controls include an
                      // "Add question" button that inserts INTO this section, right
                      // here — no scrolling to a global button.
                      if (q.type === "Header") {
                        const isCollapsed = collapsedSections.has(
                          sectionKeyByIndex.get(qIndex)
                        );
                        const owned = sectionFieldCount.get(qIndex) || 0;
                        const justAdded = justAddedIds.has(q.id);
                        // Red when this section (heading or any field under it)
                        // has a structural problem. Red wins over the green
                        // "just added" ring so a problem is never masked.
                        const hasProblem = sectionHasProblem.get(qIndex);
                        // RegX ships a standard block for this section and it
                        // isn't the one being used. Advice, not a problem — it
                        // ranks BELOW rose so a broken section is never
                        // recoloured into a suggestion, and it blocks nothing.
                        // Only on the row that carries the warning, so a
                        // section flags once. See standardBlockAdvice.js.
                        const advice = standardAdvice.get(q.id);
                        const anchorsAdvice = advice?.headingId === q.id;
                        const adviceHidden = anchorsAdvice && adviceDismissed(advice);
                        const advises = anchorsAdvice && !adviceHidden;
                        return (
                          <div
                            key={q.id}
                            id={anchorId(q.id)}
                            // Outlined, never filled — a tint here competed with
                            // the question cards below for the eye, and the
                            // heading has bold text and a wider box to set it
                            // apart already. Emerald, because it is the one hue
                            // not already spoken for: purple is an ordinary
                            // question, sky "reveals others", FUCHSIA
                            // "conditional", rose "broken", amber "a standard
                            // block exists for this". A heading must not share
                            // a colour with any of them.
                            //
                            // Problem / just-added still override. Those two DO
                            // carry a fill, which is what keeps them legible as
                            // transient states now that a resting heading has
                            // none — including the just-added green, which would
                            // otherwise be a hairline apart from the resting one.
                            //
                            // "A standard exists for this" is amber, and amber
                            // is now ONLY this. Conditional wore amber too, and
                            // at a glance the two read as the same state. It
                            // then moved one shade along to orange, which was
                            // still read as "the yellow one" beside amber — so
                            // it is off the warm end of the palette entirely
                            // now, at fuchsia. Nothing else warm is conditional.
                            className={`group scroll-mt-24 mt-5 mb-2.5 rounded-xl border-2 px-2.5 py-2 -mx-2.5 transition duration-300 ${
                              hasProblem
                                ? "ring-1 ring-rose-400/70 border-rose-400/40 bg-rose-500/[0.08]"
                                : justAdded
                                ? "ring-1 ring-emerald-400/60 border-emerald-400/40 bg-emerald-400/[0.14]"
                                : advises
                                ? "border-amber-400/55 bg-amber-400/[0.06] hover:border-amber-300/80"
                                : "border-emerald-300/45 hover:border-emerald-300/75"
                            } ${
                              // The section the open editor belongs to, in the
                              // same white the editor itself wears — FILLED,
                              // not just ringed: a resting heading carries no
                              // fill at all, so a fill is the one thing that
                              // reads as "this one" from the other end of a
                              // long form. ADDED to whatever tone the heading
                              // already has rather than replacing it, so a red
                              // "needs fixing" heading is never masked by being
                              // the one you happen to be working in.
                              // The fill is dropped when the heading already
                              // carries one of its own (red "needs fixing",
                              // green "just added"). Two background classes on
                              // one element is a coin toss decided by stylesheet
                              // order, not by which is written last here — so
                              // the ring alone carries it in those two cases,
                              // and the state that matters more stays visible.
                              editingHeadingIndex !== qIndex
                                ? ""
                                : hasProblem || justAdded
                                ? "ring-2 ring-white/70"
                                : "ring-2 ring-white/70 bg-white/[0.18] shadow-[0_10px_30px_-12px_rgba(255,255,255,0.4)]"
                            } ${
                              // A jump lands on headings too (the outline jumps
                              // to them), so they flash the same way.
                              flashId === q.id
                                ? "ring-2 ring-white/80 bg-white/[0.16]"
                                : ""
                            }`}
                          >
                          <div className="flex items-center justify-between gap-2">
                            {/* The title row itself is the collapse toggle. A
                                div, not a button — QuestionField renders block
                                elements, which are invalid inside a <button>. */}
                            <div
                              role="button"
                              tabIndex={0}
                              aria-expanded={!isCollapsed}
                              data-tour="heading-toggle"
                              onClick={() => toggleSection(qIndex)}
                              onKeyDown={(ev) => {
                                if (ev.key === "Enter" || ev.key === " ") {
                                  ev.preventDefault();
                                  toggleSection(qIndex);
                                }
                              }}
                              title={
                                isCollapsed
                                  ? "Expand this section"
                                  : "Collapse this section"
                              }
                              className="min-w-0 flex-1 flex items-center gap-1.5 cursor-pointer rounded-lg hover:bg-white/[0.04] transition"
                            >
                              <ChevronRight
                                className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${
                                  isCollapsed ? "" : "rotate-90"
                                }`}
                              />
                              {/* The section number every question under it is
                                  numbered from — see numberById. */}
                              <span
                                title={`Section ${numberById.get(q.id)}`}
                                className="shrink-0 w-6 h-6 rounded-md bg-emerald-400/20 text-emerald-100 text-xs font-bold tabular-nums flex items-center justify-center"
                              >
                                {numberById.get(q.id)}
                              </span>
                              <div className="min-w-0 flex-1">
                                <QuestionField question={q} value={q.value} readOnly />
                              </div>
                              {hasProblem && (
                                <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-rose-200 px-1.5 py-0.5 rounded-md bg-rose-500/15 border border-rose-400/30">
                                  <AlertTriangle className="w-3 h-3" /> needs fixing
                                </span>
                              )}
                              {/* All that is left of a dismissed notice: one
                                  grey book. The amber frame, the sentence and
                                  the outline highlight are all gone, which is
                                  what "remove the warning" has to mean — but
                                  dismissing by accident and reloading the page
                                  must not put a standard permanently out of
                                  reach, so the way back is here rather than
                                  nowhere. stopPropagation because the whole
                                  title row is the collapse toggle. */}
                              {adviceHidden && (
                                <button
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    setAdviceDismissed(advice, false);
                                  }}
                                  title={`${describeStandardAdvice(
                                    advice
                                  )} — you chose to keep this study's own version. Click to show this again.`}
                                  className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-slate-600 hover:text-amber-200 hover:bg-amber-400/15 transition"
                                >
                                  <LibraryBig className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {/* A heading that IS conditional says so, and the
                                  badge is how its rule is changed or cleared.
                                  The toolbar's branch button is gone from
                                  headings as it is from questions; without this
                                  the rule would still apply to the exported
                                  document with nothing left in the UI able to
                                  touch it. Nothing new appears on an ordinary
                                  heading — the badge only exists once there is
                                  a rule to edit. stopPropagation because the
                                  whole title row is the collapse toggle. */}
                              {q.visibleWhen && (
                                <button
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    toggleBranchPopover(q);
                                  }}
                                  title={`${describeCondition(
                                    q.visibleWhen,
                                    numberedLabelById
                                  ).parts.join(" / ")} — click to change or clear`}
                                  className="shrink-0 inline-flex items-center gap-1 text-[11px] text-fuchsia-200 px-1.5 py-0.5 rounded-md bg-fuchsia-400/15 border border-fuchsia-400/30 hover:bg-fuchsia-400/30 hover:text-white transition"
                                >
                                  <GitBranch className="w-3 h-3" /> conditional
                                </button>
                              )}
                              {dependentsMap.has(q.id) && (
                                <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-sky-200 px-1.5 py-0.5 rounded-md bg-sky-400/15 border border-sky-400/30">
                                  <Eye className="w-3 h-3" />
                                  reveals {dependentsMap.get(q.id).length}
                                </span>
                              )}
                              {/* Says outright what the white ring means, for
                                  a heading that is off screen from the editor
                                  it belongs to. */}
                              {editingHeadingIndex === qIndex && (
                                <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-white px-1.5 py-0.5 rounded-md bg-white/15 border border-white/40">
                                  <Pencil className="w-3 h-3" /> editing here
                                </span>
                              )}
                            </div>
                            {/* Deliberately not `.row-actions` — a section
                                heading's controls stay visible at all times.
                                Hover-to-reveal worked fine per-field, but on a
                                heading it hid the very controls (reorder whole
                                section, delete section) a user scanning the
                                outline most needs to find at a glance. */}
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => addFieldToSection(qIndex)}
                                title="Add a question to this section"
                                data-tour="heading-add-question"
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-lg text-xs text-emerald-200 bg-emerald-400/10 border border-emerald-400/25 hover:bg-emerald-400/20 transition"
                              >
                                <Plus className="w-3.5 h-3.5" /> Add question
                              </button>
                              {mkMoveBtns(
                                () => moveHeadingBlock(qIndex, -1),
                                () => moveHeadingBlock(qIndex, 1),
                                "Move this whole section up",
                                "Move this whole section down"
                              )}
                              {headerEditBtn}
                              {/* Consolidate a document with too many small
                                  sections: move this heading's fields onto
                                  the end of another heading's, then drop the
                                  now-redundant title line. */}
                              <div className="relative merge-menu-root">
                                <button
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    setMergeMenuFor((cur) => (cur === q.id ? null : q.id));
                                  }}
                                  title="Merge this section into another"
                                  data-tour="heading-merge"
                                  disabled={headingLabelById.size < 2}
                                  className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition"
                                >
                                  <Merge className="w-3.5 h-3.5" />
                                </button>
                                {mergeMenuFor === q.id && (
                                  <div className="solid-dropdown-menu absolute right-0 z-50 mt-1.5 w-64 rounded-xl border border-white/15 bg-[#0c0826] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.85)] overflow-hidden">
                                    <div className="px-3 py-2 border-b border-white/10">
                                      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                                        Merge into…
                                      </div>
                                      <div className="mt-0.5 text-[10px] text-slate-500">
                                        Its fields join the end of the section you pick.
                                      </div>
                                    </div>
                                    <ul className="max-h-64 overflow-y-auto scrollbar-thin py-1">
                                      {[...headingLabelById.entries()]
                                        .filter(([id]) => id !== q.id)
                                        .map(([id, label]) => (
                                          <li key={id}>
                                            <button
                                              onClick={async () => {
                                                setMergeMenuFor(null);
                                                const ok = await askConfirm(
                                                  `Every field under “${
                                                    q.question || "this section"
                                                  }” moves to the end of “${
                                                    label || "(untitled section)"
                                                  }”, and “${
                                                    q.question || "this section"
                                                  }” itself is removed.`,
                                                  {
                                                    title: "Merge this section?",
                                                    confirmLabel: "Merge",
                                                  }
                                                );
                                                if (ok) mergeHeadingInto(q.id, id);
                                              }}
                                              className="w-full text-left px-3 py-2 text-sm text-slate-100 hover:bg-white/[0.06] transition truncate"
                                            >
                                              {label || "(untitled section)"}
                                            </button>
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                              {/* The same section is usually wrong in every
                                  visit that carries it, so fixing one and
                                  repeating the work by hand is the job software
                                  should be doing. */}
                              <button
                                onClick={() => {
                                  setCopyBlockFor(qIndex);
                                  setCopyTargets([]);
                                }}
                                title="Copy this section, and everything under it, into other visits"
                                data-tour="heading-copy"
                                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-sky-200 hover:bg-sky-400/15 transition"
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                              {deleteBtn(
                                () => deleteHeadingBlock(qIndex),
                                "Delete this section and everything under it"
                              )}
                            </div>
                            {/* Last in the row so it pins to the FAR right,
                                past the hover controls. Sitting before them it
                                landed mid-row against the space they reserve,
                                which read as a stray number. */}
                            <span
                              title={
                                isCollapsed && owned > 0
                                  ? `${owned} field${owned === 1 ? "" : "s"} hidden`
                                  : undefined
                              }
                              className="shrink-0 w-4 text-right text-[11px] tabular-nums text-slate-500"
                            >
                              {isCollapsed && owned > 0 ? owned : ""}
                            </span>
                          </div>
                          {/* Under the title it is about — see StandardBlockNote. */}
                          {advises && (
                            <StandardBlockNote
                              advice={advice}
                              onDismiss={() => setAdviceDismissed(advice, true)}
                            />
                          )}
                          {branchPopoverBlock}
                          </div>
                        );
                      }

                      // Per-question controls: reorder, insert-a-question-below
                      // (for precise placement mid-section), edit, delete.
                      //
                      // Conditional visibility is DELIBERATELY not here. It used
                      // to have two of these buttons — an indent/outdent pair
                      // and a branch toggle — on every question in the document,
                      // which is three quarters of a toolbar spent on the thing
                      // most questions never use. It now lives in one place, the
                      // "Make conditional" button inside the question editor,
                      // and the fuchsia note on the card says when a rule exists.
                      // A heading keeps its own branch toggle: a heading is
                      // renamed rather than opened in the editor, so that toggle
                      // is the only way to make one conditional.
                      const controls = (
                        // Always visible, not hover-to-reveal. Hiding them
                        // kept the document tidy for someone who already knew
                        // they were there, and hid the entire feature set from
                        // everyone else.
                        <div className="flex items-center gap-1 shrink-0">
                          {mkMoveBtns(
                            () => moveQuestion(q.id, -1),
                            () => moveQuestion(q.id, 1),
                            "Move up",
                            "Move down"
                          )}
                          <button
                            onClick={() => addFieldAfter(q.id)}
                            title="Insert a question below this one"
                            data-tour="question-insert"
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-emerald-200 hover:bg-emerald-400/15 transition"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                          {/* A Table that should never have been one. The eCRF
                              reader groups a question with a codelist as if it
                              were a repeating log, and the field editor skips
                              Tables — so without this there is no way to correct
                              an already-built source document from the UI. */}
                          {q.type === "Table" && (q.columns || []).length > 0 && (
                            <button
                              onClick={() => splitTable(q.id)}
                              title="Not a log? Split this table into one question per column."
                              data-tour="split-table"
                              className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-amber-200 hover:bg-amber-400/15 transition"
                            >
                              <Rows3 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {editBtn}
                          <button
                            onClick={() => setCopyQuestionFor(q.id)}
                            title="Copy this question into a heading of this visit or another visit"
                            data-tour="question-copy"
                            aria-label={`Copy question ${q.question || ""}`.trim()}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-sky-200 hover:bg-sky-400/15 transition"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          {deleteBtn(() => deleteQuestion(q.id), "Delete")}
                        </div>
                      );

                      // One CARD per question, not a row on a shared rail.
                      // A rail is a line BETWEEN things and leaves you to infer
                      // where one question ends and the next begins; a bordered
                      // card states it.
                      //
                      // Every question card looks the same: frosted white fill,
                      // thick purple outline. A question is a question, whether
                      // or not it happens to branch — so branching is said ONCE,
                      // in the fuchsia "only shows when…" / sky "shows N other
                      // fields" notes inside the card (see BranchNotes), rather
                      // than also recolouring the card around them. Saying it
                      // twice made a routine document look like four kinds of
                      // thing and buried the one colour that is genuinely
                      // urgent.
                      //
                      // That one is rose: a card with a structural problem keeps
                      // its own outline, because it is not a category of
                      // question — it is a thing that must be fixed before the
                      // template can be approved, and it must never be one of a
                      // uniform row.
                      const fieldProblems = problemById.get(q.id);
                      // A standardized block exists for this run of questions
                      // and there is no heading above them to carry the notice
                      // — so the first of them does. Every other question in a
                      // flagged section says nothing: the section is warned
                      // about once. See standardBlockAdvice.js.
                      const fieldAdvice = standardAdvice.get(q.id);
                      const fieldAnchors =
                        !!fieldAdvice && fieldAdvice.headingId === q.id;
                      const fieldAdviceHidden =
                        fieldAnchors && adviceDismissed(fieldAdvice);
                      const fieldAdvises = fieldAnchors && !fieldAdviceHidden;
                      const depth = branchDepth.get(q.id) || 0;
                      // A jump just landed here — ring it for a moment. White,
                      // not one of the palette colours: it says "this is the
                      // one you asked for", which is about the navigation that
                      // just happened, not about the question.
                      const flashing = flashId === q.id;
                      const cardFill = flashing
                        ? "bg-white/[0.20]"
                        : "bg-white/[0.09]";
                      const tone = fieldProblems
                        ? "border-2 border-rose-400/80 hover:border-rose-400/95"
                        : fieldAdvises
                        // Only ever the card that CARRIES the notice, which is
                        // only ever a section with no heading of its own. In
                        // every other flagged section the amber frame is on the
                        // heading band, and the questions under it stay the
                        // uniform purple a question always is.
                        ? "border-2 border-amber-400/70 hover:border-amber-300/90"
                        : "border-2 border-purple-400/70 hover:border-purple-300/90";
                      const numTone = fieldProblems
                        ? "bg-rose-500/25 text-rose-100"
                        : "bg-purple-500/25 text-purple-100";
                      return (
                        <div
                          key={q.id}
                          id={anchorId(q.id)}
                          // Indent by branch depth so a conditional question
                          // still reads as sitting under whatever reveals it.
                          // The card's own border now does the separating, so
                          // no connector elbows are drawn on top of it.
                          style={{ marginLeft: `${1.5 + depth * 1.5}rem` }}
                          className={`group relative scroll-mt-24 mb-2.5 rounded-xl px-3 py-2.5 transition-[margin,border-color,background-color,box-shadow] duration-300 ${cardFill} ${tone} ${
                            flashing ? "ring-2 ring-white/80 ring-offset-0" : ""
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 min-w-0">
                              {/* The number is how a coordinator and a monitor
                                  refer to the same question out loud. */}
                              <span
                                title={`Question ${numberById.get(q.id)}`}
                                className={`shrink-0 mt-[1px] px-1.5 h-5 rounded-md text-[11px] font-semibold tabular-nums flex items-center ${numTone}`}
                              >
                                {numberById.get(q.id)}
                              </span>
                              <p
                                className="font-medium text-slate-100 min-w-0"
                                title={
                                  depth > 0
                                    ? "Only appears after a certain answer"
                                    : undefined
                                }
                              >
                                {q.question || (
                                  <span className="italic text-rose-300">(no question text)</span>
                                )}
                                {q.required && <span className="ml-1 text-rose-300">*</span>}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {/* Always on, never hover-only. */}
                              <span className="hidden sm:inline-flex items-center text-[10px] uppercase tracking-wide text-slate-400 mr-1">
                                {FIELD_TYPE_LABELS[q.type] || q.type}
                              </span>
                              {controls}
                            </div>
                          </div>
                          {fieldProblems && (
                            <div className="mt-1 flex items-start gap-1.5 text-xs text-rose-200">
                              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                              <span>{fieldProblems.join(" ")} Click edit to fix.</span>
                            </div>
                          )}
                          {q.sourceRef && (
                            <p
                              className={`text-xs mt-0.5 ${
                                /^uncertain/i.test(q.sourceRef)
                                  ? "text-rose-300 font-medium"
                                  : "text-slate-500"
                              }`}
                            >
                              📄 {q.sourceRef}
                            </p>
                          )}
                          {/* Only when this question heads a flagged run with
                              no heading of its own — see StandardBlockNote. */}
                          {fieldAdvises && (
                            <StandardBlockNote
                              advice={fieldAdvice}
                              onDismiss={() => setAdviceDismissed(fieldAdvice, true)}
                            />
                          )}
                          {/* The way back, same as on a heading. */}
                          {fieldAdviceHidden && (
                            <button
                              onClick={() => setAdviceDismissed(fieldAdvice, false)}
                              title={`${describeStandardAdvice(
                                fieldAdvice
                              )} — you chose to keep this study's own version. Click to show this again.`}
                              className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-slate-600 hover:text-amber-200 transition"
                            >
                              <LibraryBig className="w-3 h-3" /> Standard notice hidden
                            </button>
                          )}
                          <BranchNotes
                            question={q}
                            labelById={numberedLabelById}
                            dependents={dependentsMap.get(q.id)}
                            numberById={numberById}
                            onJump={jumpToQuestion}
                          />
                          {/* No rule picker here any more — nothing on a
                              question card opens one. A question's condition is
                              set in the question editor; see `controls`. */}
                          {/* WYSIWYG: this field exactly as the Word / PDF
                              export prints it — option marks, fill lines,
                              format hints, blank table rows. Reading the
                              document is reading the form, without opening
                              anything. (The whole-document Preview button in
                              the header shows the same thing paginated on a
                              real page; this is the per-question view of it.)
                              Deliberately NOT the interactive control: the
                              template is a blank form shared by every patient,
                              and an answer typed here would propagate to all of
                              them into a doc with no patient audit trail. Real
                              answers are recorded per patient in PatientSource.
                              BranchNotes above already states the rule, so the
                              preview doesn't repeat it. */}
                          {/* Outlined, not filled: the box is there to show
                              where the question stops and the answer starts,
                              which a line does without tinting the content
                              inside it. Purple because that is the accent this
                              document already uses for structure. */}
                          <div className="mt-2 rounded-lg border border-purple-300/40 px-2.5 py-1.5">
                            <SourcePreviewField
                              question={q}
                              labelById={labelById}
                              hideConditionNote
                            />
                          </div>
                        </div>
                      );
                    })()
                  )
                )}

                <div className="mt-6 border-t border-white/10 pt-4" data-tour="visit-notes">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400 mb-1">
                    Notes
                  </label>
                  <textarea
                    className={`${inputCls} h-24`}
                    value={selectedEventObj.notes || ""}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    placeholder="Add notes for this event…"
                  />
                </div>
              </>
            ) : (
              <p className="text-slate-500 text-sm">Select an event to view details.</p>
            )}
          </div>

          {/* Right: document outline — sticky, so it stays put while the
              document scrolls. Hidden on narrow screens where there's no room
              for a third column. */}
          {selectedEventObj && (
            <aside className="hidden lg:block lg:w-64 xl:w-72 shrink-0">
              <div className="sticky top-4" data-tour="outline">
                <TemplateOutline
                  questions={selectedEventObj.questions || []}
                  expanded={outlineOpen}
                  onToggleHeading={toggleOutlineGroup}
                  onJump={jumpToQuestion}
                  // The outline knows a heading by its id; moveHeadingBlock wants its
                  // position in the visit (before 2026-10-29 the id was passed straight
                  // through, so the outline's heading arrows did nothing).
                  onMoveHeadingBlock={(headingId, dir) =>
                    moveHeadingBlock(
                      (selectedEventObj.questions || []).findIndex((q) => q.id === headingId),
                      dir
                    )
                  }
                  onMoveQuestion={moveQuestion}
                  onReorder={reorderOutline}
                  problemById={problemById}
                  branchDepth={branchDepth}
                  numberById={numberById}
                  standardAdviceById={outlineAdvice}
                  onDropTemplate={(tplId, targetId) => {
                    setDraggingTemplateId(null);
                    insertTemplateAt(tplId, targetId);
                  }}
                  templateDragId={draggingTemplateId}
                />
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* Jump to the top / bottom of the visit. A long visit is a long scroll
          in both directions — back up to the toolbar, down to the Notes box —
          and the outline only jumps to things it lists.
          Stacked ABOVE the global ChatLauncher (fixed bottom-6 right-6), not
          beside it, so neither covers the other. Only while a visit is open:
          with nothing selected there is nothing to scroll through. */}
      {selectedEventObj && (
        <div className="fixed bottom-24 right-6 z-40 flex flex-col gap-1.5 print:hidden" data-tour="jump">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            title="Jump to the top of this visit"
            aria-label="Jump to top"
            className="w-10 h-10 rounded-xl border border-white/15 bg-[#0c0826]/90 backdrop-blur-xl shadow-[0_10px_30px_-10px_rgba(0,0,0,0.9)] text-slate-200 hover:text-white hover:bg-white/[0.12] flex items-center justify-center transition"
          >
            <ChevronsUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() =>
              window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: "smooth",
              })
            }
            title="Jump to the bottom of this visit"
            aria-label="Jump to bottom"
            className="w-10 h-10 rounded-xl border border-white/15 bg-[#0c0826]/90 backdrop-blur-xl shadow-[0_10px_30px_-10px_rgba(0,0,0,0.9)] text-slate-200 hover:text-white hover:bg-white/[0.12] flex items-center justify-center transition"
          >
            <ChevronsDown className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
